import AWS from "aws-sdk";
import React from "react";
import autoBind from "react-autobind";
import Modal from "react-responsive-modal";
import { Button } from "react-bootstrap";
import _ from "lodash";
import { showConfirm, showYesNo } from "../../actions/alertActions";
import env from "../../constants/env";
import { getAudioTranscriptionsLambda, getAwsTranscribeLambda, getResponsesCreateLambda } from "../../ai/aiAssistant";
import { getDriveObjectsAsMap, listDriveMap } from "../../actions/drive";
import { getObject } from "../../persistence/s3";
import { refreshCredentials } from "../../utils/securityUtil";
import { isAiCapabilityEnabled } from "../../utils/aiCapabilityUtils";

const MIN_SEGMENT_DURATION_SECONDS = 0.1;
const CHUNK_POINT_EPSILON_SECONDS = 0.05;
const CHUNK_PROGRESS_POLL_MS = 2500;
const CHUNK_PROGRESS_MATCH_GRACE_MS = 5000;
const GENERATION_MODE_VIDEO_CHUNKS = "video-chunks";
const GENERATION_MODE_KEYFRAMES = "keyframes";
const KEYFRAME_IMAGE_FORMAT_PNG = "png";
const KEYFRAME_IMAGE_FORMAT_JPG = "jpg";
const TRANSCRIPT_PROVIDER_OPENAI = "openai";
const TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE = "aws-transcribe";
const TRANSCRIPT_VIEW_TEXT = "text";
const TRANSCRIPT_VIEW_MATCHED_CHUNKS = "matched-chunks";
const TRANSCRIPT_VIEW_STEP_CARDS = "step-cards";
const TRANSCRIPT_VIEW_SENTENCES = "sentences";
const TRANSCRIPT_VIEW_VTT = "vtt";
const CHUNK_MODAL_LAYOUT_DEFAULT = "default";
const CHUNK_MODAL_LAYOUT_VIDEO_FOCUS = "video-focus";
const CHUNK_MODAL_LAYOUT_TRANSCRIPT_FOCUS = "transcript-focus";
const CHUNK_MODAL_LAYOUT_STEPS_FOCUS = "steps-focus";
const CUSTOM_SENTENCE_GROUP_COLORS = [
	{ border: "#f59e0b", background: "#fffbeb", accent: "#92400e", chipBackground: "#fef3c7" },
	{ border: "#10b981", background: "#ecfdf5", accent: "#065f46", chipBackground: "#d1fae5" },
	{ border: "#3b82f6", background: "#eff6ff", accent: "#1d4ed8", chipBackground: "#dbeafe" },
	{ border: "#8b5cf6", background: "#f5f3ff", accent: "#5b21b6", chipBackground: "#ede9fe" },
	{ border: "#ef4444", background: "#fef2f2", accent: "#991b1b", chipBackground: "#fee2e2" },
	{ border: "#14b8a6", background: "#f0fdfa", accent: "#115e59", chipBackground: "#ccfbf1" }
];

function roundSeconds(value = 0) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return 0;
	}
	return Math.round(parsed * 100) / 100;
}

function clampSeconds(value = 0, duration = 0) {
	if (!Number.isFinite(duration) || duration <= 0) {
		return 0;
	}
	return Math.min(Math.max(roundSeconds(value), 0), duration);
}

function formatSeconds(value = 0) {
	const totalSeconds = Math.max(0, roundSeconds(value));
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = Math.floor(totalSeconds % 60);
	const hundredths = Math.round((totalSeconds - Math.floor(totalSeconds)) * 100);

	const base = hours > 0
		? `${hours}:${`${minutes}`.padStart(2, "0")}:${`${seconds}`.padStart(2, "0")}`
		: `${minutes}:${`${seconds}`.padStart(2, "0")}`;
	return hundredths > 0 ? `${base}.${`${hundredths}`.padStart(2, "0")}` : base;
}

function getFileStem(fileName = "") {
	const trimmed = `${fileName || ""}`.trim();
	if (!trimmed) {
		return "video";
	}
	const lastSegment = trimmed.split("/").pop() || trimmed;
	return lastSegment.replace(/\.[^/.]+$/, "") || "video";
}

function normalizeChunkSuffix(value = "", index = 0) {
	const sanitized = `${value || ""}`
		.trim()
		.replace(/\.[^/.]+$/, "")
		.replace(/[\\/]+/g, "-")
		.replace(/[^a-zA-Z0-9 _-]+/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-")
		.replace(/^[-_.]+|[-_.]+$/g, "");
	return sanitized || `step${index + 1}`;
}

function buildChunkOutputFileName(fileName = "", suffix = "", index = 0) {
	const stem = getFileStem(fileName);
	const normalizedSuffix = normalizeChunkSuffix(suffix, index);
	return `${stem}-${normalizedSuffix}.mp4`;
}

function buildKeyframeOutputFileName(fileName = "", suffix = "", index = 0, imageFormat = KEYFRAME_IMAGE_FORMAT_PNG) {
	const stem = getFileStem(fileName);
	const normalizedSuffix = normalizeChunkSuffix(suffix, index);
	const safeImageFormat = `${imageFormat || KEYFRAME_IMAGE_FORMAT_PNG}`.trim().toLowerCase() === KEYFRAME_IMAGE_FORMAT_JPG
		? KEYFRAME_IMAGE_FORMAT_JPG
		: KEYFRAME_IMAGE_FORMAT_PNG;
	return `${stem}-${normalizedSuffix}.${safeImageFormat}`;
}

function buildDefaultOutlineLabel(baseLabel = "", index = 0) {
	const trimmedBaseLabel = `${baseLabel || ""}`.trim();
	return trimmedBaseLabel ? `${trimmedBaseLabel} - Step ${index + 1}` : `Step ${index + 1}`;
}

function buildChunkJobId() {
	return `chunk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getPathFileName(value = "") {
	return `${value || ""}`.trim().split("/").pop() || "";
}

function getPathStem(value = "") {
	const fileName = getPathFileName(value);
	return fileName.replace(/\.[^/.]+$/, "") || "";
}

function parseResourceTimestampMs(resource = null) {
	if (!resource || typeof resource !== "object") {
		return 0;
	}

	const candidates = [
		resource.updatedAt,
		resource.createdAt,
		resource.lastUpdatedAt
	];
	for (let index = 0; index < candidates.length; index += 1) {
		const candidate = `${candidates[index] || ""}`.trim();
		if (!candidate) {
			continue;
		}
		const timestamp = Date.parse(candidate);
		if (Number.isFinite(timestamp) && timestamp > 0) {
			return timestamp;
		}
	}

	return 0;
}

function cleanTranscriptText(value = "") {
	return typeof value === "string" ? value.trim() : "";
}

function cleanTranscriptVtt(value = "") {
	return typeof value === "string" ? value.replace(/\r\n/g, "\n").trim() : "";
}

function parseTranscriptTimecode(value = "") {
	const normalized = `${value || ""}`.trim().replace(",", ".");
	if (!normalized) {
		return null;
	}
	const parts = normalized.split(":").map((entry) => Number.parseFloat(entry));
	if (parts.some((entry) => !Number.isFinite(entry))) {
		return null;
	}
	if (parts.length === 3) {
		return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
	}
	if (parts.length === 2) {
		return (parts[0] * 60) + parts[1];
	}
	if (parts.length === 1) {
		return parts[0];
	}
	return null;
}

function parseVttCues(vttValue = "") {
	const normalized = cleanTranscriptVtt(vttValue);
	if (!normalized) {
		return [];
	}

	const cues = [];
	normalized.split(/\n{2,}/).forEach((block) => {
		const lines = `${block || ""}`.split("\n").map((line) => line.trim()).filter(Boolean);
		if (!lines.length) {
			return;
		}
		if (/^WEBVTT$/i.test(lines[0]) || /^NOTE\b/i.test(lines[0])) {
			return;
		}
		const timingLineIndex = lines.findIndex((line) => /-->/.test(line));
		if (timingLineIndex < 0) {
			return;
		}
		const timingMatch = lines[timingLineIndex].match(/^(.+?)\s+-->\s+(.+)$/);
		if (!timingMatch) {
			return;
		}
		const startSeconds = parseTranscriptTimecode(timingMatch[1]);
		const endSeconds = parseTranscriptTimecode(timingMatch[2].split(/\s+/)[0]);
		const text = lines.slice(timingLineIndex + 1).join(" ").replace(/\s+/g, " ").trim();
		if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds) || endSeconds <= startSeconds || !text) {
			return;
		}
		cues.push({
			index: cues.length,
			startSeconds,
			endSeconds,
			text
		});
	});
	return cues;
}

function extractTranscriptFromVtt(vttValue = "") {
	return parseVttCues(vttValue).map((cue) => cue.text).join(" ").replace(/\s+/g, " ").trim();
}

function extractTranscriptFromAwsTranscribeJson(jsonPayload = null) {
	if (!jsonPayload || typeof jsonPayload !== "object") {
		return "";
	}
	const directTranscript = _.get(jsonPayload, "results.transcripts[0].transcript", "");
	return typeof directTranscript === "string" ? directTranscript.trim() : "";
}

function parseAwsTranscribeSeconds(value = null) {
	const parsed = Number.parseFloat(value);
	return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function shouldAttachAwsTokenToPrevious(tokenText = "") {
	const trimmedToken = `${tokenText || ""}`.trim();
	if (!trimmedToken) {
		return false;
	}
	return /^[.,!?;:%)\]}]+$/.test(trimmedToken)
		|| /^['’].+/.test(trimmedToken)
		|| /^(?:n't|'s|'re|'ve|'ll|'d|'m)$/i.test(trimmedToken)
		|| /^-+$/.test(trimmedToken);
}

function extractTimedTranscriptWordsFromAwsTranscribePayload(payload = null) {
	let resolvedPayload = payload;
	if (typeof resolvedPayload === "string") {
		try {
			resolvedPayload = JSON.parse(resolvedPayload);
		} catch (error) {
			return [];
		}
	}

	const items = Array.isArray(_.get(resolvedPayload, "results.items"))
		? _.get(resolvedPayload, "results.items")
		: (Array.isArray(_.get(resolvedPayload, "items")) ? _.get(resolvedPayload, "items") : []);
	if (!items.length) {
		return [];
	}

	const timedWords = [];
	let pendingPrefix = "";
	items.forEach((item) => {
		const tokenText = normalizeTranscriptSuggestion(_.get(item, "alternatives[0].content", item && item.content ? item.content : ""));
		if (!tokenText) {
			return;
		}

		const itemType = `${_.get(item, "type", "")}`.trim().toLowerCase();
		const startSeconds = parseAwsTranscribeSeconds(_.get(item, "start_time", _.get(item, "startSeconds", null)));
		const endSeconds = parseAwsTranscribeSeconds(_.get(item, "end_time", _.get(item, "endSeconds", null)));

		if (itemType === "punctuation" || startSeconds === null || endSeconds === null || endSeconds < startSeconds) {
			if (timedWords.length > 0 && shouldAttachAwsTokenToPrevious(tokenText)) {
				const previousWord = timedWords[timedWords.length - 1];
				previousWord.text = `${previousWord.text}${tokenText}`;
			} else {
				pendingPrefix = `${pendingPrefix}${tokenText}`;
			}
			return;
		}

		if (timedWords.length > 0 && shouldAttachAwsTokenToPrevious(tokenText)) {
			const previousWord = timedWords[timedWords.length - 1];
			previousWord.text = `${previousWord.text}${tokenText}`;
			previousWord.endSeconds = endSeconds;
			previousWord.midpointSeconds = roundSeconds((previousWord.startSeconds + previousWord.endSeconds) / 2);
			return;
		}

		const normalizedText = `${pendingPrefix}${tokenText}`;
		pendingPrefix = "";
		timedWords.push({
			index: timedWords.length,
			text: normalizedText,
			startSeconds,
			endSeconds,
			midpointSeconds: roundSeconds((startSeconds + endSeconds) / 2)
		});
	});

	if (pendingPrefix && timedWords.length > 0) {
		timedWords[timedWords.length - 1].text = `${timedWords[timedWords.length - 1].text}${pendingPrefix}`;
	}

	return timedWords.filter((entry) => {
		return !!normalizeTranscriptSuggestion(entry && entry.text ? entry.text : "");
	});
}

function createPathCandidateVariants(pathValue = "", orgId = "") {
	const variants = [];
	const pushVariant = (candidate) => {
		const normalizedCandidate = `${candidate || ""}`.trim().replace(/^\/+/, "");
		if (!normalizedCandidate || variants.includes(normalizedCandidate)) {
			return;
		}
		variants.push(normalizedCandidate);
	};

	const basePath = `${pathValue || ""}`.trim().replace(/^\/+/, "");
	if (!basePath) {
		return variants;
	}

	pushVariant(basePath);
	pushVariant(basePath.replace(/\s/g, "_"));

	const pathParts = basePath.split("/").filter(Boolean);
	if ((pathParts[0] === "public" || pathParts[0] === "private") && pathParts.length >= 2) {
		const hasOrgSegment = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pathParts[1] || "");
		if (!hasOrgSegment && `${orgId || ""}`.trim()) {
			const withOrgPath = [pathParts[0], `${orgId}`.trim(), ...pathParts.slice(1)].join("/");
			pushVariant(withOrgPath);
			pushVariant(withOrgPath.replace(/\s/g, "_"));
		}
		if (hasOrgSegment) {
			const withoutOrgPath = [pathParts[0], ...pathParts.slice(2)].join("/");
			pushVariant(withoutOrgPath);
			pushVariant(withoutOrgPath.replace(/\s/g, "_"));
		}
	}

	return variants;
}

function buildAwsTranscribeCompanionPathCandidates(sourcePath = "", orgId = "") {
	const normalizedSourcePath = `${sourcePath || ""}`.trim().replace(/^\/+/, "");
	if (!normalizedSourcePath || normalizedSourcePath.lastIndexOf(".") < 0) {
		return { transcriptionPaths: [], vttPaths: [] };
	}

	const basePathWithoutExt = normalizedSourcePath.slice(0, normalizedSourcePath.lastIndexOf("."));
	return {
		transcriptionPaths: createPathCandidateVariants(`${basePathWithoutExt}-transcription.json`, orgId),
		vttPaths: createPathCandidateVariants(`${basePathWithoutExt}-subtitles.vtt`, orgId)
	};
}

function buildChunkMatchedTranscriptText(segment = null, timedWords = [], duration = 0) {
	if (!segment || !Array.isArray(timedWords) || timedWords.length === 0) {
		return "";
	}

	const startSeconds = Number.isFinite(Number(segment.startSeconds)) ? Number(segment.startSeconds) : null;
	const endSeconds = Number.isFinite(Number(segment.endSeconds)) ? Number(segment.endSeconds) : null;
	if (startSeconds === null || endSeconds === null || endSeconds <= startSeconds) {
		return "";
	}

	const isFinalSegment = Number.isFinite(Number(duration)) && Math.abs(Number(duration) - endSeconds) <= CHUNK_POINT_EPSILON_SECONDS;
	const matchedWords = timedWords
		.filter((word) => {
			const midpointSeconds = Number.isFinite(Number(word && word.midpointSeconds))
				? Number(word.midpointSeconds)
				: roundSeconds(((Number(word && word.startSeconds) || 0) + (Number(word && word.endSeconds) || 0)) / 2);
			return midpointSeconds >= startSeconds && (isFinalSegment ? midpointSeconds <= (endSeconds + CHUNK_POINT_EPSILON_SECONDS) : midpointSeconds < endSeconds);
		})
		.map((word) => normalizeTranscriptSuggestion(word && word.text ? word.text : ""))
		.filter(Boolean);

	return normalizeTranscriptSuggestion(matchedWords.join(" "));
}

function extractTranscriptionText(payload) {
	if (!payload) {
		return "";
	}
	if (typeof payload === "string") {
		return payload;
	}
	return (
		payload.text ||
		payload.transcript ||
		payload.output_text ||
		payload.content ||
		payload?.message?.content ||
		payload?.choices?.[0]?.message?.content ||
		payload?.results?.transcripts?.[0]?.transcript ||
		""
	);
}

function extractTranscriptionVtt(payload) {
	if (!payload) {
		return "";
	}
	if (typeof payload === "string") {
		return /-->/m.test(payload) ? payload : "";
	}
	const candidates = [payload.vtt, payload.transcriptVtt, payload.transcript_vtt, payload.output_vtt, payload.outputVtt, payload.text];
	for (let index = 0; index < candidates.length; index += 1) {
		const candidate = candidates[index];
		if (typeof candidate === "string" && /-->/m.test(candidate)) {
			return candidate;
		}
	}
	return "";
}

function normalizeTranscriptSuggestion(text = "") {
	return `${text || ""}`.replace(/\s+/g, " ").trim();
}

function extractResponsesText(payload) {
	if (!payload) {
		return "";
	}
	if (typeof payload === "string") {
		return payload;
	}
	if (typeof payload.output_text === "string" && payload.output_text.trim() !== "") {
		return payload.output_text;
	}
	if (payload.response && typeof payload.response.output_text === "string" && payload.response.output_text.trim() !== "") {
		return payload.response.output_text;
	}
	if (typeof payload.text === "string" && payload.text.trim() !== "") {
		return payload.text;
	}
	const readOutputArray = (arr) => {
		if (!Array.isArray(arr)) {
			return "";
		}
		const parts = [];
		arr.forEach((entry) => {
			if (!entry) {
				return;
			}
			if (entry.type === "message" && Array.isArray(entry.content)) {
				entry.content.forEach((contentPart) => {
					if (typeof contentPart?.text === "string" && contentPart.text.trim() !== "") {
						parts.push(contentPart.text);
					} else if (typeof contentPart?.value === "string" && contentPart.value.trim() !== "") {
						parts.push(contentPart.value);
					}
				});
				return;
			}
			if (typeof entry.text === "string" && entry.text.trim() !== "") {
				parts.push(entry.text);
			}
		});
		return parts.join("").trim();
	};
	return readOutputArray(payload.output || payload.response?.output);
}

function stripMarkdownCodeFence(value = "") {
	const text = `${value || ""}`.trim();
	if (!text.startsWith("```")) {
		return text;
	}
	return text.replace(/^```[a-zA-Z0-9_-]*\n?/, "").replace(/\n?```$/, "").trim();
}

function parseAiSuggestedLabels(value = "", expectedCount = 0) {
	const cleaned = stripMarkdownCodeFence(value);
	if (!cleaned) {
		return [];
	}
	try {
		const parsed = JSON.parse(cleaned);
		if (Array.isArray(parsed)) {
			return parsed
				.map((entry) => {
					if (typeof entry === "string") {
						return normalizeTranscriptSuggestion(entry);
					}
					if (entry && typeof entry === "object") {
						return normalizeTranscriptSuggestion(entry.label || entry.text || entry.title || "");
					}
					return "";
				})
				.filter(Boolean)
				.slice(0, expectedCount || undefined);
		}
	} catch (error) {
		// fall through to line parsing
	}
	return cleaned
		.split("\n")
		.map((line) => line.replace(/^\s*[-*0-9.]+\s*/, ""))
		.map((line) => normalizeTranscriptSuggestion(line))
		.filter(Boolean)
		.slice(0, expectedCount || undefined);
}

function buildChunkTranscriptSuggestion(segment = null, cues = []) {
	if (!segment || !Array.isArray(cues) || cues.length === 0) {
		return "";
	}
	const overlappingTexts = cues
		.filter((cue) => cue.endSeconds > segment.startSeconds && cue.startSeconds < segment.endSeconds)
		.map((cue) => cue.text);
	if (!overlappingTexts.length) {
		return "";
	}
	return normalizeTranscriptSuggestion(overlappingTexts.join(" "));
}

function buildKeyframeTranscriptSuggestion(segment = null, cues = []) {
	if (!segment || !Array.isArray(cues) || cues.length === 0) {
		return "";
	}
	const frameSeconds = Number(segment.frameSeconds) || 0;
	const containingCue = cues.find((cue) => frameSeconds >= cue.startSeconds && frameSeconds <= cue.endSeconds);
	if (containingCue) {
		return normalizeTranscriptSuggestion(containingCue.text);
	}
	const nearestCue = cues.reduce((closest, cue) => {
		const distance = frameSeconds < cue.startSeconds
			? cue.startSeconds - frameSeconds
			: frameSeconds > cue.endSeconds
				? frameSeconds - cue.endSeconds
				: 0;
		if (!closest || distance < closest.distance) {
			return {
				cue,
				distance
			};
		}
		return closest;
	}, null);
	return nearestCue && nearestCue.cue ? normalizeTranscriptSuggestion(nearestCue.cue.text) : "";
}

function isCueRelevantToSegment(cue = null, segment = null, generationMode = GENERATION_MODE_VIDEO_CHUNKS, cues = []) {
	if (!cue || !segment) {
		return false;
	}
	if (generationMode === GENERATION_MODE_KEYFRAMES) {
		const suggestedText = buildKeyframeTranscriptSuggestion(segment, cues);
		return suggestedText && normalizeTranscriptSuggestion(cue.text) === suggestedText;
	}
	return cue.endSeconds > segment.startSeconds && cue.startSeconds < segment.endSeconds;
}

function getCueForTime(cues = [], seconds = 0) {
	const safeSeconds = Number(seconds) || 0;
	return (Array.isArray(cues) ? cues : []).find((cue) => safeSeconds >= cue.startSeconds && safeSeconds <= cue.endSeconds) || null;
}

function buildCueTextRanges(cues = []) {
	let cursor = 0;
	return (Array.isArray(cues) ? cues : []).map((cue, index) => {
		const text = `${cue && cue.text ? cue.text : ""}`;
		const startOffset = cursor;
		const endOffset = startOffset + text.length;
		cursor = endOffset + 1;
		return {
			...(cue || {}),
			index: Number.isFinite(Number(cue && cue.index)) ? Number(cue.index) : index,
			text,
			startOffset,
			endOffset
		};
	});
}

function getTimedWordMidpointSeconds(word = null) {
	if (!word || typeof word !== "object") {
		return null;
	}
	if (Number.isFinite(Number(word.midpointSeconds))) {
		return Number(word.midpointSeconds);
	}
	const startSeconds = Number(word.startSeconds);
	const endSeconds = Number(word.endSeconds);
	if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds)) {
		return null;
	}
	return roundSeconds((startSeconds + endSeconds) / 2);
}

function buildTimedTranscriptDisplayModel(cues = [], timedWords = []) {
	const safeCues = Array.isArray(cues) ? cues : [];
	if (!safeCues.length) {
		return {
			displayText: "",
			cueRanges: [],
			wordRanges: []
		};
	}

	const safeTimedWords = Array.isArray(timedWords) ? timedWords : [];
	let displayText = "";
	let cursor = 0;
	const cueRanges = [];
	const wordRanges = [];
	const appendText = (text = "") => {
		const normalizedText = normalizeTranscriptSuggestion(text);
		if (!normalizedText) {
			return null;
		}
		if (cursor > 0) {
			displayText = `${displayText} `;
			cursor += 1;
		}
		const startOffset = cursor;
		displayText = `${displayText}${normalizedText}`;
		cursor += normalizedText.length;
		return {
			text: normalizedText,
			startOffset,
			endOffset: cursor
		};
	};

	safeCues.forEach((cue, index) => {
		const cueIndex = Number.isFinite(Number(cue && cue.index)) ? Number(cue.index) : index;
		const cueWords = safeTimedWords.filter((word) => {
			const midpointSeconds = getTimedWordMidpointSeconds(word);
			if (!Number.isFinite(Number(midpointSeconds))) {
				return false;
			}
			return Number(midpointSeconds) >= Number(cue.startSeconds)
				&& Number(midpointSeconds) <= (Number(cue.endSeconds) + CHUNK_POINT_EPSILON_SECONDS);
		});

		let cueStartOffset = null;
		let cueEndOffset = null;
		let cueDisplayText = "";
		if (cueWords.length > 0) {
			cueWords.forEach((word, wordIndex) => {
				const appendedWord = appendText(word && word.text ? word.text : "");
				if (!appendedWord) {
					return;
				}
				if (cueStartOffset === null) {
					cueStartOffset = appendedWord.startOffset;
				}
				cueEndOffset = appendedWord.endOffset;
				cueDisplayText = cueDisplayText ? `${cueDisplayText} ${appendedWord.text}` : appendedWord.text;
				wordRanges.push({
					...(word || {}),
					index: Number.isFinite(Number(word && word.index)) ? Number(word.index) : wordIndex,
					text: appendedWord.text,
					cueIndex,
					midpointSeconds: getTimedWordMidpointSeconds(word),
					startOffset: appendedWord.startOffset,
					endOffset: appendedWord.endOffset
				});
			});
		} else {
			const appendedCue = appendText(cue && cue.text ? cue.text : "");
			if (appendedCue) {
				cueStartOffset = appendedCue.startOffset;
				cueEndOffset = appendedCue.endOffset;
				cueDisplayText = appendedCue.text;
			}
		}

		cueRanges.push({
			...(cue || {}),
			index: cueIndex,
			text: cueDisplayText || `${cue && cue.text ? cue.text : ""}`,
			startOffset: cueStartOffset === null ? cursor : cueStartOffset,
			endOffset: cueEndOffset === null ? cursor : cueEndOffset
		});
	});

	return {
		displayText,
		cueRanges,
		wordRanges
	};
}

function isTimedWordRangeRelevantToSegment(wordRange = null, segment = null, duration = 0) {
	if (!wordRange || !segment) {
		return false;
	}

	const startSeconds = Number.isFinite(Number(segment.startSeconds)) ? Number(segment.startSeconds) : null;
	const endSeconds = Number.isFinite(Number(segment.endSeconds)) ? Number(segment.endSeconds) : null;
	if (startSeconds === null || endSeconds === null || endSeconds <= startSeconds) {
		return false;
	}

	const midpointSeconds = getTimedWordMidpointSeconds(wordRange);
	if (!Number.isFinite(Number(midpointSeconds))) {
		return false;
	}
	const isFinalSegment = Number.isFinite(Number(duration)) && Math.abs(Number(duration) - endSeconds) <= CHUNK_POINT_EPSILON_SECONDS;
	return midpointSeconds >= startSeconds && (isFinalSegment ? midpointSeconds <= (endSeconds + CHUNK_POINT_EPSILON_SECONDS) : midpointSeconds < endSeconds);
}

function getTimedWordRangeForTime(wordRanges = [], seconds = 0) {
	const safeSeconds = Number(seconds);
	if (!Number.isFinite(safeSeconds)) {
		return null;
	}
	return (Array.isArray(wordRanges) ? wordRanges : []).find((wordRange) => {
		if (!wordRange || !Number.isFinite(Number(wordRange.startSeconds)) || !Number.isFinite(Number(wordRange.endSeconds))) {
			return false;
		}
		return safeSeconds >= Number(wordRange.startSeconds) && safeSeconds <= (Number(wordRange.endSeconds) + CHUNK_POINT_EPSILON_SECONDS);
	}) || null;
}

function splitTranscriptIntoSentenceTexts(text = "") {
	const normalized = `${text || ""}`.replace(/\s+/g, " ").trim();
	if (!normalized) {
		return [];
	}
	const matched = normalized.match(/[^.!?]+(?:[.!?]+|$)/g);
	if (matched && matched.length > 0) {
		return matched.map((entry) => entry.replace(/\s+/g, " ").trim()).filter(Boolean);
	}
	return [normalized];
}

function splitTimedCueIntoSentenceFragments(cue = null) {
	if (!cue || !Number.isFinite(cue.startSeconds) || !Number.isFinite(cue.endSeconds)) {
		return [];
	}

	const parts = splitTranscriptIntoSentenceTexts(cue.text || "");
	if (!parts.length) {
		return [];
	}

	const totalDuration = Math.max(0, (Number(cue.endSeconds) || 0) - (Number(cue.startSeconds) || 0));
	const weightedLengths = parts.map((part) => Math.max(1, `${part || ""}`.trim().length));
	const totalWeight = weightedLengths.reduce((sum, length) => sum + length, 0) || parts.length;

	let cursor = Number(cue.startSeconds) || 0;
	return parts.map((part, index) => {
		const portion = totalDuration > 0 ? (totalDuration * (weightedLengths[index] / totalWeight)) : 0;
		const isLast = index === parts.length - 1;
		const startSeconds = cursor;
		const endSeconds = isLast ? (Number(cue.endSeconds) || startSeconds) : cursor + portion;
		cursor = endSeconds;
		return {
			text: normalizeTranscriptSuggestion(part),
			startSeconds,
			endSeconds
		};
	}).filter((part) => !!part.text);
}

function buildTranscriptSentenceEntries(transcriptText = "", transcriptVtt = "") {
	const cues = parseVttCues(transcriptVtt);
	if (cues.length > 0) {
		const sentences = [];
		let currentText = "";
		let currentCueIndexes = [];
		let currentStartSeconds = null;
		let currentEndSeconds = null;

		const flushSentence = () => {
			const normalizedText = normalizeTranscriptSuggestion(currentText);
			if (!normalizedText) {
				currentText = "";
				currentCueIndexes = [];
				currentStartSeconds = null;
				currentEndSeconds = null;
				return;
			}
			sentences.push({
				index: sentences.length,
				text: normalizedText,
				startSeconds: currentStartSeconds,
				endSeconds: currentEndSeconds,
				cueIndexes: [...currentCueIndexes],
				timed: true
			});
			currentText = "";
			currentCueIndexes = [];
			currentStartSeconds = null;
			currentEndSeconds = null;
		};

		cues.forEach((cue) => {
			const cueSentenceParts = splitTimedCueIntoSentenceFragments(cue);
			if (!cueSentenceParts.length) {
				return;
			}

			cueSentenceParts.forEach((part) => {
				currentText = `${currentText} ${part.text}`.trim();
				if (!currentCueIndexes.includes(cue.index)) {
					currentCueIndexes.push(cue.index);
				}
				currentStartSeconds = currentStartSeconds === null ? part.startSeconds : currentStartSeconds;
				currentEndSeconds = part.endSeconds;

				if (/[.!?]["')\]]*$/.test(`${part.text || ""}`.trim())) {
					flushSentence();
				}
			});
		});

		if (currentText) {
			flushSentence();
		}
		return sentences;
	}

	return splitTranscriptIntoSentenceTexts(transcriptText).map((text, index) => ({
		index,
		text,
		startSeconds: null,
		endSeconds: null,
		cueIndexes: [],
		timed: false
	}));
}

function getCustomSentenceGroupColor(colorIndex = 0) {
	return CUSTOM_SENTENCE_GROUP_COLORS[Math.abs(Number(colorIndex) || 0) % CUSTOM_SENTENCE_GROUP_COLORS.length];
}

function getCustomTextStepCardColor(colorIndex = 0) {
	return getCustomSentenceGroupColor(colorIndex);
}

function hexToRgba(hexColor = "", alpha = 1) {
	const normalized = `${hexColor || ""}`.trim().replace(/^#/, "");
	const expanded = normalized.length === 3
		? normalized.split("").map((part) => `${part}${part}`).join("")
		: normalized;
	if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
		return `rgba(15, 23, 42, ${alpha})`;
	}

	const integerValue = Number.parseInt(expanded, 16);
	const red = (integerValue >> 16) & 255;
	const green = (integerValue >> 8) & 255;
	const blue = integerValue & 255;
	return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function buildTranscriptFragmentHighlightStyle({
	stepCardColor = null,
	matchesActiveSegment = false,
	matchesActiveSegmentWord = false,
	matchesSelectedRange = false,
	matchesScrub = false,
	matchesCurrentTimedWord = false
} = {}) {
	const backgroundLayers = [];
	if (matchesCurrentTimedWord) {
		backgroundLayers.push("linear-gradient(180deg, rgba(251, 191, 36, 0.98) 0%, rgba(245, 158, 11, 0.92) 100%)");
	}
	if (matchesScrub) {
		backgroundLayers.push("linear-gradient(180deg, rgba(254, 240, 138, 0.96) 0%, rgba(253, 224, 71, 0.84) 100%)");
	}
	if (matchesSelectedRange) {
		backgroundLayers.push("linear-gradient(180deg, rgba(129, 140, 248, 0.64) 0%, rgba(99, 102, 241, 0.44) 100%)");
	}
	if (matchesActiveSegmentWord) {
		backgroundLayers.push("linear-gradient(180deg, rgba(147, 197, 253, 0.94) 0%, rgba(96, 165, 250, 0.74) 100%)");
	}
	if (matchesActiveSegment) {
		backgroundLayers.push("linear-gradient(180deg, rgba(219, 234, 254, 0.78) 0%, rgba(191, 219, 254, 0.58) 100%)");
	}
	if (stepCardColor) {
		backgroundLayers.push(`linear-gradient(180deg, ${hexToRgba(stepCardColor.background, 0.3)} 0%, ${hexToRgba(stepCardColor.background, 0.3)} 100%)`);
		backgroundLayers.push(`linear-gradient(180deg, transparent 0%, transparent 68%, ${hexToRgba(stepCardColor.border, 0.82)} 68%, ${hexToRgba(stepCardColor.border, 0.82)} 100%)`);
	}

	let outlineColor = "transparent";
	if (matchesCurrentTimedWord) {
		outlineColor = "rgba(180, 83, 9, 0.96)";
	} else if (matchesScrub) {
		outlineColor = "rgba(217, 119, 6, 0.95)";
	} else if (matchesSelectedRange) {
		outlineColor = "rgba(79, 70, 229, 0.82)";
	} else if (matchesActiveSegmentWord) {
		outlineColor = "rgba(37, 99, 235, 0.52)";
	} else if (matchesActiveSegment) {
		outlineColor = "rgba(96, 165, 250, 0.24)";
	} else if (stepCardColor) {
		outlineColor = hexToRgba(stepCardColor.border, 0.42);
	}

	const boxShadowLayers = [];
	if (outlineColor !== "transparent") {
		boxShadowLayers.push(`inset 0 0 0 1px ${outlineColor}`);
	}
	if (stepCardColor) {
		boxShadowLayers.push(`inset 0 -3px 0 ${hexToRgba(stepCardColor.border, 0.9)}`);
		boxShadowLayers.push(`inset 0 0 0 1px ${hexToRgba(stepCardColor.border, 0.28)}`);
	}
	if (matchesCurrentTimedWord) {
		boxShadowLayers.push("0 1px 8px rgba(217, 119, 6, 0.28)");
	}
	if (matchesScrub) {
		boxShadowLayers.push("0 1px 5px rgba(245, 158, 11, 0.18)");
	}

	return {
		backgroundImage: backgroundLayers.length ? backgroundLayers.join(", ") : "none",
		boxShadow: boxShadowLayers.length ? boxShadowLayers.join(", ") : "none",
		color: matchesCurrentTimedWord ? "#78350f" : (matchesScrub ? "#78350f" : "inherit"),
		fontWeight: matchesCurrentTimedWord ? 700 : (matchesScrub ? 600 : (matchesActiveSegmentWord ? 600 : (matchesSelectedRange ? 600 : "inherit")))
	};
}

function isSentenceRelevantToSegment(sentence = null, segment = null, generationMode = GENERATION_MODE_VIDEO_CHUNKS, currentScrubTime = null, currentScrubCue = null) {
	if (!sentence) {
		return {
			matchesScrub: false,
			matchesSegment: false
		};
	}

	const scrubTime = Number(currentScrubTime);
	const matchesScrub = Number.isFinite(scrubTime) && sentence.startSeconds !== null && sentence.endSeconds !== null
		? scrubTime >= sentence.startSeconds && scrubTime <= sentence.endSeconds
		: !!(currentScrubCue && Array.isArray(sentence.cueIndexes) && sentence.cueIndexes.includes(currentScrubCue.index));
	if (!segment || sentence.startSeconds === null || sentence.endSeconds === null) {
		return {
			matchesScrub,
			matchesSegment: false
		};
	}

	if (generationMode === GENERATION_MODE_KEYFRAMES) {
		const frameSeconds = Number(segment.frameSeconds) || 0;
		return {
			matchesScrub,
			matchesSegment: frameSeconds >= sentence.startSeconds && frameSeconds <= sentence.endSeconds
		};
	}

	return {
		matchesScrub,
		matchesSegment: sentence.endSeconds > segment.startSeconds && sentence.startSeconds < segment.endSeconds
	};
}

async function copyTextToClipboard(text = "") {
	const value = `${text || ""}`;
	if (!value) {
		return;
	}
	if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
		await navigator.clipboard.writeText(value);
		return;
	}
	if (typeof document !== "undefined") {
		const textarea = document.createElement("textarea");
		textarea.value = value;
		textarea.setAttribute("readonly", "");
		textarea.style.position = "absolute";
		textarea.style.left = "-9999px";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		document.body.removeChild(textarea);
	}
}

function normalizeChunkPoints(points = [], duration = 0) {
	const safeDuration = Number(duration) || 0;
	const normalized = (Array.isArray(points) ? points : [])
		.map((value) => clampSeconds(value, safeDuration))
		.filter((value) => value > MIN_SEGMENT_DURATION_SECONDS && value < Math.max(0, safeDuration - MIN_SEGMENT_DURATION_SECONDS))
		.sort((left, right) => left - right);

	return normalized.filter((value, index) => {
		if (index === 0) {
			return true;
		}
		return Math.abs(value - normalized[index - 1]) > CHUNK_POINT_EPSILON_SECONDS;
	});
}

function normalizeKeyframePoints(points = [], duration = 0) {
	const safeDuration = Number(duration) || 0;
	const normalized = [];
	(Array.isArray(points) ? points : []).forEach((value) => {
		const clampedValue = clampSeconds(value, safeDuration);
		const isDuplicate = normalized.some((entry) => Math.abs(entry - clampedValue) <= CHUNK_POINT_EPSILON_SECONDS);
		if (!isDuplicate) {
			normalized.push(clampedValue);
		}
	});
	return normalized;
}

function normalizeOutputPoints(points = [], duration = 0, generationMode = GENERATION_MODE_VIDEO_CHUNKS) {
	return generationMode === GENERATION_MODE_KEYFRAMES
		? normalizeKeyframePoints(points, duration)
		: normalizeChunkPoints(points, duration);
}

function getOutputEntryCount(points = [], duration = 0, generationMode = GENERATION_MODE_VIDEO_CHUNKS) {
	const pointList = normalizeOutputPoints(points, duration, generationMode);
	return generationMode === GENERATION_MODE_KEYFRAMES
		? pointList.length
		: pointList.length + 1;
}

function buildSegmentSuffixes(points = [], duration = 0, previousSuffixes = [], generationMode = GENERATION_MODE_VIDEO_CHUNKS) {
	const entryCount = getOutputEntryCount(points, duration, generationMode);
	return Array.from({ length: entryCount }, (_, index) => {
		return normalizeChunkSuffix(previousSuffixes[index], index);
	});
}

function buildSegmentOutlineLabels(points = [], duration = 0, previousLabels = [], baseLabel = "", generationMode = GENERATION_MODE_VIDEO_CHUNKS) {
	const entryCount = getOutputEntryCount(points, duration, generationMode);
	return Array.from({ length: entryCount }, (_, index) => {
		const preservedLabel = `${previousLabels[index] || ""}`.trim();
		return preservedLabel || buildDefaultOutlineLabel(baseLabel, index);
	});
}

function buildDefaultOutlineSectionTitle(baseLabel = "", sectionIndex = 0) {
	const trimmedBaseLabel = `${baseLabel || ""}`.trim();
	return trimmedBaseLabel ? `${trimmedBaseLabel} - Section ${sectionIndex + 1}` : `Section ${sectionIndex + 1}`;
}

function buildSegmentSectionStarts(points = [], duration = 0, previousStarts = [], generationMode = GENERATION_MODE_VIDEO_CHUNKS, useOutlineSections = false) {
	const entryCount = getOutputEntryCount(points, duration, generationMode);
	return Array.from({ length: entryCount }, (_, index) => {
		if (useOutlineSections !== true) {
			return false;
		}
		if (index === 0) {
			return entryCount > 0;
		}
		return previousStarts[index] === true;
	});
}

function buildSegmentSectionTitles(points = [], duration = 0, previousTitles = [], generationMode = GENERATION_MODE_VIDEO_CHUNKS) {
	const entryCount = getOutputEntryCount(points, duration, generationMode);
	return Array.from({ length: entryCount }, (_, index) => `${previousTitles[index] || ""}`.trim());
}

function parseLambdaPayload(value = null) {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	let payload = value;
	if (typeof payload === "string") {
		try {
			payload = JSON.parse(payload);
		} catch (error) {
			return payload;
		}
	}

	if (payload && typeof payload === "object" && payload.body) {
		let body = payload.body;
		if (typeof body === "string") {
			try {
				body = JSON.parse(body);
			} catch (error) {
				body = payload.body;
			}
		}
		if (body && typeof body === "object") {
			return body;
		}
	}

	return payload;
}

export default class VideoChunkModal extends React.PureComponent {
	constructor(props) {
		super(props);
		autoBind(this);
		this.videoRef = React.createRef();
		this.smartScrubberTrackRef = React.createRef();
		this.transcriptSelectionTextareaRef = React.createRef();
		this.transcriptSelectionRichTextRef = React.createRef();
		this.outlineLabelTextareaRefs = {};
		this._isMounted = false;
		this.chunkProgressPollTimer = null;
		this.chunkProgressPollInFlight = false;
		this.activeSmartScrubberDrag = null;
		this.chunkPreviewRequestToken = buildChunkJobId();
		this.timedTranscriptLoadToken = buildChunkJobId();
		this.activeChunkRunToken = null;
		this.processingCompletionHandledToken = null;
		this.state = this.getInitialState(props);
		this.lastCommittedDraftSignature = this.getDraftComparisonSignature(this.state, props);
	}

	componentDidMount() {
		this._isMounted = true;
		if (this.props.open) {
			this.resetForOpen(this.props);
		}
		this.syncOutlineLabelTextareaHeights();
	}

	componentWillUnmount() {
		this._isMounted = false;
		this.clearChunkProgressPolling();
		this.stopSmartScrubberDrag();
		this.invalidateChunkPreviewRequests();
		this.invalidateTimedTranscriptRequests();
		this.activeChunkRunToken = null;
		this.processingCompletionHandledToken = null;
	}

	componentDidUpdate(prevProps) {
		this.syncOutlineLabelTextareaHeights();

		if (!this.props.open && prevProps.open) {
			this.stopSmartScrubberDrag();
		}

		if (this.props.open && !prevProps.open) {
			this.resetForOpen(this.props);
			return;
		}

		if (this.props.open && this.getResetSignature(prevProps) !== this.getResetSignature(this.props)) {
			this.resetForOpen(this.props);
		}
	}

	getChunkSourceSignature(props = this.props) {
		const playbackChunkSource = props && props.playbackChunkSource && typeof props.playbackChunkSource === "object"
			? props.playbackChunkSource
			: null;
		const masterChunkSource = props && props.masterChunkSource && typeof props.masterChunkSource === "object"
			? props.masterChunkSource
			: null;
		const chunkSource = props && props.chunkSource && typeof props.chunkSource === "object"
			? props.chunkSource
			: null;
		const fallbackSource = playbackChunkSource || chunkSource || null;
		const playbackSignature = fallbackSource ? `${fallbackSource.bucket || ""}:${fallbackSource.key || ""}` : "";
		const masterSignature = masterChunkSource ? `${masterChunkSource.bucket || ""}:${masterChunkSource.key || ""}` : "";
		return `${playbackSignature}|${masterSignature}`;
	}

	getStableSourceIdentity(props = this.props) {
		const sourcePlaybackGuid = `${props && props.sourcePlaybackGuid ? props.sourcePlaybackGuid : ""}`.trim();
		if (sourcePlaybackGuid) {
			return `guid:${sourcePlaybackGuid}`;
		}

		const chunkSourceSignature = this.getChunkSourceSignature(props);
		if (chunkSourceSignature) {
			return `source:${chunkSourceSignature}`;
		}

		const sourcePlaybackPath = `${props && props.sourcePlaybackPath ? props.sourcePlaybackPath : (props && props.baseOutputPath ? props.baseOutputPath : "")}`.trim();
		const fileName = `${props && props.fileName ? props.fileName : ""}`.trim();
		return JSON.stringify({
			fileName,
			sourcePlaybackPath
		});
	}

	getLegacyDraftSourceSignature(props = this.props) {
		return JSON.stringify({
			fileName: `${props && props.fileName ? props.fileName : ""}`.trim(),
			baseOutputPath: `${props && props.baseOutputPath ? props.baseOutputPath : ""}`.trim(),
			sourcePlaybackGuid: `${props && props.sourcePlaybackGuid ? props.sourcePlaybackGuid : ""}`.trim(),
			sourcePlaybackPath: `${props && props.sourcePlaybackPath ? props.sourcePlaybackPath : ""}`.trim(),
			chunkSourceSignature: this.getChunkSourceSignature(props)
		});
	}

	getResetSignature(props = this.props) {
		return this.getStableSourceIdentity(props);
	}

	getDraftSourceSignature(props = this.props) {
		return this.getStableSourceIdentity(props);
	}

	getSavedDraft(props = this.props) {
		const savedDraft = props && props.savedDraft && typeof props.savedDraft === "object" ? props.savedDraft : null;
		if (!savedDraft) {
			return null;
		}

		const currentStableSignature = this.getDraftSourceSignature(props);
		if (`${savedDraft.sourceSignature || ""}`.trim() === currentStableSignature) {
			return savedDraft;
		}

		const currentSourcePlaybackGuid = `${props && props.sourcePlaybackGuid ? props.sourcePlaybackGuid : ""}`.trim();
		const savedSourcePlaybackGuid = `${savedDraft.sourcePlaybackGuid || ""}`.trim();
		if (currentSourcePlaybackGuid && savedSourcePlaybackGuid) {
			return savedSourcePlaybackGuid === currentSourcePlaybackGuid ? savedDraft : null;
		}

		const currentChunkSourceSignature = this.getChunkSourceSignature(props);
		const savedChunkSourceSignature = `${savedDraft.chunkSourceSignature || ""}`.trim();
		const currentSourcePlaybackPath = `${props && props.sourcePlaybackPath ? props.sourcePlaybackPath : (props && props.baseOutputPath ? props.baseOutputPath : "")}`.trim();
		const savedSourcePlaybackPath = `${savedDraft.sourcePlaybackPath || savedDraft.baseOutputPath || ""}`.trim();
		if (!currentSourcePlaybackGuid && savedChunkSourceSignature && currentChunkSourceSignature && savedChunkSourceSignature === currentChunkSourceSignature) {
			return savedDraft;
		}
		if (!currentSourcePlaybackGuid && savedSourcePlaybackPath && currentSourcePlaybackPath && savedSourcePlaybackPath === currentSourcePlaybackPath) {
			return savedDraft;
		}

		return `${savedDraft.sourceSignature || ""}`.trim() === this.getLegacyDraftSourceSignature(props) ? savedDraft : null;
	}

	buildComparableDraftPayload(stateSnapshot = this.state, props = this.props) {
		const normalizedChunkPoints = normalizeOutputPoints(stateSnapshot.chunkPoints, stateSnapshot.duration, stateSnapshot.generationMode);
		const normalizedDuration = Number.isFinite(Number(stateSnapshot.duration)) ? Number(stateSnapshot.duration) : 0;
		return {
			version: 1,
			sourceSignature: this.getDraftSourceSignature(props),
			sourcePlaybackGuid: `${props && props.sourcePlaybackGuid ? props.sourcePlaybackGuid : ""}`.trim(),
			sourcePlaybackPath: `${props && props.sourcePlaybackPath ? props.sourcePlaybackPath : ""}`.trim(),
			baseOutputPath: `${props && props.baseOutputPath ? props.baseOutputPath : ""}`.trim(),
			chunkSourceSignature: this.getChunkSourceSignature(props),
			duration: normalizedDuration,
			generationMode: stateSnapshot.generationMode === GENERATION_MODE_KEYFRAMES ? GENERATION_MODE_KEYFRAMES : GENERATION_MODE_VIDEO_CHUNKS,
			keyframeImageFormat: stateSnapshot.keyframeImageFormat === KEYFRAME_IMAGE_FORMAT_JPG ? KEYFRAME_IMAGE_FORMAT_JPG : KEYFRAME_IMAGE_FORMAT_PNG,
			chunkPoints: normalizedChunkPoints,
			segmentSuffixes: _.cloneDeep(stateSnapshot.segmentSuffixes || []),
			segmentOutlineLabels: _.cloneDeep(stateSnapshot.segmentOutlineLabels || []),
			useOutlineSections: stateSnapshot.useOutlineSections === true,
			segmentSectionStarts: _.cloneDeep(stateSnapshot.segmentSectionStarts || []),
			segmentSectionTitles: _.cloneDeep(stateSnapshot.segmentSectionTitles || []),
			autoCreateOutline: stateSnapshot.autoCreateOutline === true,
			useMasterSource: stateSnapshot.useMasterSource === true,
			transcriptText: cleanTranscriptText(stateSnapshot.transcriptText || ""),
			transcriptVtt: cleanTranscriptVtt(stateSnapshot.transcriptVtt || ""),
			transcriptProvider: stateSnapshot.transcriptProvider || TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE,
			transcriptViewMode: stateSnapshot.transcriptViewMode || TRANSCRIPT_VIEW_TEXT,
			layoutMode: stateSnapshot.layoutMode || CHUNK_MODAL_LAYOUT_DEFAULT,
			showSelectedSegmentHighlight: stateSnapshot.showSelectedSegmentHighlight !== false,
			showCurrentCueHighlight: stateSnapshot.showCurrentCueHighlight !== false,
			showCurrentWordHighlight: stateSnapshot.showCurrentWordHighlight !== false,
			showStepCardHighlight: stateSnapshot.showStepCardHighlight !== false,
			customTextStepCards: _.cloneDeep(stateSnapshot.customTextStepCards || []),
			customSentenceGroups: _.cloneDeep(stateSnapshot.customSentenceGroups || []),
			activeOutputIndex: Math.max(0, Number(stateSnapshot.activeOutputIndex) || 0)
		};
	}

	getDraftComparisonSignature(stateSnapshot = this.state, props = this.props) {
		return JSON.stringify(this.buildComparableDraftPayload(stateSnapshot, props));
	}

	buildSavedDraftPayload(props = this.props) {
		return {
			savedAt: new Date().toISOString(),
			...this.buildComparableDraftPayload(this.state, props)
		};
	}

	applySavedDraftToInitialState(initialState, props = this.props) {
		const savedDraft = this.getSavedDraft(props);
		if (!savedDraft) {
			return initialState;
		}

		const generationMode = `${savedDraft.generationMode || ""}`.trim() === GENERATION_MODE_KEYFRAMES
			? GENERATION_MODE_KEYFRAMES
			: GENERATION_MODE_VIDEO_CHUNKS;
		const draftDuration = Number.isFinite(Number(savedDraft.duration)) ? Number(savedDraft.duration) : initialState.duration;
		const chunkPoints = normalizeOutputPoints(savedDraft.chunkPoints, draftDuration, generationMode);
		const transcriptText = cleanTranscriptText(savedDraft.transcriptText || "") || initialState.transcriptText;
		const transcriptVtt = cleanTranscriptVtt(savedDraft.transcriptVtt || "") || initialState.transcriptVtt;
			const nextState = {
				...initialState,
			duration: draftDuration,
			currentTime: 0,
			videoReady: false,
			generationMode,
			keyframeImageFormat: `${savedDraft.keyframeImageFormat || ""}`.trim().toLowerCase() === KEYFRAME_IMAGE_FORMAT_JPG
				? KEYFRAME_IMAGE_FORMAT_JPG
				: KEYFRAME_IMAGE_FORMAT_PNG,
			chunkPoints,
			keyframePreviewImages: [],
			keyframePreviewLoading: [],
			chunkPreviewUrls: [],
			chunkPreviewLoading: [],
			timedTranscriptWords: [],
			timedTranscriptSourceSignature: "",
			timedTranscriptWordsLoaded: false,
			segmentSuffixes: buildSegmentSuffixes(chunkPoints, draftDuration, savedDraft.segmentSuffixes, generationMode),
			segmentOutlineLabels: buildSegmentOutlineLabels(chunkPoints, draftDuration, savedDraft.segmentOutlineLabels, props.defaultOutlineLabelBase, generationMode),
			useOutlineSections: savedDraft.useOutlineSections === true,
			segmentSectionStarts: buildSegmentSectionStarts(chunkPoints, draftDuration, savedDraft.segmentSectionStarts, generationMode, savedDraft.useOutlineSections === true),
			segmentSectionTitles: buildSegmentSectionTitles(chunkPoints, draftDuration, savedDraft.segmentSectionTitles, generationMode),
			autoCreateOutline: savedDraft.autoCreateOutline === true,
			useMasterSource: savedDraft.useMasterSource === true && !!(props.masterChunkSource && props.masterChunkSource.bucket && props.masterChunkSource.key),
			transcriptText,
			transcriptVtt,
			transcriptProvider: `${savedDraft.transcriptProvider || ""}`.trim() || TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE,
			transcriptViewMode: `${savedDraft.transcriptViewMode || ""}`.trim() || (transcriptText ? TRANSCRIPT_VIEW_TEXT : (transcriptVtt ? TRANSCRIPT_VIEW_VTT : TRANSCRIPT_VIEW_TEXT)),
				layoutMode: `${savedDraft.layoutMode || ""}`.trim() || CHUNK_MODAL_LAYOUT_DEFAULT,
				showTopControlsPanel: `${savedDraft.layoutMode || ""}`.trim() === CHUNK_MODAL_LAYOUT_DEFAULT,
				showSelectedSegmentHighlight: savedDraft.showSelectedSegmentHighlight !== false,
			showCurrentCueHighlight: savedDraft.showCurrentCueHighlight !== false,
			showCurrentWordHighlight: savedDraft.showCurrentWordHighlight !== false,
			showStepCardHighlight: savedDraft.showStepCardHighlight !== false,
			customTextStepCards: _.cloneDeep(savedDraft.customTextStepCards || []),
			customSentenceGroups: _.cloneDeep(savedDraft.customSentenceGroups || []),
			activeOutputIndex: Math.max(0, Number(savedDraft.activeOutputIndex) || 0)
		};

		return nextState;
	}

	buildBaseInitialState(props = this.props) {
		const sourceMetaData = props && props.sourceMetaData && typeof props.sourceMetaData === "object" ? props.sourceMetaData : {};
		const transcriptText = cleanTranscriptText(sourceMetaData.transcript || "") || extractTranscriptFromVtt(sourceMetaData.transcriptVtt || "");
		const transcriptVtt = cleanTranscriptVtt(sourceMetaData.transcriptVtt || "");
		return {
			duration: 0,
			currentTime: 0,
			videoReady: false,
			generationMode: GENERATION_MODE_VIDEO_CHUNKS,
			keyframeImageFormat: KEYFRAME_IMAGE_FORMAT_PNG,
			chunkPoints: [],
			keyframePreviewImages: [],
			keyframePreviewLoading: [],
			chunkPreviewUrls: [],
			chunkPreviewLoading: [],
			timedTranscriptWords: [],
			timedTranscriptSourceSignature: "",
			timedTranscriptWordsLoaded: false,
			segmentSuffixes: buildSegmentSuffixes([], 0, [], GENERATION_MODE_VIDEO_CHUNKS),
			segmentOutlineLabels: buildSegmentOutlineLabels([], 0, [], props.defaultOutlineLabelBase, GENERATION_MODE_VIDEO_CHUNKS),
			useOutlineSections: false,
			segmentSectionStarts: buildSegmentSectionStarts([], 0, [], GENERATION_MODE_VIDEO_CHUNKS, false),
			segmentSectionTitles: buildSegmentSectionTitles([], 0, [], GENERATION_MODE_VIDEO_CHUNKS),
			autoCreateOutline: props.defaultAutoCreateOutline === true,
			useMasterSource: props.defaultUseMasterSource === true && !!(props.masterChunkSource && props.masterChunkSource.bucket && props.masterChunkSource.key),
			transcriptText,
			transcriptVtt,
			transcriptProvider: TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE,
			transcriptViewMode: transcriptText ? TRANSCRIPT_VIEW_TEXT : (transcriptVtt ? TRANSCRIPT_VIEW_VTT : TRANSCRIPT_VIEW_TEXT),
			layoutMode: CHUNK_MODAL_LAYOUT_DEFAULT,
			showTopControlsPanel: true,
			showSelectedSegmentHighlight: true,
			showCurrentCueHighlight: true,
			showCurrentWordHighlight: true,
			showStepCardHighlight: true,
			transcriptGeneratingField: "",
			transcriptError: "",
			selectedTranscriptRangeStart: null,
			selectedTranscriptRangeEnd: null,
			selectedTranscriptRangeText: "",
			selectedTranscriptCueIndexes: [],
			customTextStepCards: [],
			stepCardNotice: "",
			selectedSentenceIndexes: [],
			customSentenceGroups: [],
			sentenceGroupingNotice: "",
			activeOutputIndex: 0,
			copiedTranscriptState: "",
			processing: false,
			savingDraft: false,
			processingProgressCount: 0,
			processingExpectedCount: 0,
			processingStatusMessage: "",
			error: ""
		};
	}

	getInitialState(props = this.props) {
		return this.applySavedDraftToInitialState(this.buildBaseInitialState(props), props);
	}

	resetForOpen(props = this.props) {
		this.clearChunkProgressPolling();
		this.invalidateChunkPreviewRequests();
		this.invalidateTimedTranscriptRequests();
		this.activeChunkRunToken = null;
		this.processingCompletionHandledToken = null;
		const nextState = this.getInitialState(props);
		this.lastCommittedDraftSignature = this.getDraftComparisonSignature(nextState, props);
		this.setState(nextState, () => {
			this.loadTimedTranscriptWords();
			if (this.state.generationMode === GENERATION_MODE_KEYFRAMES && Array.isArray(this.state.chunkPoints) && this.state.chunkPoints.length) {
				this.state.chunkPoints.forEach((frameSeconds, index) => {
					if (Number.isFinite(Number(frameSeconds))) {
						this.requestKeyframePreview(index, Number(frameSeconds));
					}
				});
			} else if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS) {
				this.refreshChunkPreviews();
			}
		});
	}

	hasUnsavedDraftChanges() {
		return this.getDraftComparisonSignature(this.state, this.props) !== this.lastCommittedDraftSignature;
	}

	closeModal() {
		const { onClose = null } = this.props;
		if (onClose) {
			onClose();
		}
	}

	handleRequestClose() {
		const { onSaveDraft = null } = this.props;
		if (this.state.processing === true || this.state.savingDraft === true) {
			return;
		}
		if (!onSaveDraft || !this.hasUnsavedDraftChanges()) {
			this.closeModal();
			return;
		}

		showYesNo(
			"Save your Chunk Video draft before closing? Choosing Yes will save and close. Choosing No will close without saving.",
			"Close Chunk Video",
			async (yes) => {
				if (yes) {
					const saved = await this.handleSaveDraft();
					if (saved === true) {
						this.closeModal();
					}
					return;
				}
				this.closeModal();
			}
		);
	}

	buildResetAuthoringState(props = this.props) {
		const initialState = this.buildBaseInitialState(props);
		const safeDuration = Number(this.state.duration) || 0;
		const nextGenerationMode = this.state.generationMode === GENERATION_MODE_KEYFRAMES
			? GENERATION_MODE_KEYFRAMES
			: GENERATION_MODE_VIDEO_CHUNKS;

		return {
			...initialState,
			duration: safeDuration,
			currentTime: this.getCurrentScrubTime(),
			videoReady: this.state.videoReady === true && safeDuration > 0,
			generationMode: nextGenerationMode,
			keyframeImageFormat: this.state.keyframeImageFormat === KEYFRAME_IMAGE_FORMAT_JPG ? KEYFRAME_IMAGE_FORMAT_JPG : KEYFRAME_IMAGE_FORMAT_PNG,
			autoCreateOutline: this.state.autoCreateOutline === true,
			useMasterSource: this.state.useMasterSource === true,
			transcriptProvider: this.state.transcriptProvider || TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE,
			transcriptViewMode: this.state.transcriptViewMode || initialState.transcriptViewMode,
			layoutMode: this.state.layoutMode || CHUNK_MODAL_LAYOUT_DEFAULT,
			showTopControlsPanel: this.state.layoutMode === CHUNK_MODAL_LAYOUT_DEFAULT,
			showSelectedSegmentHighlight: this.state.showSelectedSegmentHighlight !== false,
			showCurrentCueHighlight: this.state.showCurrentCueHighlight !== false,
			showCurrentWordHighlight: this.state.showCurrentWordHighlight !== false,
			showStepCardHighlight: this.state.showStepCardHighlight !== false,
			savingDraft: false,
			segmentSuffixes: buildSegmentSuffixes([], safeDuration, [], nextGenerationMode),
			segmentOutlineLabels: buildSegmentOutlineLabels([], safeDuration, [], props.defaultOutlineLabelBase, nextGenerationMode),
			useOutlineSections: false,
			segmentSectionStarts: buildSegmentSectionStarts([], safeDuration, [], nextGenerationMode, false),
			segmentSectionTitles: buildSegmentSectionTitles([], safeDuration, [], nextGenerationMode)
		};
	}

	confirmResetAuthoringState() {
		if (this.state.processing === true) {
			return;
		}

		showConfirm(
			"Are you sure you want to clear all chunk/keyframe points, labels, transcript selections, and temporary cards in this tool? The source video will remain unchanged. Save afterward if you also want to replace the previously saved chunk draft.",
			"Clear Chunk Video",
			() => {
				this.clearChunkProgressPolling();
				this.invalidateChunkPreviewRequests();
				this.activeChunkRunToken = null;
				this.processingCompletionHandledToken = null;
				this.setState(this.buildResetAuthoringState(this.props));
			}
		);
	}

	registerOutlineLabelTextarea(index, node) {
		if (!Number.isFinite(Number(index))) {
			return;
		}

		if (node) {
			this.outlineLabelTextareaRefs[index] = node;
			this.syncTextareaHeight(node);
			return;
		}

		delete this.outlineLabelTextareaRefs[index];
	}

	syncTextareaHeight(textarea = null) {
		if (!textarea || !textarea.style) {
			return;
		}

		textarea.style.height = "auto";
		textarea.style.height = `${Math.max(textarea.scrollHeight, 72)}px`;
	}

	syncOutlineLabelTextareaHeights() {
		Object.values(this.outlineLabelTextareaRefs || {}).forEach((textarea) => {
			this.syncTextareaHeight(textarea);
		});
	}

	getMediaConverterFunctionName() {
		if (env && env.lambda && env.lambda.mediaConverter) {
			return env.lambda.mediaConverter;
		}
		const stage = `${env && env.stage ? env.stage : "dev"}`.trim() || "dev";
		return `ambifi-media-converter-lambda-${stage}`;
	}

	hasMasterChunkSource() {
		const { masterChunkSource = null } = this.props;
		return !!(masterChunkSource && masterChunkSource.bucket && masterChunkSource.key);
	}

	shouldUseMasterSource() {
		return this.hasMasterChunkSource() && this.state.useMasterSource === true;
	}

	getActiveChunkSource() {
		const { playbackChunkSource = null, masterChunkSource = null, chunkSource = null } = this.props;
		if (this.shouldUseMasterSource()) {
			return masterChunkSource || chunkSource || null;
		}
		return playbackChunkSource || chunkSource || masterChunkSource || null;
	}

	getActiveBaseProcessingOptions() {
		const {
			playbackBaseProcessingOptions = {},
			masterBaseProcessingOptions = {},
			baseProcessingOptions = {}
		} = this.props;
		if (this.shouldUseMasterSource()) {
			return masterBaseProcessingOptions || baseProcessingOptions || {};
		}
		return playbackBaseProcessingOptions || {};
	}

	getActiveSourceDescription() {
		const {
			playbackSourceDescription = "",
			masterSourceDescription = "",
			sourceDescription = ""
		} = this.props;
		if (this.shouldUseMasterSource()) {
			return `${masterSourceDescription || sourceDescription || ""}`.trim();
		}
		return `${playbackSourceDescription || sourceDescription || ""}`.trim();
	}

	buildChunkVideoSourceContext() {
		const activeChunkSource = this.getActiveChunkSource() || {};
		const playbackChunkSource = this.props.playbackChunkSource && typeof this.props.playbackChunkSource === "object"
			? this.props.playbackChunkSource
			: null;
		const masterChunkSource = this.props.masterChunkSource && typeof this.props.masterChunkSource === "object"
			? this.props.masterChunkSource
			: null;

		return {
			sourceMode: this.shouldUseMasterSource() ? "master" : "playback",
			useMasterSource: this.shouldUseMasterSource(),
			activeChunkSource: activeChunkSource && activeChunkSource.bucket && activeChunkSource.key
				? {
					bucket: `${activeChunkSource.bucket || ""}`.trim(),
					key: `${activeChunkSource.key || ""}`.trim()
				}
				: null,
			playbackChunkSource: playbackChunkSource && playbackChunkSource.bucket && playbackChunkSource.key
				? {
					bucket: `${playbackChunkSource.bucket || ""}`.trim(),
					key: `${playbackChunkSource.key || ""}`.trim()
				}
				: null,
			masterChunkSource: masterChunkSource && masterChunkSource.bucket && masterChunkSource.key
				? {
					bucket: `${masterChunkSource.bucket || ""}`.trim(),
					key: `${masterChunkSource.key || ""}`.trim()
				}
				: null,
			baseOutputPath: `${this.props.baseOutputPath || ""}`.trim(),
			fileName: `${this.props.fileName || ""}`.trim(),
			sourcePlaybackGuid: `${this.props.sourcePlaybackGuid || ""}`.trim(),
			sourcePlaybackPath: `${this.props.sourcePlaybackPath || ""}`.trim(),
			activeBaseProcessingOptions: JSON.parse(JSON.stringify(this.getActiveBaseProcessingOptions() || {}))
		};
	}

	getMasterSourceTooltipText() {
		const baseDescription = "Off uses the current playback MP4 for faster chunking. On re-renders from the original master and reapplies the current playback processing.";
		const activeSourceDescription = this.getActiveSourceDescription();
		return activeSourceDescription ? `${baseDescription}\n\n${activeSourceDescription}` : baseDescription;
	}

	getCaptureActionTooltipText(isKeyframeMode = false) {
		return isKeyframeMode
			? "Scrub to the frame you want, then capture it. Manage captured frames from the output cards below."
			: "Scrub to the split point you want, then add it. Manage split removal directly from the output segment cards below.";
	}

	getOutlineTooltipText(isKeyframeMode = false) {
		return isKeyframeMode
			? "Auto-create outline clones the selected item for each captured keyframe so the source item stays untouched and the keyframe image becomes the generated media."
			: "Auto-create outline clones the selected item for each generated chunk so the source item stays untouched and each generated step keeps the chunk video as its media.";
	}

	getOutlineSectionsTooltipText() {
		return "When sections are on, section breaks marked on the output cards become real AmbiFi section nodes and generated outputs are placed into those sections in order.";
	}

	renderInfoTooltip(text = "", style = {}) {
		if (!`${text || ""}`.trim()) {
			return null;
		}

		return (
			<span
				title={text}
				style={{
					display: "inline-flex",
					alignItems: "center",
					justifyContent: "center",
					width: "18px",
					height: "18px",
					borderRadius: "999px",
					border: "1px solid #cbd5e1",
					background: "#f8fafc",
					color: "#64748b",
					fontSize: "11px",
					fontWeight: 700,
					lineHeight: 1,
					cursor: "help",
					userSelect: "none",
					...style
				}}
			>
				i
			</span>
		);
	}

	handleUseMasterSourceChange(event) {
		const checked = !!(event && event.target && event.target.checked);
		this.invalidateChunkPreviewRequests();
		this.setState({
			useMasterSource: checked,
			keyframePreviewImages: [],
			keyframePreviewLoading: [],
			chunkPreviewUrls: [],
			chunkPreviewLoading: []
		}, () => {
			if (this.state.generationMode === GENERATION_MODE_KEYFRAMES && Array.isArray(this.state.chunkPoints) && this.state.chunkPoints.length) {
				this.state.chunkPoints.forEach((frameSeconds, index) => {
					if (Number.isFinite(Number(frameSeconds))) {
						this.requestKeyframePreview(index, Number(frameSeconds));
					}
				});
				return;
			}
			if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS) {
				this.refreshChunkPreviews();
			}
		});
	}

	handleLoadedMetadata(event) {
		const video = event && event.target ? event.target : null;
		const duration = Number(video && video.duration) || 0;
		this.setState((prevState) => {
			const chunkPoints = normalizeOutputPoints(prevState.chunkPoints, duration, prevState.generationMode);
			const useOutlineSections = prevState.useOutlineSections === true;
			return {
				duration,
				videoReady: duration > 0,
				currentTime: clampSeconds(video && video.currentTime ? video.currentTime : 0, duration),
				chunkPoints,
				segmentSuffixes: buildSegmentSuffixes(chunkPoints, duration, prevState.segmentSuffixes, prevState.generationMode),
				segmentOutlineLabels: buildSegmentOutlineLabels(chunkPoints, duration, prevState.segmentOutlineLabels, this.props.defaultOutlineLabelBase, prevState.generationMode),
				segmentSectionStarts: buildSegmentSectionStarts(chunkPoints, duration, prevState.segmentSectionStarts, prevState.generationMode, useOutlineSections),
				segmentSectionTitles: buildSegmentSectionTitles(chunkPoints, duration, prevState.segmentSectionTitles, prevState.generationMode)
			};
		}, () => {
			if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS && Array.isArray(this.state.chunkPoints) && this.state.chunkPoints.length) {
				this.refreshChunkPreviews();
			}
		});
	}

	handleTimeUpdate(event) {
		const video = event && event.target ? event.target : this.videoRef.current;
		if (!video) {
			return;
		}
		this.setState({
			currentTime: clampSeconds(video.currentTime || 0, this.state.duration)
		});
	}

	seekMainVideo(nextTime = 0) {
		const safeTime = clampSeconds(nextTime, this.state.duration);
		const video = this.videoRef.current;
		if (video) {
			try {
				if (Math.abs((video.currentTime || 0) - safeTime) > 0.01) {
					video.currentTime = safeTime;
				}
			} catch (error) {
				console.warn("Unable to seek chunk video preview", error);
			}
		}
		if (Math.abs((Number(this.state.currentTime) || 0) - safeTime) > 0.01) {
			this.setState({
				currentTime: safeTime
			});
		}
		return safeTime;
	}

	getCurrentScrubTime() {
		const video = this.videoRef.current;
		if (!video) {
			return clampSeconds(this.state.currentTime, this.state.duration);
		}
		return clampSeconds(video.currentTime || 0, this.state.duration);
	}

	getSmartScrubberPercent(value = 0, duration = this.state.duration) {
		if (!(Number(duration) > 0)) {
			return 0;
		}
		return Math.min(100, Math.max(0, (clampSeconds(value, duration) / duration) * 100));
	}

	getSmartScrubberTimeFromClientX(clientX = 0) {
		const track = this.smartScrubberTrackRef.current;
		if (!track || !(Number(this.state.duration) > 0)) {
			return clampSeconds(this.state.currentTime, this.state.duration);
		}
		const rect = track.getBoundingClientRect();
		if (!(rect && rect.width > 0)) {
			return clampSeconds(this.state.currentTime, this.state.duration);
		}
		const ratio = (Number(clientX) - rect.left) / rect.width;
		return clampSeconds(ratio * this.state.duration, this.state.duration);
	}

	getSegmentIndexForTime(seconds = 0, segments = this.getSegments()) {
		const safeTime = clampSeconds(seconds, this.state.duration);
		const safeSegments = Array.isArray(segments) ? segments : [];
		if (!safeSegments.length) {
			return 0;
		}

		const resolvedIndex = safeSegments.findIndex((segment, index) => {
			if (!segment || !Number.isFinite(Number(segment.startSeconds)) || !Number.isFinite(Number(segment.endSeconds))) {
				return false;
			}
			if (index === safeSegments.length - 1) {
				return safeTime >= Number(segment.startSeconds) && safeTime <= Number(segment.endSeconds);
			}
			return safeTime >= Number(segment.startSeconds) && safeTime < Number(segment.endSeconds);
		});
		return resolvedIndex >= 0 ? resolvedIndex : Math.max(0, safeSegments.length - 1);
	}

	getOutputPointDragBounds(index = 0, points = this.state.chunkPoints, duration = this.state.duration, generationMode = this.state.generationMode) {
		const safeIndex = Math.max(0, Number(index) || 0);
		const normalizedPoints = normalizeOutputPoints(points, duration, generationMode);
		const previousPoint = safeIndex > 0 ? Number(normalizedPoints[safeIndex - 1]) : null;
		const nextPoint = safeIndex < normalizedPoints.length - 1 ? Number(normalizedPoints[safeIndex + 1]) : null;

		if (generationMode === GENERATION_MODE_KEYFRAMES) {
			const minTime = previousPoint !== null
				? Math.min(duration, previousPoint + CHUNK_POINT_EPSILON_SECONDS)
				: 0;
			const maxTime = nextPoint !== null
				? Math.max(0, nextPoint - CHUNK_POINT_EPSILON_SECONDS)
				: Number(duration) || 0;
			return {
				minTime: Math.min(minTime, maxTime),
				maxTime: Math.max(minTime, maxTime)
			};
		}

		const minTime = previousPoint !== null
			? Math.min(duration, previousPoint + MIN_SEGMENT_DURATION_SECONDS)
			: MIN_SEGMENT_DURATION_SECONDS;
		const maxTime = nextPoint !== null
			? Math.max(0, nextPoint - MIN_SEGMENT_DURATION_SECONDS)
			: Math.max(MIN_SEGMENT_DURATION_SECONDS, (Number(duration) || 0) - MIN_SEGMENT_DURATION_SECONDS);
		return {
			minTime: Math.min(minTime, maxTime),
			maxTime: Math.max(minTime, maxTime)
		};
	}

	moveOutputPoint(index = 0, nextTime = 0, options = {}) {
		const { seekVideo = true } = options;
		const safeIndex = Math.max(0, Number(index) || 0);
		let resolvedTime = null;
		let nextGenerationMode = this.state.generationMode;
		let nextDuration = this.state.duration;
		this.setState((prevState) => {
			nextGenerationMode = prevState.generationMode;
			nextDuration = prevState.duration;
			const chunkPoints = normalizeOutputPoints(prevState.chunkPoints, prevState.duration, prevState.generationMode);
			if (safeIndex < 0 || safeIndex >= chunkPoints.length) {
				return null;
			}

			const { minTime, maxTime } = this.getOutputPointDragBounds(safeIndex, chunkPoints, prevState.duration, prevState.generationMode);
			resolvedTime = roundSeconds(Math.min(Math.max(Number(nextTime) || 0, minTime), maxTime));
			chunkPoints[safeIndex] = resolvedTime;
			const normalizedChunkPoints = normalizeOutputPoints(chunkPoints, prevState.duration, prevState.generationMode);
			const useOutlineSections = prevState.useOutlineSections === true;
			return {
				chunkPoints: normalizedChunkPoints,
				segmentSuffixes: buildSegmentSuffixes(normalizedChunkPoints, prevState.duration, prevState.segmentSuffixes, prevState.generationMode),
				segmentOutlineLabels: buildSegmentOutlineLabels(normalizedChunkPoints, prevState.duration, prevState.segmentOutlineLabels, this.props.defaultOutlineLabelBase, prevState.generationMode),
				segmentSectionStarts: buildSegmentSectionStarts(normalizedChunkPoints, prevState.duration, prevState.segmentSectionStarts, prevState.generationMode, useOutlineSections),
				segmentSectionTitles: buildSegmentSectionTitles(normalizedChunkPoints, prevState.duration, prevState.segmentSectionTitles, prevState.generationMode),
				activeOutputIndex: prevState.generationMode === GENERATION_MODE_KEYFRAMES
					? safeIndex
					: Math.max(0, Math.min(safeIndex, normalizedChunkPoints.length)),
				currentTime: resolvedTime,
				error: ""
			};
		}, () => {
			if (seekVideo === true && Number.isFinite(resolvedTime)) {
				this.seekMainVideo(resolvedTime);
			}
			if (nextGenerationMode === GENERATION_MODE_VIDEO_CHUNKS) {
				this.refreshChunkPreviews();
			}
		});
		return Number.isFinite(resolvedTime) ? resolvedTime : clampSeconds(nextTime, nextDuration);
	}

	startSmartScrubberDrag(dragState = null) {
		this.stopSmartScrubberDrag();
		this.activeSmartScrubberDrag = dragState;
		if (typeof window === "undefined") {
			return;
		}
		window.addEventListener("pointermove", this.handleSmartScrubberGlobalPointerMove);
		window.addEventListener("pointerup", this.handleSmartScrubberGlobalPointerUp);
		window.addEventListener("pointercancel", this.handleSmartScrubberGlobalPointerUp);
	}

	stopSmartScrubberDrag() {
		this.activeSmartScrubberDrag = null;
		if (typeof window === "undefined") {
			return;
		}
		window.removeEventListener("pointermove", this.handleSmartScrubberGlobalPointerMove);
		window.removeEventListener("pointerup", this.handleSmartScrubberGlobalPointerUp);
		window.removeEventListener("pointercancel", this.handleSmartScrubberGlobalPointerUp);
	}

	handleSmartScrubberTrackPointerDown(event) {
		if (this.state.processing === true || !(Number(this.state.duration) > 0)) {
			return;
		}
		event.preventDefault();
		const nextTime = this.getSmartScrubberTimeFromClientX(event.clientX);
		const resolvedTime = this.seekMainVideo(nextTime);
		if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS) {
			this.handleSetActiveOutputIndex(this.getSegmentIndexForTime(resolvedTime));
		}
		this.startSmartScrubberDrag({
			type: "scrub"
		});
	}

	handleSmartScrubberMarkerPointerDown(index = 0, event) {
		if (this.state.processing === true || !(Number(this.state.duration) > 0)) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		const safeIndex = Math.max(0, Number(index) || 0);
		const normalizedPoints = normalizeOutputPoints(this.state.chunkPoints, this.state.duration, this.state.generationMode);
		const pointTime = Number(normalizedPoints[safeIndex]);
		if (!Number.isFinite(pointTime)) {
			return;
		}
		this.seekMainVideo(pointTime);
		this.handleSetActiveOutputIndex(this.state.generationMode === GENERATION_MODE_KEYFRAMES ? safeIndex : Math.max(0, Math.min(safeIndex, normalizedPoints.length)));
		this.startSmartScrubberDrag({
			type: "marker",
			index: safeIndex,
			generationMode: this.state.generationMode,
			startTime: pointTime,
			lastTime: pointTime,
			moved: false
		});
	}

	handleSmartScrubberGlobalPointerMove(event) {
		const dragState = this.activeSmartScrubberDrag;
		if (!dragState) {
			return;
		}
		event.preventDefault();
		const nextTime = this.getSmartScrubberTimeFromClientX(event.clientX);
		if (dragState.type === "scrub") {
			const resolvedTime = this.seekMainVideo(nextTime);
			if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS) {
				this.handleSetActiveOutputIndex(this.getSegmentIndexForTime(resolvedTime));
			}
			return;
		}
		if (dragState.type === "marker") {
			const resolvedTime = this.moveOutputPoint(dragState.index, nextTime, { seekVideo: true });
			dragState.lastTime = resolvedTime;
			dragState.moved = dragState.moved === true || Math.abs(resolvedTime - dragState.startTime) > 0.01;
		}
	}

	handleSmartScrubberGlobalPointerUp() {
		const dragState = this.activeSmartScrubberDrag;
		this.stopSmartScrubberDrag();
		if (!dragState) {
			return;
		}
		if (
			dragState.type === "marker" &&
			dragState.generationMode === GENERATION_MODE_KEYFRAMES &&
			dragState.moved === true &&
			Number.isFinite(Number(dragState.lastTime))
		) {
			this.requestKeyframePreview(dragState.index, Number(dragState.lastTime));
		}
	}

	handleAddChunkPoint() {
		const scrubTime = this.getCurrentScrubTime();
		if (!(this.state.duration > 0)) {
			return;
		}
		if (
			this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS &&
			(scrubTime <= MIN_SEGMENT_DURATION_SECONDS || scrubTime >= (this.state.duration - MIN_SEGMENT_DURATION_SECONDS))
		) {
			return;
		}
		let capturedPreviewIndex = null;
		let capturedPreviewTime = null;
		this.setState((prevState) => {
			const chunkPoints = normalizeOutputPoints([...prevState.chunkPoints, scrubTime], prevState.duration, prevState.generationMode);
			const useOutlineSections = prevState.useOutlineSections === true;
			let segmentSuffixes = buildSegmentSuffixes(chunkPoints, prevState.duration, prevState.segmentSuffixes, prevState.generationMode);
			let segmentOutlineLabels = buildSegmentOutlineLabels(chunkPoints, prevState.duration, prevState.segmentOutlineLabels, this.props.defaultOutlineLabelBase, prevState.generationMode);
			let segmentSectionStarts = buildSegmentSectionStarts(chunkPoints, prevState.duration, prevState.segmentSectionStarts, prevState.generationMode, useOutlineSections);
			let segmentSectionTitles = buildSegmentSectionTitles(chunkPoints, prevState.duration, prevState.segmentSectionTitles, prevState.generationMode);
			let activeOutputIndex = prevState.activeOutputIndex;
			if (prevState.generationMode === GENERATION_MODE_KEYFRAMES) {
				capturedPreviewIndex = chunkPoints.findIndex((value) => Math.abs(value - scrubTime) <= CHUNK_POINT_EPSILON_SECONDS);
				capturedPreviewTime = capturedPreviewIndex >= 0 ? chunkPoints[capturedPreviewIndex] : null;
				activeOutputIndex = capturedPreviewIndex >= 0 ? capturedPreviewIndex : prevState.activeOutputIndex;
			} else {
				const insertedPointIndex = chunkPoints.findIndex((value) => Math.abs(value - scrubTime) <= CHUNK_POINT_EPSILON_SECONDS);
				if (insertedPointIndex >= 0) {
					const insertAtSegmentIndex = insertedPointIndex + 1;
					const previousSuffixes = [...(prevState.segmentSuffixes || [])];
					const previousLabels = [...(prevState.segmentOutlineLabels || [])];
					const previousSectionStarts = [...(prevState.segmentSectionStarts || [])];
					const previousSectionTitles = [...(prevState.segmentSectionTitles || [])];
					previousSuffixes.splice(insertAtSegmentIndex, 0, "");
					previousLabels.splice(insertAtSegmentIndex, 0, "");
					previousSectionStarts.splice(insertAtSegmentIndex, 0, false);
					previousSectionTitles.splice(insertAtSegmentIndex, 0, "");
					segmentSuffixes = buildSegmentSuffixes(chunkPoints, prevState.duration, previousSuffixes, prevState.generationMode);
					segmentOutlineLabels = buildSegmentOutlineLabels(chunkPoints, prevState.duration, previousLabels, this.props.defaultOutlineLabelBase, prevState.generationMode);
					segmentSectionStarts = buildSegmentSectionStarts(chunkPoints, prevState.duration, previousSectionStarts, prevState.generationMode, useOutlineSections);
					segmentSectionTitles = buildSegmentSectionTitles(chunkPoints, prevState.duration, previousSectionTitles, prevState.generationMode);
					activeOutputIndex = insertAtSegmentIndex;
				}
			}
			return {
				chunkPoints,
				keyframePreviewImages: prevState.generationMode === GENERATION_MODE_KEYFRAMES ? [...(prevState.keyframePreviewImages || [])] : prevState.keyframePreviewImages,
				keyframePreviewLoading: prevState.generationMode === GENERATION_MODE_KEYFRAMES ? [...(prevState.keyframePreviewLoading || [])] : prevState.keyframePreviewLoading,
				chunkPreviewUrls: prevState.generationMode === GENERATION_MODE_VIDEO_CHUNKS ? [...(prevState.chunkPreviewUrls || [])] : prevState.chunkPreviewUrls,
				chunkPreviewLoading: prevState.generationMode === GENERATION_MODE_VIDEO_CHUNKS ? [...(prevState.chunkPreviewLoading || [])] : prevState.chunkPreviewLoading,
				segmentSuffixes,
				segmentOutlineLabels,
				segmentSectionStarts,
				segmentSectionTitles,
				activeOutputIndex,
				error: ""
			};
		}, () => {
			if (this.state.generationMode === GENERATION_MODE_KEYFRAMES && capturedPreviewIndex !== null && capturedPreviewIndex >= 0 && Number.isFinite(capturedPreviewTime)) {
				this.requestKeyframePreview(capturedPreviewIndex, capturedPreviewTime);
				return;
			}
			if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS) {
				this.refreshChunkPreviews();
			}
		});
	}

	handleRemoveChunkPoint(index) {
		this.setState((prevState) => {
			const chunkPoints = prevState.chunkPoints.filter((_, pointIndex) => pointIndex !== index);
			const keyframePreviewImages = (prevState.keyframePreviewImages || []).filter((_, pointIndex) => pointIndex !== index);
			const keyframePreviewLoading = (prevState.keyframePreviewLoading || []).filter((_, pointIndex) => pointIndex !== index);
			const chunkPreviewUrls = (prevState.chunkPreviewUrls || []).filter((_, pointIndex) => pointIndex !== index);
			const chunkPreviewLoading = (prevState.chunkPreviewLoading || []).filter((_, pointIndex) => pointIndex !== index);
			const useOutlineSections = prevState.useOutlineSections === true;
			return {
				chunkPoints,
				keyframePreviewImages,
				keyframePreviewLoading,
				chunkPreviewUrls,
				chunkPreviewLoading,
				segmentSuffixes: buildSegmentSuffixes(chunkPoints, prevState.duration, prevState.segmentSuffixes, prevState.generationMode),
				segmentOutlineLabels: buildSegmentOutlineLabels(chunkPoints, prevState.duration, prevState.segmentOutlineLabels, this.props.defaultOutlineLabelBase, prevState.generationMode),
				segmentSectionStarts: buildSegmentSectionStarts(chunkPoints, prevState.duration, prevState.segmentSectionStarts, prevState.generationMode, useOutlineSections),
				segmentSectionTitles: buildSegmentSectionTitles(chunkPoints, prevState.duration, prevState.segmentSectionTitles, prevState.generationMode)
			};
		}, () => {
			if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS) {
				this.refreshChunkPreviews();
			}
		});
	}

	handleRemoveOutput(index) {
		const normalizedIndex = Math.max(0, Number(index) || 0);
		this.setState((prevState) => {
			const chunkPoints = Array.isArray(prevState.chunkPoints) ? [...prevState.chunkPoints] : [];
			const keyframePreviewImages = [...(prevState.keyframePreviewImages || [])];
			const keyframePreviewLoading = [...(prevState.keyframePreviewLoading || [])];
			const chunkPreviewUrls = [...(prevState.chunkPreviewUrls || [])];
			const chunkPreviewLoading = [...(prevState.chunkPreviewLoading || [])];
			const previousSuffixes = [...(prevState.segmentSuffixes || [])];
			const previousLabels = [...(prevState.segmentOutlineLabels || [])];
			const previousSectionStarts = [...(prevState.segmentSectionStarts || [])];
			const previousSectionTitles = [...(prevState.segmentSectionTitles || [])];
			const useOutlineSections = prevState.useOutlineSections === true;

			if (prevState.generationMode === GENERATION_MODE_KEYFRAMES) {
				if (normalizedIndex < 0 || normalizedIndex >= chunkPoints.length) {
					return null;
				}

				chunkPoints.splice(normalizedIndex, 1);
				keyframePreviewImages.splice(normalizedIndex, 1);
				keyframePreviewLoading.splice(normalizedIndex, 1);
				previousSuffixes.splice(normalizedIndex, 1);
				previousLabels.splice(normalizedIndex, 1);
				previousSectionStarts.splice(normalizedIndex, 1);
				previousSectionTitles.splice(normalizedIndex, 1);
				return {
					chunkPoints,
					keyframePreviewImages,
					keyframePreviewLoading,
					chunkPreviewUrls,
					chunkPreviewLoading,
					segmentSuffixes: buildSegmentSuffixes(chunkPoints, prevState.duration, previousSuffixes, prevState.generationMode),
					segmentOutlineLabels: buildSegmentOutlineLabels(chunkPoints, prevState.duration, previousLabels, this.props.defaultOutlineLabelBase, prevState.generationMode),
					segmentSectionStarts: buildSegmentSectionStarts(chunkPoints, prevState.duration, previousSectionStarts, prevState.generationMode, useOutlineSections),
					segmentSectionTitles: buildSegmentSectionTitles(chunkPoints, prevState.duration, previousSectionTitles, prevState.generationMode),
					activeOutputIndex: Math.max(0, Math.min(normalizedIndex, chunkPoints.length - 1)),
					error: ""
				};
			}

			if (chunkPoints.length < 1) {
				return null;
			}

			const normalizedPointIndex = normalizedIndex >= chunkPoints.length ? chunkPoints.length - 1 : normalizedIndex;
			if (normalizedPointIndex < 0 || normalizedPointIndex >= chunkPoints.length) {
				return null;
			}

			chunkPoints.splice(normalizedPointIndex, 1);
			const mergedSegmentIndexToRemove = normalizedPointIndex + 1;
			if (mergedSegmentIndexToRemove >= 0 && mergedSegmentIndexToRemove < previousSuffixes.length) {
				previousSuffixes.splice(mergedSegmentIndexToRemove, 1);
			}
			if (mergedSegmentIndexToRemove >= 0 && mergedSegmentIndexToRemove < previousLabels.length) {
				previousLabels.splice(mergedSegmentIndexToRemove, 1);
			}
			if (mergedSegmentIndexToRemove >= 0 && mergedSegmentIndexToRemove < previousSectionStarts.length) {
				previousSectionStarts.splice(mergedSegmentIndexToRemove, 1);
			}
			if (mergedSegmentIndexToRemove >= 0 && mergedSegmentIndexToRemove < previousSectionTitles.length) {
				previousSectionTitles.splice(mergedSegmentIndexToRemove, 1);
			}

			return {
				chunkPoints,
				keyframePreviewImages,
				keyframePreviewLoading,
				chunkPreviewUrls,
				chunkPreviewLoading,
				segmentSuffixes: buildSegmentSuffixes(chunkPoints, prevState.duration, previousSuffixes, prevState.generationMode),
				segmentOutlineLabels: buildSegmentOutlineLabels(chunkPoints, prevState.duration, previousLabels, this.props.defaultOutlineLabelBase, prevState.generationMode),
				segmentSectionStarts: buildSegmentSectionStarts(chunkPoints, prevState.duration, previousSectionStarts, prevState.generationMode, useOutlineSections),
				segmentSectionTitles: buildSegmentSectionTitles(chunkPoints, prevState.duration, previousSectionTitles, prevState.generationMode),
				activeOutputIndex: Math.max(0, Math.min(normalizedPointIndex, chunkPoints.length)),
				error: ""
			};
		}, () => {
			if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS) {
				this.refreshChunkPreviews();
			}
		});
	}

	getRemoveOutputLabel(index, segmentCount) {
		if (this.state.generationMode === GENERATION_MODE_KEYFRAMES) {
			return "Remove";
		}
		const normalizedIndex = Math.max(0, Number(index) || 0);
		const totalSegments = Math.max(0, Number(segmentCount) || 0);
		return normalizedIndex >= totalSegments - 1 ? "Remove Split Before" : "Remove Split After";
	}

	handleSuffixChange(index, event) {
		const nextValue = event && event.target ? event.target.value : "";
		this.setState((prevState) => {
			const segmentSuffixes = [...prevState.segmentSuffixes];
			segmentSuffixes[index] = nextValue;
			return {
				segmentSuffixes
			};
		});
	}

	handleOutlineLabelChange(index, event) {
		const nextValue = event && event.target ? event.target.value : "";
		this.syncTextareaHeight(event && event.target ? event.target : null);
		this.setState((prevState) => {
			const segmentOutlineLabels = [...prevState.segmentOutlineLabels];
			segmentOutlineLabels[index] = nextValue;
			return {
				segmentOutlineLabels
			};
		});
	}

	handleAutoCreateOutlineChange(event) {
		this.setState({
			autoCreateOutline: !!(event && event.target && event.target.checked)
		});
	}

	handleUseOutlineSectionsChange(event) {
		const checked = !!(event && event.target && event.target.checked);
		this.setState((prevState) => ({
			useOutlineSections: checked,
			segmentSectionStarts: buildSegmentSectionStarts(prevState.chunkPoints, prevState.duration, prevState.segmentSectionStarts, prevState.generationMode, checked),
			segmentSectionTitles: buildSegmentSectionTitles(prevState.chunkPoints, prevState.duration, prevState.segmentSectionTitles, prevState.generationMode)
		}));
	}

	handleOutlineSectionStartChange(index, event) {
		const safeIndex = Math.max(0, Number(index) || 0);
		const checked = !!(event && event.target && event.target.checked);
		this.setState((prevState) => {
			if (safeIndex <= 0) {
				return null;
			}
			const segmentSectionStarts = buildSegmentSectionStarts(prevState.chunkPoints, prevState.duration, prevState.segmentSectionStarts, prevState.generationMode, prevState.useOutlineSections === true);
			segmentSectionStarts[safeIndex] = checked;
			return {
				segmentSectionStarts
			};
		});
	}

	handleOutlineSectionTitleChange(index, event) {
		const safeIndex = Math.max(0, Number(index) || 0);
		const nextValue = event && event.target ? event.target.value : "";
		this.setState((prevState) => {
			const segmentSectionTitles = [...(prevState.segmentSectionTitles || [])];
			segmentSectionTitles[safeIndex] = nextValue;
			return {
				segmentSectionTitles
			};
		});
	}

	handleGenerationModeChange(event) {
		const nextGenerationMode = `${event && event.target ? event.target.value : ""}`.trim() === GENERATION_MODE_KEYFRAMES
			? GENERATION_MODE_KEYFRAMES
			: GENERATION_MODE_VIDEO_CHUNKS;
		this.invalidateChunkPreviewRequests();
		this.setState({
			generationMode: nextGenerationMode,
			chunkPoints: [],
			keyframePreviewImages: [],
			keyframePreviewLoading: [],
			chunkPreviewUrls: [],
			chunkPreviewLoading: [],
			segmentSuffixes: buildSegmentSuffixes([], this.state.duration, [], nextGenerationMode),
			segmentOutlineLabels: buildSegmentOutlineLabels([], this.state.duration, [], this.props.defaultOutlineLabelBase, nextGenerationMode),
			useOutlineSections: this.state.useOutlineSections === true,
			segmentSectionStarts: buildSegmentSectionStarts([], this.state.duration, [], nextGenerationMode, this.state.useOutlineSections === true),
			segmentSectionTitles: buildSegmentSectionTitles([], this.state.duration, [], nextGenerationMode),
			transcriptViewMode: nextGenerationMode === GENERATION_MODE_KEYFRAMES && this.state.transcriptViewMode === TRANSCRIPT_VIEW_MATCHED_CHUNKS
				? TRANSCRIPT_VIEW_TEXT
				: this.state.transcriptViewMode,
			error: ""
		});
	}

	handleKeyframeImageFormatChange(event) {
		const nextImageFormat = `${event && event.target ? event.target.value : ""}`.trim().toLowerCase() === KEYFRAME_IMAGE_FORMAT_JPG
			? KEYFRAME_IMAGE_FORMAT_JPG
			: KEYFRAME_IMAGE_FORMAT_PNG;
		this.setState({
			keyframeImageFormat: nextImageFormat
		});
	}

	handleTranscriptProviderChange(event) {
		this.setState({
			transcriptProvider: event && event.target ? event.target.value : TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE
		});
	}

	handleTranscriptViewModeChange(nextViewMode = TRANSCRIPT_VIEW_TEXT) {
		this.setState({
			transcriptViewMode: nextViewMode === TRANSCRIPT_VIEW_VTT
				? TRANSCRIPT_VIEW_VTT
				: (nextViewMode === TRANSCRIPT_VIEW_MATCHED_CHUNKS
					? TRANSCRIPT_VIEW_MATCHED_CHUNKS
					: (nextViewMode === TRANSCRIPT_VIEW_STEP_CARDS
						? TRANSCRIPT_VIEW_STEP_CARDS
						: (nextViewMode === TRANSCRIPT_VIEW_SENTENCES ? TRANSCRIPT_VIEW_SENTENCES : TRANSCRIPT_VIEW_TEXT)))
		});
	}

	handleChunkModalLayoutChange(nextLayoutMode = CHUNK_MODAL_LAYOUT_DEFAULT) {
		const resolvedLayoutMode = nextLayoutMode === CHUNK_MODAL_LAYOUT_VIDEO_FOCUS || nextLayoutMode === CHUNK_MODAL_LAYOUT_TRANSCRIPT_FOCUS
			? CHUNK_MODAL_LAYOUT_VIDEO_FOCUS
			: (nextLayoutMode === CHUNK_MODAL_LAYOUT_STEPS_FOCUS ? CHUNK_MODAL_LAYOUT_STEPS_FOCUS : CHUNK_MODAL_LAYOUT_DEFAULT);
		this.setState({
			layoutMode: resolvedLayoutMode,
			showTopControlsPanel: resolvedLayoutMode === CHUNK_MODAL_LAYOUT_DEFAULT
		});
	}

	handleTopControlsPanelToggle() {
		this.setState((prevState) => ({
			showTopControlsPanel: prevState.showTopControlsPanel !== true
		}));
	}

	handleTranscriptHighlightToggle(fieldName = "") {
		const allowedFields = [
			"showSelectedSegmentHighlight",
			"showCurrentCueHighlight",
			"showCurrentWordHighlight",
			"showStepCardHighlight"
		];
		if (!allowedFields.includes(fieldName)) {
			return;
		}
		this.setState((prevState) => ({
			[fieldName]: prevState[fieldName] === false
		}));
	}

	async handleSaveDraft() {
		const { onSaveDraft = null } = this.props;
		if (!onSaveDraft || this.state.processing === true || this.state.savingDraft === true) {
			return false;
		}

		this.setState({
			savingDraft: true,
			error: ""
		});

		try {
			await Promise.resolve(onSaveDraft(this.buildSavedDraftPayload(this.props)));
			this.lastCommittedDraftSignature = this.getDraftComparisonSignature(this.state, this.props);
			return true;
		} catch (error) {
			console.error("Unable to save chunk video draft", error);
			this.setState({
				error: `${error && error.message ? error.message : error}`
			});
			return false;
		} finally {
			if (this._isMounted) {
				this.setState({
					savingDraft: false
				});
			}
		}
	}

	handleSetActiveOutputIndex(index = 0) {
		this.setState({
			activeOutputIndex: Math.max(0, Number(index) || 0)
		});
	}

	getTranscriptCues() {
		return parseVttCues(this.state.transcriptVtt);
	}

	getTranscriptSentences() {
		return buildTranscriptSentenceEntries(this.getCurrentTranscriptSourceText(), this.state.transcriptVtt);
	}

	getCustomSentenceGroups() {
		return Array.isArray(this.state.customSentenceGroups) ? this.state.customSentenceGroups : [];
	}

	getSentenceGroupAssignments(groups = this.getCustomSentenceGroups()) {
		const assignments = {};
		(Array.isArray(groups) ? groups : []).forEach((group, groupIndex) => {
			(Array.isArray(group && group.sentenceIndexes) ? group.sentenceIndexes : []).forEach((sentenceIndex) => {
				if (!Number.isFinite(Number(sentenceIndex)) || assignments[sentenceIndex]) {
					return;
				}
				assignments[sentenceIndex] = {
					groupId: group.id,
					groupIndex,
					groupOrder: groupIndex + 1,
					colorIndex: Number(group.colorIndex) || 0,
					color: getCustomSentenceGroupColor(group.colorIndex)
				};
			});
		});
		return assignments;
	}

	getCustomSentenceGroupText(group = null, sentences = this.getTranscriptSentences()) {
		if (!group) {
			return "";
		}
		const sentenceIndexes = Array.isArray(group.sentenceIndexes) ? group.sentenceIndexes : [];
		return normalizeTranscriptSuggestion(sentenceIndexes.map((index) => sentences[index] && sentences[index].text ? sentences[index].text : "").filter(Boolean).join(" "));
	}

	getCustomSentenceGroupRange(group = null, sentences = this.getTranscriptSentences()) {
		if (!group) {
			return { startSeconds: null, endSeconds: null };
		}
		const sentenceIndexes = Array.isArray(group.sentenceIndexes) ? group.sentenceIndexes : [];
		const groupSentences = sentenceIndexes.map((index) => sentences[index]).filter(Boolean);
		const timedSentences = groupSentences.filter((sentence) => sentence && sentence.startSeconds !== null && sentence.endSeconds !== null);
		if (!timedSentences.length) {
			return { startSeconds: null, endSeconds: null };
		}
		return {
			startSeconds: timedSentences[0].startSeconds,
			endSeconds: timedSentences[timedSentences.length - 1].endSeconds
		};
	}

	getTranscriptSuggestions(segments = this.getSegments(), cues = this.getTranscriptCues()) {
		return (Array.isArray(segments) ? segments : []).map((segment) => {
			return this.state.generationMode === GENERATION_MODE_KEYFRAMES
				? buildKeyframeTranscriptSuggestion(segment, cues)
				: buildChunkTranscriptSuggestion(segment, cues);
		});
	}

	getPlaybackTranscriptSourcePath() {
		const sourcePath = `${this.props.sourcePlaybackPath || _.get(this.props, "playbackChunkSource.key", _.get(this.props, "chunkSource.key", "")) || ""}`.trim();
		if (!sourcePath) {
			return "";
		}

		const pathParts = sourcePath.replace(/^\/+/, "").split("/").filter(Boolean);
		if (pathParts.length < 2) {
			return sourcePath.replace(/^\/+/, "");
		}

		const rootSegment = pathParts[0];
		if (rootSegment !== "private" && rootSegment !== "public") {
			return sourcePath.replace(/^\/+/, "");
		}

		const hasOrgSegment = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pathParts[1] || "");
		if (!hasOrgSegment && `${this.props.orgId || ""}`.trim()) {
			pathParts.splice(1, 0, `${this.props.orgId}`.trim());
		}
		return pathParts.join("/");
	}

	getMatchedChunkTranscriptTexts(segments = this.getSegments(), timedTranscriptWords = this.state.timedTranscriptWords) {
		if (this.state.generationMode !== GENERATION_MODE_VIDEO_CHUNKS || !Array.isArray(segments) || segments.length === 0) {
			return [];
		}
		return segments.map((segment) => buildChunkMatchedTranscriptText(segment, timedTranscriptWords, this.state.duration));
	}

	getCurrentTranscriptSourceText() {
		return cleanTranscriptText(this.state.transcriptText || "") || extractTranscriptFromVtt(this.state.transcriptVtt || "");
	}

	async loadTimedTranscriptWords(options = {}) {
		const { awsPayload = null, force = false } = options;
		const sourcePath = this.getPlaybackTranscriptSourcePath();
		const sourceBucket = `${_.get(this.props, "playbackChunkSource.bucket", _.get(this.props, "chunkSource.bucket", _.get(env, "s3.assetsBucket", ""))) || ""}`.trim();
		const sourceSignature = `${sourceBucket}:${sourcePath}`;
		const requestToken = buildChunkJobId();
		this.timedTranscriptLoadToken = requestToken;

		const inlineTimedWords = extractTimedTranscriptWordsFromAwsTranscribePayload(awsPayload);
		if (inlineTimedWords.length > 0) {
			if (this._isMounted && this.timedTranscriptLoadToken === requestToken) {
				this.setState({
					timedTranscriptWords: inlineTimedWords,
					timedTranscriptSourceSignature: sourceSignature,
					timedTranscriptWordsLoaded: true
				});
			}
			return inlineTimedWords;
		}

		if (!sourcePath || !sourceBucket) {
			return [];
		}
		if (
			force !== true &&
			this.state.timedTranscriptSourceSignature === sourceSignature &&
			(this.state.timedTranscriptWordsLoaded === true || (Array.isArray(this.state.timedTranscriptWords) && this.state.timedTranscriptWords.length > 0))
		) {
			return this.state.timedTranscriptWords || [];
		}

		if (this._isMounted) {
			this.setState({
				timedTranscriptSourceSignature: sourceSignature,
				timedTranscriptWordsLoaded: false
			});
		}

		const { transcriptionPaths } = buildAwsTranscribeCompanionPathCandidates(sourcePath, this.props.orgId);
		let transcriptionPayload = null;
		for (let index = 0; index < transcriptionPaths.length; index += 1) {
			try {
				transcriptionPayload = await getObject(sourceBucket, transcriptionPaths[index]);
				if (transcriptionPayload) {
					break;
				}
			} catch (error) {
				transcriptionPayload = null;
			}
		}

		const nextTimedWords = extractTimedTranscriptWordsFromAwsTranscribePayload(transcriptionPayload);
		if (!this._isMounted || this.timedTranscriptLoadToken !== requestToken) {
			return nextTimedWords;
		}

		this.setState((prevState) => {
			const transcriptText = prevState.transcriptText || cleanTranscriptText(extractTranscriptFromAwsTranscribeJson(transcriptionPayload));
			return {
				timedTranscriptWords: nextTimedWords,
				timedTranscriptSourceSignature: sourceSignature,
				timedTranscriptWordsLoaded: true,
				...(transcriptText ? { transcriptText } : {})
			};
		});
		return nextTimedWords;
	}

	getCustomTextStepCards() {
		return Array.isArray(this.state.customTextStepCards) ? this.state.customTextStepCards : [];
	}

	getTextStepCardRanges(cards = this.getCustomTextStepCards()) {
		return (Array.isArray(cards) ? cards : [])
			.map((card, cardIndex) => {
				const startOffset = Number.isFinite(Number(card && card.startOffset)) ? Number(card.startOffset) : null;
				const endOffset = Number.isFinite(Number(card && card.endOffset)) ? Number(card.endOffset) : null;
				if (startOffset === null || endOffset === null || endOffset <= startOffset) {
					return null;
				}
				return {
					...(card || {}),
					cardIndex,
					cardOrder: cardIndex + 1,
					colorIndex: Number(card && card.colorIndex) || 0,
					color: getCustomTextStepCardColor(card && card.colorIndex),
					startOffset,
					endOffset
				};
			})
			.filter(Boolean);
	}

	handleTranscriptTextSelection(event) {
		const textarea = event && event.target ? event.target : this.transcriptSelectionTextareaRef.current;
		if (!textarea) {
			return;
		}

		const selectionStart = Number.isFinite(Number(textarea.selectionStart)) ? Number(textarea.selectionStart) : null;
		const selectionEnd = Number.isFinite(Number(textarea.selectionEnd)) ? Number(textarea.selectionEnd) : null;
		const selectedTranscriptRangeText = selectionStart !== null && selectionEnd !== null && selectionEnd > selectionStart
			? normalizeTranscriptSuggestion((textarea.value || "").slice(selectionStart, selectionEnd))
			: "";

		this.setState({
			selectedTranscriptRangeStart: selectionStart,
			selectedTranscriptRangeEnd: selectionEnd,
			selectedTranscriptRangeText
		});
	}

	handleTranscriptRichTextSelection() {
		const container = this.transcriptSelectionRichTextRef.current;
		if (!container || typeof window === "undefined" || typeof window.getSelection !== "function") {
			return;
		}

		const selection = window.getSelection();
		const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
		const isInsideContainer = !!(range && container.contains(range.commonAncestorContainer));
		const selectedTranscriptRangeText = isInsideContainer && selection
			? normalizeTranscriptSuggestion(selection.toString())
			: "";
		const selectedTranscriptRangeStart = isInsideContainer && range
			? (() => {
				const preSelectionRange = range.cloneRange();
				preSelectionRange.selectNodeContents(container);
				preSelectionRange.setEnd(range.startContainer, range.startOffset);
				return preSelectionRange.toString().length;
			})()
			: null;
		const selectedTranscriptRangeEnd = isInsideContainer && range
			? (() => {
				const preSelectionRange = range.cloneRange();
				preSelectionRange.selectNodeContents(container);
				preSelectionRange.setEnd(range.endContainer, range.endOffset);
				return preSelectionRange.toString().length;
			})()
			: null;
		const selectedTranscriptCueIndexes = isInsideContainer && range
			? Array.from(container.querySelectorAll("[data-cue-index]"))
				.map((node) => ({
					node,
					cueIndex: Number(node.getAttribute("data-cue-index"))
				}))
				.filter((entry) => Number.isFinite(entry.cueIndex) && typeof range.intersectsNode === "function" && range.intersectsNode(entry.node))
				.map((entry) => entry.cueIndex)
			: [];

		this.setState({
			selectedTranscriptRangeStart,
			selectedTranscriptRangeEnd,
			selectedTranscriptRangeText,
			selectedTranscriptCueIndexes
		});
	}

	handleClearTranscriptTextSelection() {
		const textarea = this.transcriptSelectionTextareaRef.current;
		if (textarea && typeof textarea.setSelectionRange === "function") {
			const endPosition = Number.isFinite(Number(textarea.selectionEnd)) ? Number(textarea.selectionEnd) : 0;
			textarea.setSelectionRange(endPosition, endPosition);
		}
		if (typeof window !== "undefined" && typeof window.getSelection === "function") {
			const selection = window.getSelection();
			if (selection && typeof selection.removeAllRanges === "function") {
				selection.removeAllRanges();
			}
		}
		this.setState({
			selectedTranscriptRangeStart: null,
			selectedTranscriptRangeEnd: null,
			selectedTranscriptRangeText: "",
			selectedTranscriptCueIndexes: [],
			stepCardNotice: ""
		});
	}

	handleAddTextStepCardFromSelection() {
		const selectedTranscriptRangeText = normalizeTranscriptSuggestion(this.state.selectedTranscriptRangeText);
		const selectedTranscriptRangeStart = Number.isFinite(Number(this.state.selectedTranscriptRangeStart)) ? Number(this.state.selectedTranscriptRangeStart) : null;
		const selectedTranscriptRangeEnd = Number.isFinite(Number(this.state.selectedTranscriptRangeEnd)) ? Number(this.state.selectedTranscriptRangeEnd) : null;
		const selectedTranscriptCueIndexes = Array.isArray(this.state.selectedTranscriptCueIndexes)
			? this.state.selectedTranscriptCueIndexes.filter((cueIndex) => Number.isFinite(Number(cueIndex))).map((cueIndex) => Number(cueIndex))
			: [];
		if (!selectedTranscriptRangeText) {
			return;
		}
		const hasConcreteOffsets = selectedTranscriptRangeStart !== null && selectedTranscriptRangeEnd !== null && selectedTranscriptRangeEnd > selectedTranscriptRangeStart;

		this.setState((prevState) => {
			const customTextStepCards = Array.isArray(prevState.customTextStepCards) ? [...prevState.customTextStepCards] : [];
			const duplicateCard = customTextStepCards.find((card) => (
				card &&
				card.text === selectedTranscriptRangeText &&
				(hasConcreteOffsets
					? (card.startOffset === selectedTranscriptRangeStart && card.endOffset === selectedTranscriptRangeEnd)
					: (
						Array.isArray(card.cueIndexes) &&
						card.cueIndexes.length === selectedTranscriptCueIndexes.length &&
						card.cueIndexes.every((cueIndex, cueIndexPosition) => cueIndex === selectedTranscriptCueIndexes[cueIndexPosition])
					))
			));
			if (duplicateCard) {
				return {
					stepCardNotice: "That selected text is already a step card."
				};
			}

			return {
				customTextStepCards: [
					...customTextStepCards,
					{
						id: `step-card-${Date.now()}-${Math.round(Math.random() * 100000)}`,
						startOffset: hasConcreteOffsets ? selectedTranscriptRangeStart : null,
						endOffset: hasConcreteOffsets ? selectedTranscriptRangeEnd : null,
						text: selectedTranscriptRangeText,
						cueIndexes: selectedTranscriptCueIndexes,
						colorIndex: customTextStepCards.length
					}
				],
				stepCardNotice: "Added a step card from the selected text.",
				selectedTranscriptRangeStart: null,
				selectedTranscriptRangeEnd: null,
				selectedTranscriptRangeText: "",
				selectedTranscriptCueIndexes: []
			};
		}, () => {
			const textarea = this.transcriptSelectionTextareaRef.current;
			if (textarea && typeof textarea.setSelectionRange === "function") {
				textarea.setSelectionRange(selectedTranscriptRangeEnd, selectedTranscriptRangeEnd);
				textarea.focus();
			}
			if (typeof window !== "undefined" && typeof window.getSelection === "function") {
				const selection = window.getSelection();
				if (selection && typeof selection.removeAllRanges === "function") {
					selection.removeAllRanges();
				}
			}
		});
	}

	handleRemoveTextStepCard(cardId = "") {
		const normalizedCardId = `${cardId || ""}`.trim();
		if (!normalizedCardId) {
			return;
		}
		this.setState((prevState) => ({
			customTextStepCards: (Array.isArray(prevState.customTextStepCards) ? prevState.customTextStepCards : []).filter((card) => card && card.id !== normalizedCardId),
			stepCardNotice: "Removed step card."
		}));
	}

	handleApplyTextStepCardToActiveLabel(cardId = "") {
		const normalizedCardId = `${cardId || ""}`.trim();
		if (!normalizedCardId) {
			return;
		}
		const stepCard = this.getCustomTextStepCards().find((card) => card && card.id === normalizedCardId);
		if (!stepCard || !stepCard.text) {
			return;
		}
		this.handleApplyTranscriptSuggestion(this.state.activeOutputIndex, stepCard.text);
	}

	handleApplyAllTextStepCardsToLabels() {
		const customTextStepCards = this.getCustomTextStepCards();
		if (!customTextStepCards.length) {
			return;
		}
		this.setState((prevState) => {
			const segmentOutlineLabels = [...prevState.segmentOutlineLabels];
			customTextStepCards.forEach((card, index) => {
				if (card && card.text && segmentOutlineLabels[index] !== undefined) {
					segmentOutlineLabels[index] = card.text;
				}
			});
			return {
				segmentOutlineLabels
			};
		});
	}

	handleCopyTextStepCard(cardId = "") {
		const normalizedCardId = `${cardId || ""}`.trim();
		if (!normalizedCardId) {
			return;
		}
		const stepCard = this.getCustomTextStepCards().find((card) => card && card.id === normalizedCardId);
		if (!stepCard || !stepCard.text) {
			return;
		}
		this.handleCopyTranscriptText(stepCard.text);
	}

	handleToggleSentenceSelection(index = 0) {
		const normalizedIndex = Math.max(0, Number(index) || 0);
		this.setState((prevState) => {
			const selectedSentenceIndexes = Array.isArray(prevState.selectedSentenceIndexes) ? [...prevState.selectedSentenceIndexes] : [];
			const existingIndex = selectedSentenceIndexes.indexOf(normalizedIndex);
			if (existingIndex >= 0) {
				selectedSentenceIndexes.splice(existingIndex, 1);
			} else {
				selectedSentenceIndexes.push(normalizedIndex);
				selectedSentenceIndexes.sort((left, right) => left - right);
			}
			return {
				selectedSentenceIndexes
			};
		});
	}

	handleClearSentenceSelection() {
		this.setState({
			selectedSentenceIndexes: [],
			sentenceGroupingNotice: ""
		});
	}

	getSelectedSentenceText(sentences = this.getTranscriptSentences()) {
		const selectedIndexes = Array.isArray(this.state.selectedSentenceIndexes) ? this.state.selectedSentenceIndexes : [];
		if (!selectedIndexes.length) {
			return "";
		}
		return normalizeTranscriptSuggestion(selectedIndexes.map((index) => sentences[index] && sentences[index].text ? sentences[index].text : "").filter(Boolean).join(" "));
	}

	handleApplySelectedSentencesToActiveLabel() {
		const combinedText = this.getSelectedSentenceText();
		if (!combinedText) {
			return;
		}
		this.handleApplyTranscriptSuggestion(this.state.activeOutputIndex, combinedText);
	}

	handleAddSentenceGroup() {
		const transcriptSentences = this.getTranscriptSentences();
		this.setState((prevState) => {
			const selectedSentenceIndexes = Array.isArray(prevState.selectedSentenceIndexes) ? [...prevState.selectedSentenceIndexes].sort((left, right) => left - right) : [];
			if (!selectedSentenceIndexes.length) {
				return null;
			}

			const existingAssignments = this.getSentenceGroupAssignments(prevState.customSentenceGroups);
			const availableSentenceIndexes = selectedSentenceIndexes.filter((sentenceIndex) => !existingAssignments[sentenceIndex] && transcriptSentences[sentenceIndex]);
			if (!availableSentenceIndexes.length) {
				return {
					sentenceGroupingNotice: "All selected sentences are already part of a sentence card.",
					selectedSentenceIndexes: []
				};
			}

			const customSentenceGroups = Array.isArray(prevState.customSentenceGroups) ? [...prevState.customSentenceGroups] : [];
			customSentenceGroups.push({
				id: `custom-sentence-group-${Date.now()}-${Math.round(Math.random() * 100000)}`,
				sentenceIndexes: availableSentenceIndexes,
				colorIndex: customSentenceGroups.length
			});

			const skippedCount = selectedSentenceIndexes.length - availableSentenceIndexes.length;
			return {
				customSentenceGroups,
				selectedSentenceIndexes: [],
				sentenceGroupingNotice: skippedCount > 0
					? `Added a sentence card. ${skippedCount} sentence${skippedCount === 1 ? "" : "s"} already belonged to another card and were skipped.`
					: "Added a sentence card from the selected sentences."
			};
		});
	}

	handleRemoveSentenceGroup(groupId = "") {
		const normalizedGroupId = `${groupId || ""}`.trim();
		if (!normalizedGroupId) {
			return;
		}
		this.setState((prevState) => ({
			customSentenceGroups: (Array.isArray(prevState.customSentenceGroups) ? prevState.customSentenceGroups : []).filter((group) => group && group.id !== normalizedGroupId),
			sentenceGroupingNotice: "Removed sentence card.",
			selectedSentenceIndexes: []
		}));
	}

	handleApplyCustomSentenceGroupToActiveLabel(groupId = "") {
		const normalizedGroupId = `${groupId || ""}`.trim();
		if (!normalizedGroupId) {
			return;
		}
		const group = this.getCustomSentenceGroups().find((entry) => entry && entry.id === normalizedGroupId);
		const groupText = this.getCustomSentenceGroupText(group);
		if (!groupText) {
			return;
		}
		this.handleApplyTranscriptSuggestion(this.state.activeOutputIndex, groupText);
	}

	handleCopyCustomSentenceGroup(groupId = "") {
		const normalizedGroupId = `${groupId || ""}`.trim();
		if (!normalizedGroupId) {
			return;
		}
		const group = this.getCustomSentenceGroups().find((entry) => entry && entry.id === normalizedGroupId);
		const groupText = this.getCustomSentenceGroupText(group);
		if (!groupText) {
			return;
		}
		this.handleCopyTranscriptText(groupText);
	}

	async runAiPrompt(promptText = "") {
		if (!isAiCapabilityEnabled(env.appCapabilities)) {
			throw new Error("AI is not available in this environment.");
		}
		const requestBody = {
			model: "gpt-5.4",
			input: [
				{
					role: "user",
					content: [{ type: "input_text", text: promptText }]
				}
			]
		};
		const payload = await getResponsesCreateLambda(JSON.stringify(requestBody), { showAlert: false });
		return extractResponsesText(payload);
	}

	async handleAiCleanTranscript() {
		const transcriptSourceText = this.getCurrentTranscriptSourceText();
		if (!transcriptSourceText) {
			this.setState({
				transcriptError: "Generate or load transcript text before using AI cleanup."
			});
			return;
		}

		this.setState({
			transcriptGeneratingField: "ai-clean",
			transcriptError: "",
			copiedTranscriptState: ""
		});
		try {
			const responseText = await this.runAiPrompt([
				"You are cleaning a source transcript for human editing.",
				"Remove filler words like um, uh, ah, you know, false starts, repeated fragments, and obvious disfluencies.",
				"Preserve the original meaning and ordering.",
				"Return only the cleaned transcript text.",
				"",
				transcriptSourceText
			].join("\n"));
			const cleanedTranscript = cleanTranscriptText(responseText);
			if (!cleanedTranscript) {
				throw new Error("AI cleanup returned empty transcript text.");
			}
			this.setState({
				transcriptText: cleanedTranscript,
				transcriptViewMode: TRANSCRIPT_VIEW_TEXT,
				selectedSentenceIndexes: [],
				customSentenceGroups: [],
				sentenceGroupingNotice: "",
				customTextStepCards: [],
				selectedTranscriptRangeStart: null,
				selectedTranscriptRangeEnd: null,
				selectedTranscriptRangeText: "",
				stepCardNotice: ""
			});
		} catch (error) {
			this.setState({
				transcriptError: error && error.message ? error.message : `${error}`
			});
		} finally {
			this.setState({
				transcriptGeneratingField: ""
			});
		}
	}

	async handleAiConciseTranscript() {
		const transcriptSourceText = this.getCurrentTranscriptSourceText();
		if (!transcriptSourceText) {
			this.setState({
				transcriptError: "Generate or load transcript text before asking AI to make it more concise."
			});
			return;
		}

		this.setState({
			transcriptGeneratingField: "ai-concise",
			transcriptError: "",
			copiedTranscriptState: ""
		});
		try {
			const responseText = await this.runAiPrompt([
				"You are revising a source transcript for a human author.",
				"Make the text more concise and readable while preserving the meaning and the order of the spoken content.",
				"Keep it as plain text, not bullets.",
				"Return only the revised transcript text.",
				"",
				transcriptSourceText
			].join("\n"));
			const conciseTranscript = cleanTranscriptText(responseText);
			if (!conciseTranscript) {
				throw new Error("AI concise rewrite returned empty transcript text.");
			}
			this.setState({
				transcriptText: conciseTranscript,
				transcriptViewMode: TRANSCRIPT_VIEW_TEXT,
				selectedSentenceIndexes: [],
				customSentenceGroups: [],
				sentenceGroupingNotice: "",
				customTextStepCards: [],
				selectedTranscriptRangeStart: null,
				selectedTranscriptRangeEnd: null,
				selectedTranscriptRangeText: "",
				stepCardNotice: ""
			});
		} catch (error) {
			this.setState({
				transcriptError: error && error.message ? error.message : `${error}`
			});
		} finally {
			this.setState({
				transcriptGeneratingField: ""
			});
		}
	}

	async handleAiSuggestLabels() {
		const segments = this.getSegments();
		const transcriptSourceText = this.getCurrentTranscriptSourceText();
		const transcriptVtt = cleanTranscriptVtt(this.state.transcriptVtt || "");
		if (!segments.length) {
			this.setState({
				transcriptError: `Add at least one ${this.state.generationMode === GENERATION_MODE_KEYFRAMES ? "keyframe" : "chunk point"} before asking AI for labels.`
			});
			return;
		}
		if (!transcriptSourceText && !transcriptVtt) {
			this.setState({
				transcriptError: "Generate transcript text or VTT before asking AI for label suggestions."
			});
			return;
		}

		const segmentSummary = segments.map((segment, index) => {
			if (this.state.generationMode === GENERATION_MODE_KEYFRAMES) {
				return `Keyframe ${index + 1}: captured at ${formatSeconds(segment.frameSeconds)}`;
			}
			return `Segment ${index + 1}: ${formatSeconds(segment.startSeconds)} to ${formatSeconds(segment.endSeconds)}`;
		}).join("\n");

		this.setState({
			transcriptGeneratingField: "ai-labels",
			transcriptError: "",
			copiedTranscriptState: ""
		});
		try {
			const responseText = await this.runAiPrompt([
				"You are helping an author create short step labels from transcript content.",
				"Return only a JSON array of strings.",
				`Return exactly ${segments.length} labels, in order.`,
				"Each label should be concise, action-oriented when possible, and suitable for a checklist or activity step.",
				"Do not include numbering.",
				"",
				`Generation mode: ${this.state.generationMode === GENERATION_MODE_KEYFRAMES ? "keyframes" : "video chunks"}`,
				"Current output structure:",
				segmentSummary,
				"",
				transcriptVtt ? `Timed transcript (VTT):\n${transcriptVtt}\n` : "",
				transcriptSourceText ? `Transcript text:\n${transcriptSourceText}` : ""
			].filter(Boolean).join("\n"));

			const suggestedLabels = parseAiSuggestedLabels(responseText, segments.length);
			if (!suggestedLabels.length) {
				throw new Error("AI did not return any usable label suggestions.");
			}

			this.setState((prevState) => {
				const segmentOutlineLabels = [...prevState.segmentOutlineLabels];
				suggestedLabels.forEach((label, index) => {
					if (label) {
						segmentOutlineLabels[index] = label;
					}
				});
				return {
					segmentOutlineLabels
				};
			});
		} catch (error) {
			this.setState({
				transcriptError: error && error.message ? error.message : `${error}`
			});
		} finally {
			this.setState({
				transcriptGeneratingField: ""
			});
		}
	}

	async requestKeyframePreview(index = 0, frameSeconds = 0) {
		const chunkSource = this.getActiveChunkSource();
		const baseProcessingOptions = this.getActiveBaseProcessingOptions();
		if (!chunkSource || !chunkSource.bucket || !chunkSource.key) {
			return;
		}

		this.setState((prevState) => {
			const keyframePreviewLoading = [...(prevState.keyframePreviewLoading || [])];
			keyframePreviewLoading[index] = true;
			return {
				keyframePreviewLoading
			};
		});

		try {
			await refreshCredentials();
			const lambda = new AWS.Lambda({
				region: (env && env.aws && env.aws.defaultRegion) || "us-east-1",
				apiVersion: "2015-03-31"
			});
			const response = await lambda.invoke({
				FunctionName: this.getMediaConverterFunctionName(),
				InvocationType: "RequestResponse",
				LogType: "None",
				Payload: JSON.stringify({
					eventName: "renderVideoPreviewFrame",
					params: {
						bucket: chunkSource.bucket,
						key: chunkSource.key,
						processingOptions: baseProcessingOptions,
						frameSeconds
					}
				})
			}).promise();

			if (response && response.FunctionError) {
				throw new Error("Keyframe preview failed in the media converter Lambda.");
			}

			const payload = parseLambdaPayload(response && response.Payload ? response.Payload : null);
			if (!payload || payload.ok !== true || !payload.dataBase64) {
				throw new Error("Keyframe preview did not return image data.");
			}

			const dataUrl = `data:${payload.contentType || "image/jpeg"};base64,${payload.dataBase64}`;
			this.setState((prevState) => {
				const keyframePreviewImages = [...(prevState.keyframePreviewImages || [])];
				const keyframePreviewLoading = [...(prevState.keyframePreviewLoading || [])];
				keyframePreviewImages[index] = dataUrl;
				keyframePreviewLoading[index] = false;
				return {
					keyframePreviewImages,
					keyframePreviewLoading
				};
			});
		} catch (error) {
			console.error("Unable to render keyframe preview", error);
			this.setState((prevState) => {
				const keyframePreviewLoading = [...(prevState.keyframePreviewLoading || [])];
				keyframePreviewLoading[index] = false;
				return {
					keyframePreviewLoading,
					transcriptError: prevState.transcriptError || (error && error.message ? error.message : "Unable to render keyframe preview.")
				};
			});
		}
	}

	invalidateChunkPreviewRequests() {
		this.chunkPreviewRequestToken = buildChunkJobId();
	}

	invalidateTimedTranscriptRequests() {
		this.timedTranscriptLoadToken = buildChunkJobId();
	}

	buildChunkPreviewProcessingOptions(segment = null) {
		const baseProcessingOptions = _.cloneDeep(this.getActiveBaseProcessingOptions() || {}) || {};
		const baseTrimStartSeconds = Number.isFinite(Number(baseProcessingOptions.trimStartSeconds))
			? Number(baseProcessingOptions.trimStartSeconds)
			: 0;
		const segmentStartSeconds = Number.isFinite(Number(segment && segment.startSeconds))
			? Number(segment.startSeconds)
			: 0;
		const segmentDurationSeconds = Number.isFinite(Number(segment && segment.durationSeconds))
			? Number(segment.durationSeconds)
			: 0;

		return Object.assign({}, baseProcessingOptions, {
			trimStartSeconds: roundSeconds(baseTrimStartSeconds + segmentStartSeconds),
			trimDurationSeconds: roundSeconds(segmentDurationSeconds),
			createThumbnailArtifact: false
		});
	}

	refreshChunkPreviews(segments = this.getSegments()) {
		return segments;
	}

	async requestChunkPreview(index = 0, segment = null, requestToken = this.chunkPreviewRequestToken) {
		return { index, segment, requestToken };
	}

	getChunkPreviewRange(segment = null) {
		const startSeconds = Number.isFinite(Number(segment && segment.startSeconds))
			? Math.max(0, Number(segment.startSeconds))
			: 0;
		const endSeconds = Number.isFinite(Number(segment && segment.endSeconds))
			? Math.max(startSeconds, Number(segment.endSeconds))
			: startSeconds;
		return {
			startSeconds,
			endSeconds
		};
	}

	handleChunkPreviewLoadedMetadata(event, segment = null) {
		const video = event && event.target ? event.target : null;
		if (!video) {
			return;
		}
		const { startSeconds, endSeconds } = this.getChunkPreviewRange(segment);
		if (!(endSeconds > startSeconds)) {
			return;
		}
		try {
			if (video.currentTime < startSeconds || video.currentTime > endSeconds) {
				video.currentTime = startSeconds;
			}
		} catch (error) {
			console.warn("Unable to position chunk preview video", error);
		}
	}

	handleChunkPreviewPlay(event, segment = null) {
		const video = event && event.target ? event.target : null;
		if (!video) {
			return;
		}
		const { startSeconds, endSeconds } = this.getChunkPreviewRange(segment);
		if (!(endSeconds > startSeconds)) {
			return;
		}
		if (video.currentTime < startSeconds || video.currentTime >= (endSeconds - 0.05)) {
			try {
				video.currentTime = startSeconds;
			} catch (error) {
				console.warn("Unable to reset chunk preview video", error);
			}
		}
	}

	handleChunkPreviewSeeking(event, segment = null) {
		const video = event && event.target ? event.target : null;
		if (!video) {
			return;
		}
		const { startSeconds, endSeconds } = this.getChunkPreviewRange(segment);
		if (!(endSeconds > startSeconds)) {
			return;
		}
		const clampedTime = Math.min(Math.max(video.currentTime || 0, startSeconds), endSeconds);
		if (Math.abs(clampedTime - (video.currentTime || 0)) > 0.05) {
			try {
				video.currentTime = clampedTime;
			} catch (error) {
				console.warn("Unable to clamp chunk preview seek", error);
			}
		}
	}

	handleChunkPreviewTimeUpdate(event, segment = null) {
		const video = event && event.target ? event.target : null;
		if (!video) {
			return;
		}
		const { startSeconds, endSeconds } = this.getChunkPreviewRange(segment);
		if (!(endSeconds > startSeconds)) {
			return;
		}
		if (video.currentTime < startSeconds) {
			try {
				video.currentTime = startSeconds;
			} catch (error) {
				console.warn("Unable to correct chunk preview start", error);
			}
			return;
		}
		if (video.currentTime >= Math.max(startSeconds, endSeconds - 0.05)) {
			video.pause();
			if (video.currentTime > endSeconds + 0.02) {
				try {
					video.currentTime = endSeconds;
				} catch (error) {
					console.warn("Unable to clamp chunk preview end", error);
				}
			}
		}
	}

	renderSmartScrubber(segments = this.getSegments(), activeOutputIndex = 0) {
		const duration = Number(this.state.duration) || 0;
		if (!(duration > 0)) {
			return null;
		}

		const isKeyframeMode = this.state.generationMode === GENERATION_MODE_KEYFRAMES;
		const captureActionTooltipText = this.getCaptureActionTooltipText(isKeyframeMode);
		const normalizedPoints = normalizeOutputPoints(this.state.chunkPoints, duration, this.state.generationMode);
		const currentPercent = this.getSmartScrubberPercent(this.state.currentTime, duration);
		const resolvedActiveOutputIndex = Math.max(0, Number(activeOutputIndex) || 0);
		const baseTrackStyle = {
			position: "absolute",
			left: "12px",
			right: "12px",
			top: "50%",
			height: "8px",
			transform: "translateY(-50%)",
			borderRadius: "999px",
			background: "#dbe5f0"
		};

			return (
				<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", background: "#ffffff", padding: "12px", display: "flex", flexDirection: "column", gap: "8px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
							<div style={{ fontWeight: 600, fontSize: "13px", color: "#111827" }}>Scrub position</div>
							<div style={{ fontSize: "14px", color: "#374151", background: "#f3f4f6", borderRadius: "8px", padding: "8px 10px" }}>
								{formatSeconds(this.state.currentTime)}
							</div>
							<div style={{ fontSize: "13px", color: "#6b7280", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "8px 10px", whiteSpace: "nowrap" }}>
								{formatSeconds(duration)}
							</div>
						</div>
						<div style={{ fontWeight: 600, fontSize: "13px", color: "#111827" }}>Smart Scrubber</div>
					</div>
				<div style={{ display: "grid", gridTemplateColumns: (typeof window !== "undefined" && window.innerWidth < 1180) ? "minmax(0, 1fr)" : "minmax(0, 1fr) auto", gap: "10px", alignItems: "stretch" }}>
					<div
						ref={this.smartScrubberTrackRef}
						onPointerDown={this.handleSmartScrubberTrackPointerDown}
						style={{
							position: "relative",
							height: "62px",
							borderRadius: "12px",
							border: "1px solid #dbe5f0",
							background: "linear-gradient(180deg, #f8fbff 0%, #eef4fa 100%)",
							cursor: "pointer",
							overflow: "hidden",
							touchAction: "none",
							userSelect: "none"
						}}
					>
						<div style={baseTrackStyle} />
						<div
							style={{
								position: "absolute",
								left: "12px",
								top: "50%",
								height: "8px",
								transform: "translateY(-50%)",
								borderRadius: "999px",
								background: "linear-gradient(90deg, rgba(59, 130, 246, 0.32) 0%, rgba(59, 130, 246, 0.18) 100%)",
								width: `calc((100% - 24px) * ${Math.min(100, Math.max(0, currentPercent)) / 100})`
							}}
						/>
						{!isKeyframeMode && (Array.isArray(segments) ? segments : []).map((segment, index) => {
							if (!segment) {
								return null;
							}
							const leftPercent = this.getSmartScrubberPercent(segment.startSeconds, duration);
							const rightPercent = this.getSmartScrubberPercent(segment.endSeconds, duration);
							const widthPercent = Math.max(0.4, rightPercent - leftPercent);
							const isActive = index === resolvedActiveOutputIndex;
							return (
								<div
									key={`smart-segment-band-${index}`}
									title={`Segment ${index + 1}: ${formatSeconds(segment.startSeconds)} - ${formatSeconds(segment.endSeconds)}`}
									style={{
										position: "absolute",
										left: `calc(12px + (100% - 24px) * ${leftPercent / 100})`,
										top: "16px",
										height: "30px",
										width: `calc((100% - 24px) * ${widthPercent / 100})`,
										minWidth: "2px",
										borderRadius: "8px",
										border: isActive ? "1px solid #2563eb" : "1px solid rgba(148, 163, 184, 0.38)",
										background: isActive
											? "linear-gradient(180deg, rgba(96, 165, 250, 0.30) 0%, rgba(37, 99, 235, 0.18) 100%)"
											: (index % 2 === 0 ? "rgba(148, 163, 184, 0.16)" : "rgba(191, 219, 254, 0.24)"),
										boxShadow: isActive ? "0 0 0 1px rgba(37, 99, 235, 0.08)" : "none",
										pointerEvents: "none"
									}}
								/>
							);
						})}
						{normalizedPoints.map((point, index) => {
							const markerPercent = this.getSmartScrubberPercent(point, duration);
							const isActive = isKeyframeMode
								? resolvedActiveOutputIndex === index
								: (resolvedActiveOutputIndex === index || resolvedActiveOutputIndex === (index + 1));
							return (
								<button
									key={`smart-marker-${index}`}
									type="button"
									title={isKeyframeMode ? `Keyframe ${index + 1}: ${formatSeconds(point)}` : `Split ${index + 1}: ${formatSeconds(point)}`}
									onPointerDown={(event) => this.handleSmartScrubberMarkerPointerDown(index, event)}
									style={{
										position: "absolute",
										left: `calc(12px + (100% - 24px) * ${markerPercent / 100})`,
										top: "50%",
										transform: "translate(-50%, -50%)",
										width: isKeyframeMode ? "18px" : "20px",
										height: "42px",
										border: "none",
										background: "transparent",
										padding: 0,
										cursor: "ew-resize",
										touchAction: "none"
									}}
								>
									<div
										style={{
											position: "absolute",
											left: "50%",
											top: "6px",
											bottom: "6px",
											width: isKeyframeMode ? "3px" : "4px",
											transform: "translateX(-50%)",
											borderRadius: "999px",
											background: isActive ? "#2563eb" : (isKeyframeMode ? "#f59e0b" : "#0f172a"),
											boxShadow: isActive ? "0 0 0 3px rgba(37, 99, 235, 0.16)" : "none"
										}}
									/>
									<div
										style={{
											position: "absolute",
											left: "50%",
											top: isKeyframeMode ? "5px" : "3px",
											width: isKeyframeMode ? "14px" : "12px",
											height: isKeyframeMode ? "14px" : "12px",
											transform: "translateX(-50%)",
											borderRadius: "999px",
											border: isKeyframeMode ? "2px solid #ffffff" : "1px solid #ffffff",
											background: isActive ? "#2563eb" : (isKeyframeMode ? "#f59e0b" : "#1e293b"),
											boxShadow: "0 2px 6px rgba(15, 23, 42, 0.18)"
										}}
									/>
								</button>
							);
						})}
						<div
							style={{
								position: "absolute",
								left: `calc(12px + (100% - 24px) * ${currentPercent / 100})`,
								top: "10px",
								bottom: "10px",
								width: "2px",
								transform: "translateX(-50%)",
								background: "#0f172a",
								boxShadow: "0 0 0 1px rgba(255, 255, 255, 0.8)",
								pointerEvents: "none"
							}}
						/>
						<div
							style={{
								position: "absolute",
								left: `calc(12px + (100% - 24px) * ${currentPercent / 100})`,
								top: "50%",
								width: "16px",
								height: "16px",
								transform: "translate(-50%, -50%)",
								borderRadius: "999px",
								border: "2px solid #ffffff",
								background: "#111827",
								boxShadow: "0 4px 12px rgba(15, 23, 42, 0.24)",
								pointerEvents: "none"
							}}
						/>
					</div>
					<div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-end" }}>
						<Button bsStyle="default" onClick={this.handleAddChunkPoint} disabled={!this.state.videoReady || this.state.processing}>
							{isKeyframeMode ? "Capture Keyframe" : "Add Chunk Point"}
						</Button>
						{this.renderInfoTooltip(captureActionTooltipText)}
					</div>
				</div>
				<div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
					<div>
						{isKeyframeMode
							? "Click anywhere to scrub. Drag keyframe markers to retime captured frames."
							: "Click anywhere to scrub. Drag split markers to retime segment boundaries and update the output cards instantly."}
					</div>
					{!isKeyframeMode && Array.isArray(segments) && segments[resolvedActiveOutputIndex] &&
						<div style={{ color: "#1d4ed8", fontWeight: 600 }}>
							Segment {resolvedActiveOutputIndex + 1}: {formatSeconds(segments[resolvedActiveOutputIndex].startSeconds)} - {formatSeconds(segments[resolvedActiveOutputIndex].endSeconds)}
						</div>
					}
				</div>
			</div>
		);
	}

	renderOutputPreview(index = 0, segment = null, options = {}) {
		const { previewUrl = "" } = this.props;
		const { maxWidth = "260px", marginBottom = "10px" } = options;
		const containerStyle = {
			marginBottom,
			width: "100%",
			maxWidth,
			aspectRatio: "16 / 9",
			borderRadius: "8px",
			overflow: "hidden",
			background: "#e5e7eb",
			border: "1px solid #dbe5f0"
		};
		const placeholderStyle = {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			color: "#6b7280",
			fontSize: "12px"
		};

		if (this.state.generationMode === GENERATION_MODE_KEYFRAMES) {
			const previewImage = (this.state.keyframePreviewImages || [])[index];
			const isLoading = !!((this.state.keyframePreviewLoading || [])[index]);
			return (
				<div style={containerStyle}>
					{previewImage ? (
						<img
							src={previewImage}
							alt={`Keyframe ${index + 1}`}
							style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center center" }}
						/>
					) : isLoading ? (
						<div style={placeholderStyle}>Rendering preview...</div>
					) : (
						<div style={placeholderStyle}>Preview pending</div>
					)}
				</div>
			);
		}

		const durationLabel = Number.isFinite(Number(segment && segment.durationSeconds))
			? formatSeconds(segment.durationSeconds)
			: "";
		return (
			<div style={containerStyle}>
				{previewUrl ? (
					<div style={{ width: "100%", height: "100%", position: "relative", background: "#020617" }}>
						<video
							src={previewUrl}
							preload="metadata"
							controls
							playsInline
							onLoadedMetadata={(event) => this.handleChunkPreviewLoadedMetadata(event, segment)}
							onPlay={(event) => this.handleChunkPreviewPlay(event, segment)}
							onSeeking={(event) => this.handleChunkPreviewSeeking(event, segment)}
							onTimeUpdate={(event) => this.handleChunkPreviewTimeUpdate(event, segment)}
							style={{ width: "100%", height: "100%", objectFit: "contain", display: "block", background: "#020617" }}
						/>
						{durationLabel &&
							<div style={{ position: "absolute", right: "8px", bottom: "8px", padding: "4px 8px", borderRadius: "999px", background: "rgba(15, 23, 42, 0.72)", color: "#f8fafc", fontSize: "11px", fontWeight: 600, lineHeight: 1 }}>
								{durationLabel}
							</div>
						}
					</div>
				) : (
					<div style={placeholderStyle}>Clip preview unavailable</div>
				)}
			</div>
		);
	}

	renderOutlineSectionEditor(index = 0, segment = null, options = {}) {
		const { compact = false } = options;
		const { allowAutoCreateOutline = false, defaultOutlineLabelBase = "" } = this.props;
		if (
			allowAutoCreateOutline !== true ||
			this.state.autoCreateOutline !== true ||
			this.state.useOutlineSections !== true ||
			!segment
		) {
			return null;
		}

		const sectionTitlePlaceholder = buildDefaultOutlineSectionTitle(defaultOutlineLabelBase, Number(segment.outlineSectionIndex) || 0);
		const outputUnitLabel = this.state.generationMode === GENERATION_MODE_KEYFRAMES ? "keyframe" : "segment";
		return (
			<div style={{ marginTop: compact ? "8px" : "10px", padding: compact ? "8px 10px" : "10px 12px", borderRadius: "8px", background: "#f8fbff", border: "1px solid #dbe5f0" }}>
				<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "8px" }}>
					Section {Number(segment.outlineSectionIndex) + 1}
				</div>
				{index > 0 &&
					<label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: segment.outlineSectionStart === true ? "8px" : "0", cursor: this.state.processing === true ? "default" : "pointer" }}>
						<input
							type="checkbox"
							checked={segment.outlineSectionStart === true}
							onChange={(event) => this.handleOutlineSectionStartChange(index, event)}
							disabled={this.state.processing === true}
						/>
						<span>Start a new section here</span>
					</label>
				}
				{segment.outlineSectionStart === true &&
					<>
						<label style={{ fontWeight: 600, marginBottom: "6px", display: "block", fontSize: "12px" }}>Section title</label>
						<input
							className="form-control"
							type="text"
							value={(this.state.segmentSectionTitles || [])[index] || ""}
							onChange={(event) => this.handleOutlineSectionTitleChange(index, event)}
							onFocus={() => this.handleSetActiveOutputIndex(index)}
							disabled={this.state.processing === true}
							placeholder={sectionTitlePlaceholder}
						/>
						<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, marginTop: "6px" }}>
							Generated outline items from this {outputUnitLabel} forward stay in this section until the next section break.
						</div>
					</>
				}
			</div>
		);
	}

	renderChunkMatchedTranscriptCard(index = 0, matchedTranscriptText = "", options = {}) {
		if (this.state.generationMode !== GENERATION_MODE_VIDEO_CHUNKS) {
			return null;
		}

		const transcriptText = normalizeTranscriptSuggestion(matchedTranscriptText);
		if (!transcriptText) {
			return null;
		}

		const { compact = false } = options;
		const canApplyLabel = this.props.allowAutoCreateOutline === true;
		return (
			<div style={{ marginTop: compact ? "8px" : "10px", padding: compact ? "8px 10px" : "10px 12px", borderRadius: "8px", background: "#f8fbff", border: "1px solid #dbe5f0" }}>
				<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px" }}>Matched Transcript Text</div>
				<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5 }}>{transcriptText}</div>
				<div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
					<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(transcriptText)}>
						Copy text
					</Button>
					{canApplyLabel &&
						<Button bsStyle="default" bsSize="small" onClick={() => this.handleApplyTranscriptSuggestion(index, transcriptText)} disabled={this.state.processing === true}>
							Use for Label 1
						</Button>
					}
				</div>
			</div>
		);
	}

	handleApplyTranscriptSuggestion(index, text = "") {
		const suggestion = normalizeTranscriptSuggestion(text);
		if (!suggestion) {
			return;
		}
		this.setState((prevState) => {
			const segmentOutlineLabels = [...prevState.segmentOutlineLabels];
			segmentOutlineLabels[index] = suggestion;
			return {
				segmentOutlineLabels,
				activeOutputIndex: Math.max(0, Number(index) || 0)
			};
		});
	}

	handleApplyAllTranscriptSuggestions() {
		const segments = this.getSegments();
		const suggestions = this.getTranscriptSuggestions(segments);
		if (!suggestions.some(Boolean)) {
			return;
		}
		this.setState((prevState) => {
			const segmentOutlineLabels = [...prevState.segmentOutlineLabels];
			suggestions.forEach((suggestion, index) => {
				if (suggestion) {
					segmentOutlineLabels[index] = suggestion;
				}
			});
			return {
				segmentOutlineLabels
			};
		});
	}

	async handleCopyTranscriptText(text = "") {
		const value = normalizeTranscriptSuggestion(text);
		if (!value) {
			return;
		}
		try {
			await copyTextToClipboard(value);
			this.setState({
				copiedTranscriptState: "Copied transcript text."
			});
		} catch (error) {
			this.setState({
				transcriptError: error && error.message ? error.message : "Unable to copy transcript text."
			});
		}
	}

	async handleGenerateTranscript() {
		if (!isAiCapabilityEnabled(env.appCapabilities)) {
			this.setState({ transcriptError: "Automatic transcript generation is not available in this environment." });
			return;
		}
		const { fileName = "" } = this.props;
		const chunkSource = this.getActiveChunkSource();
		if (!chunkSource || !chunkSource.bucket || !chunkSource.key) {
			this.setState({
				transcriptError: "Missing source video information for transcript generation."
			});
			return;
		}

		this.setState({
			transcriptGeneratingField: "transcript",
			transcriptError: "",
			copiedTranscriptState: ""
		});
		try {
			if (this.state.transcriptProvider === TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE) {
				const awsResponse = await getAwsTranscribeLambda({
					bucket: chunkSource.bucket,
					key: chunkSource.key,
					fileName,
					mediaType: "video",
					response_format: "text"
				}, { showAlert: false });
				const transcriptVtt = cleanTranscriptVtt(extractTranscriptionVtt(awsResponse));
				const transcriptText = cleanTranscriptText(extractTranscriptionText(awsResponse)) || extractTranscriptFromVtt(transcriptVtt);
				if (!transcriptText) {
					throw new Error("Transcript generation returned no text.");
				}
				this.setState({
					transcriptText,
					...(transcriptVtt ? { transcriptVtt } : {}),
					transcriptViewMode: transcriptText ? TRANSCRIPT_VIEW_TEXT : TRANSCRIPT_VIEW_VTT,
					selectedSentenceIndexes: [],
					customSentenceGroups: [],
					sentenceGroupingNotice: "",
					customTextStepCards: [],
					selectedTranscriptRangeStart: null,
					selectedTranscriptRangeEnd: null,
					selectedTranscriptRangeText: "",
					stepCardNotice: ""
				});
				await this.loadTimedTranscriptWords({ awsPayload: awsResponse, force: true });
				return;
			}

			const response = await getAudioTranscriptionsLambda({
				bucket: chunkSource.bucket,
				key: chunkSource.key,
				fileName,
				mediaType: "video",
				prompt: "Transcribe this video into plain text with useful section breaks."
			});
			const transcriptText = cleanTranscriptText(extractTranscriptionText(response));
			if (!transcriptText) {
				throw new Error("Transcript generation returned no text.");
			}
			this.setState({
				transcriptText,
				transcriptViewMode: TRANSCRIPT_VIEW_TEXT,
				selectedSentenceIndexes: [],
				customSentenceGroups: [],
				sentenceGroupingNotice: "",
				customTextStepCards: [],
				selectedTranscriptRangeStart: null,
				selectedTranscriptRangeEnd: null,
				selectedTranscriptRangeText: "",
				stepCardNotice: ""
			});
		} catch (error) {
			this.setState({
				transcriptError: error && error.message ? error.message : `${error}`
			});
		} finally {
			this.setState({
				transcriptGeneratingField: ""
			});
		}
	}

	async handleGenerateTranscriptVtt() {
		if (!isAiCapabilityEnabled(env.appCapabilities)) {
			this.setState({ transcriptError: "Automatic transcript generation is not available in this environment." });
			return;
		}
		const { fileName = "" } = this.props;
		const chunkSource = this.getActiveChunkSource();
		if (!chunkSource || !chunkSource.bucket || !chunkSource.key) {
			this.setState({
				transcriptError: "Missing source video information for timed transcript generation."
			});
			return;
		}

		this.setState({
			transcriptGeneratingField: "transcriptVtt",
			transcriptError: "",
			copiedTranscriptState: ""
		});
		try {
			if (this.state.transcriptProvider === TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE) {
				const awsResponse = await getAwsTranscribeLambda({
					bucket: chunkSource.bucket,
					key: chunkSource.key,
					fileName,
					mediaType: "video",
					response_format: "vtt"
				}, { showAlert: false });
				const transcriptVtt = cleanTranscriptVtt(extractTranscriptionVtt(awsResponse));
				if (!transcriptVtt || !/-->/m.test(transcriptVtt)) {
					throw new Error("Timed transcript generation returned no valid VTT cues.");
				}
				this.setState((prevState) => ({
					transcriptVtt,
					transcriptText: prevState.transcriptText || extractTranscriptFromVtt(transcriptVtt),
					transcriptViewMode: TRANSCRIPT_VIEW_VTT,
					selectedSentenceIndexes: [],
					customSentenceGroups: [],
					sentenceGroupingNotice: "",
					customTextStepCards: [],
					selectedTranscriptRangeStart: null,
					selectedTranscriptRangeEnd: null,
					selectedTranscriptRangeText: "",
					stepCardNotice: ""
				}));
				await this.loadTimedTranscriptWords({ awsPayload: awsResponse, force: true });
				return;
			}

			const response = await getAudioTranscriptionsLambda({
				bucket: chunkSource.bucket,
				key: chunkSource.key,
				fileName,
				mediaType: "video",
				model: "whisper-1",
				response_format: "vtt"
			});
			const transcriptVtt = cleanTranscriptVtt(extractTranscriptionText(response));
			if (!transcriptVtt || !/-->/m.test(transcriptVtt)) {
				throw new Error("Timed transcript generation returned no valid VTT cues.");
			}
			this.setState((prevState) => ({
				transcriptVtt,
				transcriptText: prevState.transcriptText || extractTranscriptFromVtt(transcriptVtt),
				transcriptViewMode: TRANSCRIPT_VIEW_VTT,
				selectedSentenceIndexes: [],
				customSentenceGroups: [],
				sentenceGroupingNotice: "",
				customTextStepCards: [],
				selectedTranscriptRangeStart: null,
				selectedTranscriptRangeEnd: null,
				selectedTranscriptRangeText: "",
				stepCardNotice: ""
			}));
		} catch (error) {
			this.setState({
				transcriptError: error && error.message ? error.message : `${error}`
			});
		} finally {
			this.setState({
				transcriptGeneratingField: ""
			});
		}
	}

	getSegments() {
		const chunkPoints = normalizeOutputPoints(this.state.chunkPoints, this.state.duration, this.state.generationMode);
		const suffixes = buildSegmentSuffixes(chunkPoints, this.state.duration, this.state.segmentSuffixes, this.state.generationMode);
		const outlineLabels = buildSegmentOutlineLabels(chunkPoints, this.state.duration, this.state.segmentOutlineLabels, this.props.defaultOutlineLabelBase, this.state.generationMode);
		const useOutlineSections = this.state.useOutlineSections === true;
		const sectionStarts = buildSegmentSectionStarts(chunkPoints, this.state.duration, this.state.segmentSectionStarts, this.state.generationMode, useOutlineSections);
		const sectionTitles = buildSegmentSectionTitles(chunkPoints, this.state.duration, this.state.segmentSectionTitles, this.state.generationMode);
		const segments = [];
		if (this.state.generationMode === GENERATION_MODE_KEYFRAMES) {
			chunkPoints.forEach((point, index) => {
				segments.push({
					index,
					frameSeconds: point,
					suffix: suffixes[index],
					outlineLabel: `${outlineLabels[index] || ""}`.trim() || buildDefaultOutlineLabel(this.props.defaultOutlineLabelBase, index)
				});
			});
		} else {
			let previousTime = 0;
			chunkPoints.forEach((point, index) => {
				const endSeconds = clampSeconds(point, this.state.duration);
				const durationSeconds = roundSeconds(endSeconds - previousTime);
				if (durationSeconds > 0) {
					segments.push({
						index,
						startSeconds: previousTime,
						endSeconds,
						durationSeconds,
						suffix: suffixes[index],
						outlineLabel: `${outlineLabels[index] || ""}`.trim() || buildDefaultOutlineLabel(this.props.defaultOutlineLabelBase, index)
					});
				}
				previousTime = endSeconds;
			});

			const finalDuration = roundSeconds(this.state.duration - previousTime);
			if (finalDuration > 0) {
				const finalIndex = segments.length;
				segments.push({
					index: finalIndex,
					startSeconds: previousTime,
					endSeconds: this.state.duration,
					durationSeconds: finalDuration,
					suffix: suffixes[finalIndex],
					outlineLabel: `${outlineLabels[finalIndex] || ""}`.trim() || buildDefaultOutlineLabel(this.props.defaultOutlineLabelBase, finalIndex)
				});
			}
		}

		let currentSectionIndex = -1;
		return segments.map((segment, index) => {
			const outlineSectionStart = useOutlineSections === true && (index === 0 || sectionStarts[index] === true);
			if (outlineSectionStart) {
				currentSectionIndex += 1;
			}
			const resolvedSectionIndex = Math.max(currentSectionIndex, 0);
			const rawSectionTitle = `${sectionTitles[index] || ""}`.trim();
			return {
				...segment,
				outlineSectionStart,
				outlineSectionIndex: resolvedSectionIndex,
				outlineSectionTitle: outlineSectionStart
					? (rawSectionTitle || buildDefaultOutlineSectionTitle(this.props.defaultOutlineLabelBase, resolvedSectionIndex))
					: ""
			};
		});
	}

	clearChunkProgressPolling() {
		if (this.chunkProgressPollTimer) {
			clearInterval(this.chunkProgressPollTimer);
			this.chunkProgressPollTimer = null;
		}
		this.chunkProgressPollInFlight = false;
	}

	buildCreateRunConfig(segments = []) {
		const { baseOutputPath = "", sourcePlaybackGuid = "", fileName = "" } = this.props;
		const generationMode = this.state.generationMode;
		const expectedSegments = Array.isArray(segments) ? segments : [];
		const expectedArtifacts = expectedSegments.map((segment, index) => {
			const normalizedSuffix = normalizeChunkSuffix(segment && segment.suffix, index);
			return {
				index: Number(segment && segment.index) || index,
				suffix: normalizedSuffix,
				outputFileName: generationMode === GENERATION_MODE_KEYFRAMES
					? buildKeyframeOutputFileName(fileName || baseOutputPath, normalizedSuffix, index, this.state.keyframeImageFormat)
					: buildChunkOutputFileName(fileName || baseOutputPath, normalizedSuffix, index)
			};
		});
		const expectedSuffixOrder = new Map(expectedArtifacts.map((artifact, index) => [artifact.suffix, index]));

		return {
			runToken: buildChunkJobId(),
			chunkJobId: buildChunkJobId(),
			generationMode,
			expectedCount: expectedArtifacts.length,
			expectedArtifacts,
			expectedSuffixOrder,
			expectedSuffixSet: new Set(expectedArtifacts.map((artifact) => artifact.suffix)),
			derivedArtifact: generationMode === GENERATION_MODE_KEYFRAMES ? "video-keyframe" : "video-chunk",
			baseOutputStem: getPathStem(baseOutputPath || fileName),
			sourcePlaybackGuid: `${sourcePlaybackGuid || ""}`.trim(),
			startedAtMs: Date.now()
		};
	}

	getCreatedResourceSuffix(resource = null, generationMode = GENERATION_MODE_VIDEO_CHUNKS) {
		const metaData = resource && resource.metaData && typeof resource.metaData === "object" ? resource.metaData : {};
		return generationMode === GENERATION_MODE_KEYFRAMES
			? `${metaData.keyframeSuffix || ""}`.trim()
			: `${metaData.chunkSuffix || ""}`.trim();
	}

	getCreatedResourceOrder(resource = null, runConfig = null) {
		const suffix = this.getCreatedResourceSuffix(resource, runConfig && runConfig.generationMode ? runConfig.generationMode : GENERATION_MODE_VIDEO_CHUNKS);
		if (runConfig && runConfig.expectedSuffixOrder && runConfig.expectedSuffixOrder.has(suffix)) {
			return runConfig.expectedSuffixOrder.get(suffix);
		}

		const metaData = resource && resource.metaData && typeof resource.metaData === "object" ? resource.metaData : {};
		const fallbackIndex = runConfig && runConfig.generationMode === GENERATION_MODE_KEYFRAMES
			? Number(metaData.keyframeIndex)
			: Number(metaData.chunkIndex);
		return Number.isFinite(fallbackIndex) ? fallbackIndex : Number.MAX_SAFE_INTEGER;
	}

	sortCreatedResourcesForRun(resources = [], runConfig = null) {
		return [...(Array.isArray(resources) ? resources : [])].sort((a, b) => {
			const orderA = this.getCreatedResourceOrder(a, runConfig);
			const orderB = this.getCreatedResourceOrder(b, runConfig);
			if (orderA !== orderB) {
				return orderA - orderB;
			}
			return `${(a && a.path) || ""}`.localeCompare(`${(b && b.path) || ""}`);
		});
	}

	isCreatedResourceForRun(resource = null, runConfig = null) {
		if (!resource || !runConfig) {
			return false;
		}

		const metaData = resource.metaData && typeof resource.metaData === "object" ? resource.metaData : {};
		if (`${metaData.derivedArtifact || ""}`.trim() !== `${runConfig.derivedArtifact || ""}`.trim()) {
			return false;
		}
		if (`${metaData.sourceVideoDriveObjectGuid || ""}`.trim() !== `${runConfig.sourcePlaybackGuid || ""}`.trim()) {
			return false;
		}

		const resourceJobId = `${metaData.chunkJobId || ""}`.trim();
		if (resourceJobId && resourceJobId === `${runConfig.chunkJobId || ""}`.trim()) {
			return true;
		}

		const suffix = this.getCreatedResourceSuffix(resource, runConfig.generationMode);
		if (!runConfig.expectedSuffixSet || !runConfig.expectedSuffixSet.has(suffix)) {
			return false;
		}

		const fileName = getPathFileName(resource.path || resource.name);
		const expectedPrefix = runConfig.baseOutputStem ? `${runConfig.baseOutputStem}-${suffix}` : "";
		if (expectedPrefix && fileName && !fileName.startsWith(expectedPrefix)) {
			return false;
		}

		const resourceTimestampMs = parseResourceTimestampMs(resource);
		return resourceTimestampMs > 0 && resourceTimestampMs >= (runConfig.startedAtMs - CHUNK_PROGRESS_MATCH_GRACE_MS);
	}

	async getCreatedResourcesForRun(runConfig = null) {
		await listDriveMap();
		const driveResources = Object.values(getDriveObjectsAsMap() || {});
		return this.sortCreatedResourcesForRun(
			driveResources.filter((resource) => this.isCreatedResourceForRun(resource, runConfig)),
			runConfig
		);
	}

	buildProcessingStatusMessage(runConfig = null, createdResources = []) {
		if (!runConfig) {
			return "";
		}

		const completedCount = Array.isArray(createdResources) ? createdResources.length : 0;
		const totalCount = Number(runConfig.expectedCount) || 0;
		const elapsedSeconds = Math.max(0, Math.round((Date.now() - (runConfig.startedAtMs || Date.now())) / 1000));
		const unitLabel = runConfig.generationMode === GENERATION_MODE_KEYFRAMES ? "keyframes" : "chunks";
		const latestResource = completedCount > 0 ? createdResources[completedCount - 1] : null;
		const latestFileName = latestResource ? getPathFileName(latestResource.path || latestResource.name) : "";

		if (completedCount <= 0) {
			return `Starting ${unitLabel}. This can take a few minutes for processed video. Elapsed: ${formatSeconds(elapsedSeconds)}.`;
		}

		return `Created ${completedCount} of ${totalCount} ${unitLabel}${latestFileName ? ` (${latestFileName})` : ""}. Elapsed: ${formatSeconds(elapsedSeconds)}.`;
	}

	startChunkProgressPolling(runConfig = null, segments = []) {
		if (!runConfig) {
			return null;
		}

		this.clearChunkProgressPolling();
		this.activeChunkRunToken = runConfig.runToken;
		this.processingCompletionHandledToken = null;

		const pollProgress = async () => {
			if (!this._isMounted || this.activeChunkRunToken !== runConfig.runToken || this.chunkProgressPollInFlight) {
				return;
			}

			this.chunkProgressPollInFlight = true;
			try {
				const createdResources = await this.getCreatedResourcesForRun(runConfig);
				if (!this._isMounted || this.activeChunkRunToken !== runConfig.runToken) {
					return;
				}

				this.setState({
					processingProgressCount: createdResources.length,
					processingExpectedCount: runConfig.expectedCount,
					processingStatusMessage: this.buildProcessingStatusMessage(runConfig, createdResources)
				});

				if (createdResources.length >= runConfig.expectedCount) {
					await this.completeChunkRun(runConfig.runToken, createdResources, segments);
				}
			} catch (error) {
				console.warn("Unable to refresh chunk creation progress", error);
			} finally {
				this.chunkProgressPollInFlight = false;
			}
		};

		pollProgress();
		this.chunkProgressPollTimer = setInterval(pollProgress, CHUNK_PROGRESS_POLL_MS);
		return runConfig.runToken;
	}

	async completeChunkRun(runToken = "", createdResources = [], segments = []) {
		const { onCreate = null, onClose = null } = this.props;
		if (!runToken || runToken !== this.activeChunkRunToken || this.processingCompletionHandledToken === runToken) {
			return false;
		}

		this.processingCompletionHandledToken = runToken;
		this.clearChunkProgressPolling();

		try {
			if (onCreate) {
				await Promise.resolve(onCreate({
					autoCreateOutline: this.state.autoCreateOutline === true,
					createdResources,
					segments,
					generationMode: this.state.generationMode,
					chunkVideoSourceContext: this.buildChunkVideoSourceContext()
				}));
			}

			this.activeChunkRunToken = null;
			if (onClose) {
				onClose();
			} else if (this._isMounted) {
				this.setState({
					processing: false,
					processingProgressCount: createdResources.length,
					processingExpectedCount: createdResources.length,
					processingStatusMessage: ""
				});
			}
			return true;
		} catch (error) {
			this.processingCompletionHandledToken = null;
			this.activeChunkRunToken = null;
			if (this._isMounted) {
				this.setState({
					processing: false,
					processingStatusMessage: "",
					error: `${error && error.message ? error.message : error}`
				});
			}
			return true;
		}
	}

	async handleCreateChunks() {
		const { baseOutputPath = "", sourcePlaybackGuid = "", sourcePlaybackPath = "", sourceMetaData = {}, orgId = "" } = this.props;
		const chunkSource = this.getActiveChunkSource();
		const baseProcessingOptions = this.getActiveBaseProcessingOptions();
		const segments = this.getSegments();
		const shouldCreateChunkThumbnailArtifacts =
			this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS &&
			this.state.autoCreateOutline === true;
		if (!chunkSource || !chunkSource.bucket || !chunkSource.key || !baseOutputPath || !orgId) {
			this.setState({
				error: "Missing source or output information for chunking this video."
			});
			return;
		}
		if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS && segments.length < 2) {
			this.setState({
				error: "Add at least one chunk point to split this video into multiple step files."
			});
			return;
		}
		if (this.state.generationMode === GENERATION_MODE_KEYFRAMES && segments.length < 1) {
			this.setState({
				error: "Capture at least one keyframe to generate image artifacts."
			});
			return;
		}
		if (this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS && segments.some((segment) => segment.durationSeconds < MIN_SEGMENT_DURATION_SECONDS)) {
			this.setState({
				error: "Each chunk must be at least 0.1 seconds long."
			});
			return;
		}

		const runConfig = this.buildCreateRunConfig(segments);
		this.setState({
			processing: true,
			error: "",
			processingProgressCount: 0,
			processingExpectedCount: runConfig.expectedCount,
			processingStatusMessage: this.buildProcessingStatusMessage(runConfig, [])
		});
		this.startChunkProgressPolling(runConfig, segments);

		try {
			await refreshCredentials();
			const lambda = new AWS.Lambda({
				region: (env && env.aws && env.aws.defaultRegion) || "us-east-1",
				apiVersion: "2015-03-31"
			});
			const response = await lambda.invoke({
				FunctionName: this.getMediaConverterFunctionName(),
				InvocationType: "RequestResponse",
				LogType: "None",
				Payload: JSON.stringify({
					eventName: "chunkVideo",
					params: {
						bucket: chunkSource.bucket,
						key: chunkSource.key,
						orgId,
						baseOutputPath,
						generationMode: this.state.generationMode,
						keyframeImageFormat: this.state.keyframeImageFormat,
						sourcePlaybackGuid,
						sourcePlaybackPath,
						sourceMetaData,
						baseProcessingOptions,
						createThumbnailArtifacts: shouldCreateChunkThumbnailArtifacts,
						chunkJobId: runConfig.chunkJobId,
						chunks: this.state.generationMode === GENERATION_MODE_VIDEO_CHUNKS
							? segments.map((segment) => ({
								index: segment.index,
								suffix: normalizeChunkSuffix(segment.suffix, segment.index),
								startSeconds: segment.startSeconds,
								durationSeconds: segment.durationSeconds
							}))
							: [],
						keyframes: this.state.generationMode === GENERATION_MODE_KEYFRAMES
							? segments.map((segment) => ({
								index: segment.index,
								suffix: normalizeChunkSuffix(segment.suffix, segment.index),
								frameSeconds: segment.frameSeconds
							}))
							: []
					}
				})
			}).promise();

			if (this.processingCompletionHandledToken === runConfig.runToken || this.activeChunkRunToken !== runConfig.runToken) {
				return;
			}

			if (response && response.FunctionError) {
				throw new Error("Chunk Video failed in the media converter Lambda.");
			}

			const payload = parseLambdaPayload(response && response.Payload ? response.Payload : null);
			if (!payload || payload === "null") {
				throw new Error("Chunk Video returned no payload from the media converter Lambda. The deployed Lambda likely does not include the latest chunkVideo route yet.");
			}
			if (!payload || payload.ok !== true || !Array.isArray(payload.createdResources) || payload.createdResources.length === 0) {
				const driveCreatedResources = await this.getCreatedResourcesForRun(runConfig);
				if (driveCreatedResources.length < runConfig.expectedCount) {
					throw new Error(payload && payload.error ? payload.error : "Chunk Video did not return any created Drive artifacts.");
				}
				await this.completeChunkRun(runConfig.runToken, driveCreatedResources, segments);
				return;
			}

			await this.completeChunkRun(
				runConfig.runToken,
				this.sortCreatedResourcesForRun(payload.createdResources, runConfig),
				segments
			);
		} catch (error) {
			console.error("Unable to chunk video", error);
			try {
				const driveCreatedResources = await this.getCreatedResourcesForRun(runConfig);
				if (driveCreatedResources.length >= runConfig.expectedCount) {
					await this.completeChunkRun(runConfig.runToken, driveCreatedResources, segments);
					return;
				}
			} catch (driveError) {
				console.warn("Unable to finalize chunk creation from Drive progress fallback", driveError);
			}
			if (this._isMounted && this.activeChunkRunToken === runConfig.runToken) {
				this.setState({
					error: `${error && error.message ? error.message : error}`
				});
			}
		} finally {
			if (this.activeChunkRunToken === runConfig.runToken && this.processingCompletionHandledToken !== runConfig.runToken) {
				this.activeChunkRunToken = null;
				this.clearChunkProgressPolling();
				if (this._isMounted) {
					this.setState({
						processing: false,
						processingStatusMessage: ""
					});
				}
			}
		}
	}

	render() {
		const { open = false, previewUrl = "", fileName = "", title = "Chunk Video", allowAutoCreateOutline = false, allowOutlineSections = false, defaultOutlineLabelBase = "" } = this.props;
		const {
			currentTime,
			duration,
			segmentSuffixes,
			segmentOutlineLabels,
			useOutlineSections,
			autoCreateOutline,
			processing,
			error,
			videoReady,
			generationMode,
			keyframeImageFormat,
			transcriptText,
			transcriptVtt,
			transcriptProvider,
			transcriptViewMode,
			transcriptGeneratingField,
			transcriptError,
			selectedTranscriptRangeText,
			selectedTranscriptRangeStart,
			selectedTranscriptRangeEnd,
			customTextStepCards,
			stepCardNotice,
			selectedSentenceIndexes,
			customSentenceGroups,
			sentenceGroupingNotice,
				activeOutputIndex,
				copiedTranscriptState,
				layoutMode,
				showTopControlsPanel,
				useMasterSource,
			showSelectedSegmentHighlight,
			showCurrentCueHighlight,
			showCurrentWordHighlight,
			showStepCardHighlight,
			savingDraft,
			processingProgressCount,
			processingExpectedCount,
			processingStatusMessage
		} = this.state;
		const segments = this.getSegments();
		const transcriptCues = this.getTranscriptCues();
		const transcriptSentences = this.getTranscriptSentences();
		const transcriptSuggestions = this.getTranscriptSuggestions(segments, transcriptCues);
		const matchedChunkTranscriptTexts = this.getMatchedChunkTranscriptTexts(segments);
		const resolvedActiveOutputIndex = segments.length > 0 ? Math.min(Math.max(0, Number(activeOutputIndex) || 0), segments.length - 1) : 0;
		const activeSegment = segments[resolvedActiveOutputIndex] || null;
		const currentScrubCue = getCueForTime(transcriptCues, currentTime);
		const transcriptSourceText = this.getCurrentTranscriptSourceText();
		const selectedSentenceText = this.getSelectedSentenceText(transcriptSentences);
		const sentenceGroupAssignments = this.getSentenceGroupAssignments(customSentenceGroups);
		const textStepCardRanges = this.getTextStepCardRanges(customTextStepCards);
		const timedTranscriptDisplayModel = buildTimedTranscriptDisplayModel(transcriptCues, this.state.timedTranscriptWords);
		const timedTranscriptDisplayText = timedTranscriptDisplayModel.displayText || transcriptCues.map((cue) => cue.text).join(" ");
		const timedTranscriptCueRanges = timedTranscriptDisplayModel.cueRanges.length > 0 ? timedTranscriptDisplayModel.cueRanges : buildCueTextRanges(transcriptCues);
		const timedTranscriptWordRanges = timedTranscriptDisplayModel.wordRanges;
		const currentTimedWordRange = getTimedWordRangeForTime(timedTranscriptWordRanges, currentTime);
		const hasTranscriptSuggestions = transcriptSuggestions.some(Boolean);
		const aiEnabled = isAiCapabilityEnabled(env.appCapabilities);
		const busy = processing === true || savingDraft === true;
		const canCreateChunks = videoReady && duration > 0 && (generationMode === GENERATION_MODE_KEYFRAMES ? segments.length > 0 : segments.length > 1) && !busy;
		const isKeyframeMode = generationMode === GENERATION_MODE_KEYFRAMES;
		const activeMatchedChunkTranscriptText = matchedChunkTranscriptTexts[resolvedActiveOutputIndex] || "";
		const activeSegmentTimedWordRanges = (!isKeyframeMode && activeSegment)
			? timedTranscriptWordRanges.filter((wordRange) => isTimedWordRangeRelevantToSegment(wordRange, activeSegment, duration))
			: [];
		const activeSegmentTranscriptText = isKeyframeMode
			? (transcriptSuggestions[resolvedActiveOutputIndex] || "")
			: (activeMatchedChunkTranscriptText || transcriptSuggestions[resolvedActiveOutputIndex] || "");
		const hasMatchedChunkTranscriptView = !isKeyframeMode && segments.length > 0 && (matchedChunkTranscriptTexts.some(Boolean) || transcriptSuggestions.some(Boolean));
		const resolvedLayoutMode = layoutMode === CHUNK_MODAL_LAYOUT_VIDEO_FOCUS || layoutMode === CHUNK_MODAL_LAYOUT_TRANSCRIPT_FOCUS
			? CHUNK_MODAL_LAYOUT_VIDEO_FOCUS
			: (layoutMode === CHUNK_MODAL_LAYOUT_STEPS_FOCUS ? CHUNK_MODAL_LAYOUT_STEPS_FOCUS : CHUNK_MODAL_LAYOUT_DEFAULT);
		const isVideoFocusLayout = resolvedLayoutMode === CHUNK_MODAL_LAYOUT_VIDEO_FOCUS;
		const isStepsFocusLayout = resolvedLayoutMode === CHUNK_MODAL_LAYOUT_STEPS_FOCUS;
		const hasMasterChunkSource = this.hasMasterChunkSource();
		const masterSourceTooltipText = this.getMasterSourceTooltipText();
		const outlineTooltipText = this.getOutlineTooltipText(isKeyframeMode);
		const outlineSectionsTooltipText = this.getOutlineSectionsTooltipText();
		const processingUnitLabel = isKeyframeMode ? "keyframes" : "chunks";
		const createButtonLabel = processing
			? (processingExpectedCount > 0
				? `Creating ${processingUnitLabel}: ${Math.min(processingProgressCount, processingExpectedCount)}/${processingExpectedCount}`
				: (isKeyframeMode ? "Creating Keyframes..." : "Creating Chunks..."))
			: (isKeyframeMode ? "Create Keyframes" : "Create Chunks");
			const createSettingsPanel = (
				<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "12px", background: "#ffffff" }}>
					<div style={{ fontWeight: 600, marginBottom: "10px" }}>Create</div>
					<div style={{ display: "grid", gridTemplateColumns: isKeyframeMode ? "minmax(0, 1fr) 120px" : "minmax(0, 1fr)", gap: "8px", alignItems: "end" }}>
						<div>
							<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Mode</label>
							<select
								className="form-control"
								value={generationMode}
								onChange={this.handleGenerationModeChange}
								disabled={processing}
							>
								<option value={GENERATION_MODE_VIDEO_CHUNKS}>Video Chunks</option>
								<option value={GENERATION_MODE_KEYFRAMES}>Keyframes</option>
							</select>
						</div>
						{isKeyframeMode &&
							<div>
								<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>Format</label>
								<select
									className="form-control"
									value={keyframeImageFormat}
									onChange={this.handleKeyframeImageFormatChange}
									disabled={processing}
								>
									<option value={KEYFRAME_IMAGE_FORMAT_PNG}>PNG</option>
									<option value={KEYFRAME_IMAGE_FORMAT_JPG}>JPG</option>
								</select>
							</div>
						}
					</div>
					{hasMasterChunkSource &&
						<div style={{ marginTop: "10px" }}>
							<label style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginBottom: "0", cursor: processing ? "default" : "pointer" }}>
								<input
									type="checkbox"
									checked={useMasterSource === true}
									onChange={this.handleUseMasterSourceChange}
									disabled={processing}
									style={{ marginTop: "2px" }}
								/>
								<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
									<div style={{ fontWeight: 600 }}>Render from preserved master source</div>
									{this.renderInfoTooltip(masterSourceTooltipText, { marginTop: "-1px" })}
								</span>
							</label>
						</div>
					}
				</div>
			);
			const outlineSettingsPanel = (
				<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "12px", background: "#f8fbff" }}>
					<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
						<div style={{ fontWeight: 600 }}>Outline</div>
						{this.renderInfoTooltip(outlineTooltipText)}
					</div>
					{allowAutoCreateOutline ? (
						<>
							<label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0", cursor: processing ? "default" : "pointer" }}>
								<input
									type="checkbox"
									checked={autoCreateOutline === true}
									onChange={this.handleAutoCreateOutlineChange}
									disabled={processing}
								/>
								<span>Auto-create outline from selected item</span>
							</label>
							{allowOutlineSections === true &&
								<label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", marginBottom: "0", cursor: processing ? "default" : "pointer" }}>
									<input
										type="checkbox"
										checked={useOutlineSections === true}
										onChange={this.handleUseOutlineSectionsChange}
										disabled={processing || autoCreateOutline !== true}
									/>
									<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
										<span>Create sections in the outline</span>
										{this.renderInfoTooltip(outlineSectionsTooltipText, { marginTop: "-1px" })}
									</span>
								</label>
							}
						</>
					) : (
						<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
							Auto-create outline is only available when the selected activity node is an item with this video field.
						</div>
					)}
				</div>
			);
			const topControlsPanel = (
				<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "12px 14px", background: "#f8fbff", display: "flex", flexDirection: "column", gap: "12px", flex: "0 0 auto" }}>
					<div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
						<div>
							<div style={{ fontWeight: 600 }}>Create And Outline</div>
							<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, marginTop: "4px" }}>
								Configure generation mode, master-source behavior, and outline creation without taking over the main working area.
							</div>
						</div>
						<Button bsStyle="default" bsSize="small" onClick={this.handleTopControlsPanelToggle} disabled={processing}>
							{showTopControlsPanel ? "Collapse" : "Expand"}
						</Button>
					</div>
					{showTopControlsPanel &&
						<div style={{ display: "grid", gridTemplateColumns: (typeof window !== "undefined" && window.innerWidth < 1200) ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "12px", alignItems: "stretch" }}>
							{createSettingsPanel}
							{outlineSettingsPanel}
						</div>
					}
				</div>
			);
			const renderVideoPreviewPanel = (options = {}) => {
			const fillHeight = options.fillHeight === true;
			const separateScrubberCard = options.separateScrubberCard === true;
			const videoMaxHeight = options.videoMaxHeight || "min(560px, 46vh)";
			const videoMinHeight = options.videoMinHeight || 0;
			if (!separateScrubberCard) {
				return (
					<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "6px", background: "#ffffff", display: "flex", flexDirection: "column", gap: "4px", minHeight: 0, overflow: "hidden", height: fillHeight ? "100%" : "auto" }}>
						<div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", alignItems: "stretch", justifyContent: "stretch" }}>
							{previewUrl ? (
								<video
									ref={this.videoRef}
									src={previewUrl}
									preload="metadata"
									controls
									playsInline
									style={{ width: "100%", height: "100%", maxHeight: fillHeight ? "none" : videoMaxHeight, minHeight: videoMinHeight, background: "black", borderRadius: "6px", display: "block" }}
									onLoadedMetadata={this.handleLoadedMetadata}
									onTimeUpdate={this.handleTimeUpdate}
									onSeeked={this.handleTimeUpdate}
									onPause={this.handleTimeUpdate}
								/>
							) : (
								<div style={{ color: "#d1d5db", textAlign: "center", padding: "48px 24px" }}>
									Preview unavailable for this video.
								</div>
							)}
						</div>
						{previewUrl && this.renderSmartScrubber(segments, resolvedActiveOutputIndex)}
					</div>
				);
			}
			return (
				<div style={{ display: "flex", flexDirection: "column", gap: "10px", minHeight: 0, height: fillHeight ? "100%" : "auto" }}>
					<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "6px", background: "#ffffff", display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden", flex: fillHeight ? "1 1 auto" : "0 0 auto" }}>
						<div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", alignItems: "stretch", justifyContent: "stretch" }}>
							{previewUrl ? (
								<video
									ref={this.videoRef}
									src={previewUrl}
									preload="metadata"
									controls
									playsInline
									style={{ width: "100%", height: "100%", maxHeight: fillHeight ? "none" : videoMaxHeight, minHeight: videoMinHeight, background: "black", borderRadius: "6px", display: "block" }}
									onLoadedMetadata={this.handleLoadedMetadata}
									onTimeUpdate={this.handleTimeUpdate}
									onSeeked={this.handleTimeUpdate}
									onPause={this.handleTimeUpdate}
								/>
							) : (
								<div style={{ color: "#d1d5db", textAlign: "center", padding: "48px 24px" }}>
									Preview unavailable for this video.
								</div>
							)}
						</div>
					</div>
					{previewUrl && this.renderSmartScrubber(segments, resolvedActiveOutputIndex)}
				</div>
			);
			};
			const defaultVideoPanel = (
				<div style={{ minHeight: 0, overflowY: "auto", paddingRight: "4px" }}>
					{renderVideoPreviewPanel()}
				</div>
			);
			const videoFocusEditorPanel = (
				<div style={{ flex: "1 1 auto", minHeight: 0, height: "100%", display: "flex", flexDirection: "column" }}>
					{renderVideoPreviewPanel({
						fillHeight: true,
						separateScrubberCard: true,
						videoMinHeight: (typeof window !== "undefined" && window.innerWidth < 1300) ? "min(520px, 66vh)" : "min(760px, 82vh)"
					})}
				</div>
			);
		const outputSegmentsPanel = (
			<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "14px", background: "#ffffff" }}>
				<div style={{ fontWeight: 600, marginBottom: "10px" }}>{isKeyframeMode ? "Output Keyframes and Labels" : "Output Segments and Labels"}</div>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					{segments.map((segment, index) => (
						<div
							key={`segment-${index}`}
							style={{
								border: index === resolvedActiveOutputIndex ? "2px solid #7aa7d9" : "1px solid #e5e7eb",
								borderRadius: "8px",
								padding: "12px",
								background: index === resolvedActiveOutputIndex ? "#f8fbff" : "#ffffff",
								cursor: "pointer"
							}}
							onMouseEnter={() => this.handleSetActiveOutputIndex(index)}
							onClick={() => this.handleSetActiveOutputIndex(index)}
						>
							<div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
								<Button
									bsStyle="default"
									bsSize="small"
									onClick={(event) => {
										event.stopPropagation();
										this.handleRemoveOutput(index);
									}}
									disabled={processing || (isKeyframeMode ? segments.length < 1 : segments.length < 2)}
								>
									{this.getRemoveOutputLabel(index, segments.length)}
								</Button>
							</div>
							{this.renderOutputPreview(index, segment, { maxWidth: "260px", marginBottom: "10px" })}
							<div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", marginBottom: "8px" }}>
								<div style={{ fontWeight: 600 }}>{isKeyframeMode ? `Keyframe ${index + 1}` : `Segment ${index + 1}`}</div>
								<div style={{ color: "#6b7280", fontSize: "12px" }}>
									{isKeyframeMode
										? `${formatSeconds(segment.frameSeconds)}`
										: `${formatSeconds(segment.startSeconds)} - ${formatSeconds(segment.endSeconds)} (${formatSeconds(segment.durationSeconds)})`}
								</div>
							</div>
							<label style={{ fontWeight: 600, marginBottom: "6px", display: "block" }}>File suffix</label>
							<input
								className="form-control"
								type="text"
								value={segmentSuffixes[index] || ""}
								onChange={(event) => this.handleSuffixChange(index, event)}
								disabled={processing}
							/>
							<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px", lineHeight: 1.5 }}>
								Output file: {isKeyframeMode
									? buildKeyframeOutputFileName(fileName, segmentSuffixes[index], index, keyframeImageFormat)
									: buildChunkOutputFileName(fileName, segmentSuffixes[index], index)}
							</div>
							{allowAutoCreateOutline && autoCreateOutline === true &&
								<>
									<label style={{ fontWeight: 600, marginTop: "10px", marginBottom: "6px", display: "block" }}>Label 1 content</label>
									<textarea
										className="form-control"
										value={segmentOutlineLabels[index] || ""}
										onChange={(event) => this.handleOutlineLabelChange(index, event)}
										onFocus={() => this.handleSetActiveOutputIndex(index)}
										disabled={processing}
										placeholder={buildDefaultOutlineLabel(defaultOutlineLabelBase, index)}
										rows={2}
										ref={(node) => this.registerOutlineLabelTextarea(index, node)}
										style={{ minHeight: "72px", resize: "vertical", overflow: "hidden" }}
									/>
									<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "6px", lineHeight: 1.5 }}>
										Used for the selected item and cloned siblings when auto-create outline is enabled.
									</div>
								</>
							}
							{this.renderOutlineSectionEditor(index, segment)}
							{this.renderChunkMatchedTranscriptCard(index, matchedChunkTranscriptTexts[index])}
							{(!matchedChunkTranscriptTexts[index] && transcriptSuggestions[index]) &&
								<div style={{ marginTop: "10px", padding: "8px 10px", borderRadius: "8px", background: "#f8fbff", border: "1px solid #dbe5f0" }}>
									<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px" }}>Suggested Label 1 from transcript</div>
									<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5 }}>{transcriptSuggestions[index]}</div>
									<div style={{ display: "flex", gap: "8px", marginTop: "8px", flexWrap: "wrap" }}>
										<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(transcriptSuggestions[index])} disabled={processing}>
											Copy suggestion
										</Button>
										{allowAutoCreateOutline &&
											<Button bsStyle="default" bsSize="small" onClick={() => this.handleApplyTranscriptSuggestion(index, transcriptSuggestions[index])} disabled={processing}>
												Use for Label 1
											</Button>
										}
									</div>
								</div>
							}
						</div>
					))}
				</div>
			</div>
		);
		const buildTranscriptWorkspacePanel = (isStacked = false) => (
			<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "14px", background: "#ffffff", display: "flex", flexDirection: "column", gap: "12px", minWidth: 0, minHeight: 0, overflowY: isStacked ? "visible" : "auto", flex: isStacked ? "0 0 auto" : "1 1 auto" }}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "flex-start", flexWrap: "wrap" }}>
					<div>
						<div style={{ fontWeight: 600 }}>Transcript Workspace</div>
						<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, marginTop: "4px" }}>
							View or generate full-source transcript text and timed subtitle cues, then reuse that text for chunk or keyframe labels before you create anything.
						</div>
					</div>
					{aiEnabled && <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
						<select
							className="form-control"
							value={transcriptProvider}
							onChange={this.handleTranscriptProviderChange}
							disabled={transcriptGeneratingField !== "" || processing}
							style={{ minWidth: "170px" }}
						>
							<option value={TRANSCRIPT_PROVIDER_AWS_TRANSCRIBE}>Amazon Transcribe</option>
							<option value={TRANSCRIPT_PROVIDER_OPENAI}>OpenAI</option>
						</select>
						<Button bsStyle="default" onClick={this.handleGenerateTranscript} disabled={transcriptGeneratingField !== "" || processing}>
							{transcriptGeneratingField === "transcript" ? "Generating..." : "Generate Transcript"}
						</Button>
						<Button bsStyle="default" onClick={this.handleGenerateTranscriptVtt} disabled={transcriptGeneratingField !== "" || processing}>
							{transcriptGeneratingField === "transcriptVtt" ? "Generating..." : "Generate VTT"}
						</Button>
					</div>}
				</div>

				{aiEnabled && <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
					<Button bsStyle="default" bsSize="small" onClick={this.handleAiCleanTranscript} disabled={transcriptGeneratingField !== "" || processing}>
						{transcriptGeneratingField === "ai-clean" ? "AI cleaning..." : "AI Clean Filler Words"}
					</Button>
					<Button bsStyle="default" bsSize="small" onClick={this.handleAiConciseTranscript} disabled={transcriptGeneratingField !== "" || processing}>
						{transcriptGeneratingField === "ai-concise" ? "AI rewriting..." : "AI Make More Concise"}
					</Button>
					<Button bsStyle="default" bsSize="small" onClick={this.handleAiSuggestLabels} disabled={transcriptGeneratingField !== "" || processing}>
						{transcriptGeneratingField === "ai-labels" ? "AI suggesting..." : "AI Suggest Step Labels"}
					</Button>
					<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
						AI actions are optional and all generated text stays editable.
					</div>
				</div>}

				<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
					<Button bsStyle={transcriptViewMode === TRANSCRIPT_VIEW_TEXT ? "primary" : "default"} bsSize="small" onClick={() => this.handleTranscriptViewModeChange(TRANSCRIPT_VIEW_TEXT)}>
						Clean Text
					</Button>
					<Button bsStyle={transcriptViewMode === TRANSCRIPT_VIEW_STEP_CARDS ? "primary" : "default"} bsSize="small" onClick={() => this.handleTranscriptViewModeChange(TRANSCRIPT_VIEW_STEP_CARDS)} disabled={!transcriptSourceText && !customTextStepCards.length}>
						Step Cards
					</Button>
					{!isKeyframeMode &&
						<Button
							bsStyle={transcriptViewMode === TRANSCRIPT_VIEW_MATCHED_CHUNKS ? "primary" : "default"}
							bsSize="small"
							onClick={() => this.handleTranscriptViewModeChange(TRANSCRIPT_VIEW_MATCHED_CHUNKS)}
							disabled={!hasMatchedChunkTranscriptView}
						>
							Matched Chunks
						</Button>
					}
					<Button bsStyle={transcriptViewMode === TRANSCRIPT_VIEW_SENTENCES ? "primary" : "default"} bsSize="small" onClick={() => this.handleTranscriptViewModeChange(TRANSCRIPT_VIEW_SENTENCES)} disabled={!transcriptSentences.length}>
						Sentences
					</Button>
					<Button bsStyle={transcriptViewMode === TRANSCRIPT_VIEW_VTT ? "primary" : "default"} bsSize="small" onClick={() => this.handleTranscriptViewModeChange(TRANSCRIPT_VIEW_VTT)} disabled={!transcriptVtt}>
						Subtitle Timestamps
					</Button>
					{allowAutoCreateOutline && hasTranscriptSuggestions &&
						<Button bsStyle="default" bsSize="small" onClick={this.handleApplyAllTranscriptSuggestions} disabled={processing}>
							Apply All Suggested Labels
						</Button>
					}
					{copiedTranscriptState &&
						<div style={{ color: "#047857", fontSize: "12px" }}>{copiedTranscriptState}</div>
					}
				</div>

				{(currentScrubCue || activeSegment) &&
					<div style={{ display: "grid", gridTemplateColumns: (typeof window !== "undefined" && window.innerWidth < 900) ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "10px" }}>
						<div style={{ border: "1px solid #fde68a", borderRadius: "8px", padding: "10px 12px", background: "#fffbeb" }}>
							<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px" }}>Current Scrubber Cue</div>
							{currentScrubCue ? (
								<>
									<div style={{ color: "#92400e", fontSize: "12px", marginBottom: "6px" }}>
										{formatSeconds(currentScrubCue.startSeconds)} - {formatSeconds(currentScrubCue.endSeconds)}
									</div>
									<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5 }}>{currentScrubCue.text}</div>
								</>
							) : (
								<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
									Scrub into a timed subtitle cue to highlight transcript text from playback position.
								</div>
							)}
						</div>
						<div style={{ border: "1px solid #bfdbfe", borderRadius: "8px", padding: "10px 12px", background: "#eff6ff" }}>
							<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px" }}>
								{isKeyframeMode ? "Selected Keyframe Focus" : "Selected Segment Focus"}
							</div>
							{activeSegment ? (
								<>
									<div style={{ color: "#1d4ed8", fontSize: "12px", marginBottom: "6px" }}>
										{isKeyframeMode
											? `${formatSeconds(activeSegment.frameSeconds)}`
											: `${formatSeconds(activeSegment.startSeconds)} - ${formatSeconds(activeSegment.endSeconds)}`}
									</div>
									<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5 }}>
										{activeSegmentTranscriptText
											? activeSegmentTranscriptText
											: (isKeyframeMode
												? "Select a keyframe card to focus the nearest subtitle cue."
												: "Select a segment card to focus the subtitle cues inside that time range.")}
									</div>
								</>
							) : (
								<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
									Select a chunk or keyframe card to focus transcript timing for that output.
								</div>
							)}
						</div>
					</div>
				}

				{transcriptViewMode === TRANSCRIPT_VIEW_TEXT ? (
					transcriptCues.length > 0 ? (
						<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
							<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
								{!isKeyframeMode && timedTranscriptWordRanges.length > 0
									? "The transcript below highlights the selected chunk in blue, the currently playing subtitle cue in yellow, and the current spoken word in orange. Use `Matched Chunks` when you want to inspect the exact transcript text assigned to each segment."
									: "The transcript below is both the live-highlighted playback view and the text-selection workspace. Scrub the video to watch the relevant cue light up, then highlight any text directly in the transcript to copy it or turn it into a step card."}
							</div>
							{!isKeyframeMode &&
								<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
									<div style={{ color: "#6b7280", fontSize: "12px", fontWeight: 600 }}>Show highlights:</div>
									<Button
										bsStyle={showSelectedSegmentHighlight ? "primary" : "default"}
										bsSize="small"
										onClick={() => this.handleTranscriptHighlightToggle("showSelectedSegmentHighlight")}
									>
										<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
											<span style={{ width: "10px", height: "10px", borderRadius: "999px", background: "#93c5fd", display: "inline-block" }} />
											Selected segment
										</span>
									</Button>
									<Button
										bsStyle={showCurrentCueHighlight ? "warning" : "default"}
										bsSize="small"
										onClick={() => this.handleTranscriptHighlightToggle("showCurrentCueHighlight")}
									>
										<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
											<span style={{ width: "10px", height: "10px", borderRadius: "999px", background: "#f59e0b", display: "inline-block" }} />
											Current subtitle cue
										</span>
									</Button>
									<Button
										bsStyle={showCurrentWordHighlight ? "danger" : "default"}
										bsSize="small"
										onClick={() => this.handleTranscriptHighlightToggle("showCurrentWordHighlight")}
										disabled={!timedTranscriptWordRanges.length}
									>
										<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
											<span style={{ width: "10px", height: "10px", borderRadius: "999px", background: "#ea580c", display: "inline-block" }} />
											Current spoken word
										</span>
									</Button>
									<Button
										bsStyle={showStepCardHighlight ? "success" : "default"}
										bsSize="small"
										onClick={() => this.handleTranscriptHighlightToggle("showStepCardHighlight")}
										disabled={!textStepCardRanges.length}
									>
										<span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
											<span style={{ width: "10px", height: "10px", borderRadius: "999px", background: "#10b981", display: "inline-block" }} />
											Step cards
										</span>
									</Button>
								</div>
							}
							<style>
								{`
										.chunk-video-transcript-selection-surface::selection,
										.chunk-video-transcript-selection-surface *::selection {
											background: rgba(99, 102, 241, 0.42);
											color: #111827;
										}

										.chunk-video-transcript-selection-surface::-moz-selection,
										.chunk-video-transcript-selection-surface *::-moz-selection {
											background: rgba(99, 102, 241, 0.42);
											color: #111827;
										}
									`}
							</style>
							<div style={{ position: "sticky", top: 0, zIndex: 3, background: "#ffffff", border: "1px solid #dbe5f0", borderRadius: "8px", padding: "10px 12px", boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)" }}>
								<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
									<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(transcriptText || extractTranscriptFromVtt(transcriptVtt))}>
										Copy full transcript
									</Button>
									<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(selectedTranscriptRangeText)} disabled={!selectedTranscriptRangeText}>
										Copy selection
									</Button>
									<Button bsStyle="default" bsSize="small" onClick={this.handleAddTextStepCardFromSelection} disabled={!selectedTranscriptRangeText}>
										Add step card from selection
									</Button>
									{allowAutoCreateOutline &&
										<Button bsStyle="default" bsSize="small" onClick={() => this.handleApplyTranscriptSuggestion(resolvedActiveOutputIndex, selectedTranscriptRangeText)} disabled={!selectedTranscriptRangeText}>
											Use selection for active label
										</Button>
									}
									<Button bsStyle="default" bsSize="small" onClick={this.handleClearTranscriptTextSelection} disabled={!selectedTranscriptRangeText}>
										Clear selection
									</Button>
								</div>
								{selectedTranscriptRangeText &&
									<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, marginTop: "8px" }}>
										Selected text: {selectedTranscriptRangeText}
									</div>
								}
								{stepCardNotice &&
									<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, marginTop: "8px" }}>
										{stepCardNotice}
									</div>
								}
							</div>
							<div
								className="chunk-video-transcript-selection-surface"
								ref={this.transcriptSelectionRichTextRef}
								onMouseUp={this.handleTranscriptRichTextSelection}
								onKeyUp={this.handleTranscriptRichTextSelection}
								style={{ border: "1px solid #dbe5f0", borderRadius: "8px", padding: "12px", background: "#f9fafb", lineHeight: 2, userSelect: "text" }}
							>
								{(() => {
									const currentCueRange = currentScrubCue
										? timedTranscriptCueRanges.find((cueRange) => cueRange.index === currentScrubCue.index) || null
										: null;
									const activeCueRanges = timedTranscriptCueRanges.filter((cueRange) => isCueRelevantToSegment(cueRange, activeSegment, generationMode, transcriptCues));
									const hasSelectedTranscriptRange = selectedTranscriptRangeStart !== null &&
										selectedTranscriptRangeStart !== undefined &&
										selectedTranscriptRangeEnd !== null &&
										selectedTranscriptRangeEnd !== undefined &&
										Number.isFinite(Number(selectedTranscriptRangeStart)) &&
										Number.isFinite(Number(selectedTranscriptRangeEnd)) &&
										Number(selectedTranscriptRangeEnd) > Number(selectedTranscriptRangeStart);
									const boundaries = new Set([0, timedTranscriptDisplayText.length]);
									timedTranscriptCueRanges.forEach((cueRange) => {
										boundaries.add(cueRange.startOffset);
										boundaries.add(cueRange.endOffset);
									});
									timedTranscriptWordRanges.forEach((wordRange) => {
										boundaries.add(wordRange.startOffset);
										boundaries.add(wordRange.endOffset);
									});
									textStepCardRanges.forEach((stepCardRange) => {
										boundaries.add(stepCardRange.startOffset);
										boundaries.add(stepCardRange.endOffset);
									});
									if (hasSelectedTranscriptRange) {
										boundaries.add(Number(selectedTranscriptRangeStart));
										boundaries.add(Number(selectedTranscriptRangeEnd));
									}
									const sortedBoundaries = [...boundaries]
										.filter((value) => Number.isFinite(Number(value)) && value >= 0 && value <= timedTranscriptDisplayText.length)
										.sort((left, right) => left - right);
									const fragments = [];
									for (let index = 0; index < sortedBoundaries.length - 1; index += 1) {
										const startOffset = sortedBoundaries[index];
										const endOffset = sortedBoundaries[index + 1];
										if (endOffset <= startOffset) {
											continue;
										}
										const text = timedTranscriptDisplayText.slice(startOffset, endOffset);
										if (!text) {
											continue;
										}
										const stepCardRange = showStepCardHighlight
											? ([...textStepCardRanges].reverse().find((range) => startOffset < range.endOffset && endOffset > range.startOffset) || null)
											: null;
										const matchesScrub = showCurrentCueHighlight && !!(currentCueRange && startOffset < currentCueRange.endOffset && endOffset > currentCueRange.startOffset);
										const matchesActiveSegment = showSelectedSegmentHighlight && activeCueRanges.some((cueRange) => startOffset < cueRange.endOffset && endOffset > cueRange.startOffset);
										const matchesActiveSegmentWord = showSelectedSegmentHighlight && activeSegmentTimedWordRanges.some((wordRange) => startOffset < wordRange.endOffset && endOffset > wordRange.startOffset);
										const matchesCurrentTimedWord = showCurrentWordHighlight && !!(currentTimedWordRange && startOffset < currentTimedWordRange.endOffset && endOffset > currentTimedWordRange.startOffset);
										const matchesSelectedRange = hasSelectedTranscriptRange &&
											startOffset < Number(selectedTranscriptRangeEnd) &&
											endOffset > Number(selectedTranscriptRangeStart);
										const matchingCueRange = timedTranscriptCueRanges.find((cueRange) => startOffset < cueRange.endOffset && endOffset > cueRange.startOffset) || null;
										fragments.push({
											key: `cue-fragment-${startOffset}-${endOffset}`,
											text,
											startOffset,
											endOffset,
											cueIndex: matchingCueRange ? matchingCueRange.index : null,
											stepCardRange,
											matchesScrub,
											matchesActiveSegment,
											matchesActiveSegmentWord,
											matchesCurrentTimedWord,
											matchesSelectedRange
										});
									}
									return fragments.map((fragment) => {
										const stepCardColor = fragment.stepCardRange ? fragment.stepCardRange.color : null;
										const highlightStyle = buildTranscriptFragmentHighlightStyle({
											stepCardColor,
											matchesActiveSegment: fragment.matchesActiveSegment,
											matchesActiveSegmentWord: fragment.matchesActiveSegmentWord,
											matchesSelectedRange: fragment.matchesSelectedRange,
											matchesScrub: fragment.matchesScrub,
											matchesCurrentTimedWord: fragment.matchesCurrentTimedWord
										});
										return (
											<span
												key={fragment.key}
												data-cue-index={Number.isFinite(Number(fragment.cueIndex)) ? Number(fragment.cueIndex) : undefined}
												style={{
													display: "inline",
													padding: "2px 0",
													borderRadius: "4px",
													...highlightStyle
												}}
											>
												{fragment.text}
											</span>
										);
									});
								})()}
							</div>
						</div>
					) : transcriptText ? (
						<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
							<div style={{ position: "sticky", top: 0, zIndex: 3, background: "#ffffff", border: "1px solid #dbe5f0", borderRadius: "8px", padding: "10px 12px", boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)" }}>
								<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
									<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(transcriptText)}>
										Copy full transcript
									</Button>
									<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(selectedTranscriptRangeText)} disabled={!selectedTranscriptRangeText}>
										Copy selection
									</Button>
									<Button bsStyle="default" bsSize="small" onClick={this.handleAddTextStepCardFromSelection} disabled={!selectedTranscriptRangeText}>
										Add step card from selection
									</Button>
									{allowAutoCreateOutline &&
										<Button bsStyle="default" bsSize="small" onClick={() => this.handleApplyTranscriptSuggestion(resolvedActiveOutputIndex, selectedTranscriptRangeText)} disabled={!selectedTranscriptRangeText}>
											Use selection for active label
										</Button>
									}
									<Button bsStyle="default" bsSize="small" onClick={this.handleClearTranscriptTextSelection} disabled={!selectedTranscriptRangeText}>
										Clear selection
									</Button>
								</div>
								{selectedTranscriptRangeText &&
									<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, marginTop: "8px" }}>
										Selected text: {selectedTranscriptRangeText}
									</div>
								}
								{stepCardNotice &&
									<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5, marginTop: "8px" }}>
										{stepCardNotice}
									</div>
								}
							</div>
							<textarea
								ref={this.transcriptSelectionTextareaRef}
								className="form-control"
								readOnly
								value={transcriptText}
								onSelect={this.handleTranscriptTextSelection}
								onMouseUp={this.handleTranscriptTextSelection}
								onKeyUp={this.handleTranscriptTextSelection}
								style={{ minHeight: "180px", resize: "vertical" }}
							/>
							<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
								Highlight any chunk of clean transcript text, then turn it into a step card for later reuse.
							</div>
						</div>
					) : (
						<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
							No transcript text loaded yet. Generate transcript to review the source narration as one cleaned-up text block.
						</div>
					)
				) : transcriptViewMode === TRANSCRIPT_VIEW_MATCHED_CHUNKS ? (
					isKeyframeMode ? (
						<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
							Matched chunk transcript cards are only available while `Create` is set to `Video Chunks`.
						</div>
					) : hasMatchedChunkTranscriptView ? (
						<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
							<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
								These cards show the transcript text assigned to each chunk. When AWS word timing is loaded, the cards use the exact timed-word match. Otherwise they fall back to subtitle-cue overlap.
							</div>
							{timedTranscriptWordRanges.length === 0 &&
								<div style={{ border: "1px solid #fde68a", borderRadius: "8px", padding: "10px 12px", background: "#fffbeb", color: "#92400e", fontSize: "12px", lineHeight: 1.5 }}>
									Exact word timing is not mapped into the transcript workspace yet for this source, so these cards are currently using the broader subtitle-cue overlap text.
								</div>
							}
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								{segments.map((segment, index) => {
									const matchedTranscriptText = normalizeTranscriptSuggestion(matchedChunkTranscriptTexts[index]);
									const fallbackTranscriptText = normalizeTranscriptSuggestion(transcriptSuggestions[index]);
									const displayTranscriptText = matchedTranscriptText || fallbackTranscriptText;
									const hasExactTiming = !!matchedTranscriptText;
									const segmentIsActive = index === resolvedActiveOutputIndex;
									return (
										<div
											key={`matched-chunk-transcript-${index}`}
											style={{
												border: segmentIsActive ? "2px solid #60a5fa" : "1px solid #dbe5f0",
												borderRadius: "10px",
												padding: "12px",
												background: segmentIsActive ? "#f8fbff" : "#ffffff",
												boxShadow: segmentIsActive ? "0 4px 12px rgba(96, 165, 250, 0.16)" : "none",
												cursor: "pointer"
											}}
											onClick={() => {
												this.handleSetActiveOutputIndex(index);
												this.seekMainVideo(segment.startSeconds);
											}}
										>
											<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start", flexWrap: "wrap" }}>
												<div style={{ minWidth: 0 }}>
													<div style={{ fontWeight: 600, fontSize: "13px", color: "#111827", display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
														<span>{`Segment ${index + 1}`}</span>
														<span style={{ fontSize: "11px", fontWeight: 600, padding: "3px 8px", borderRadius: "999px", background: hasExactTiming ? "#dbeafe" : "#f3f4f6", color: hasExactTiming ? "#1d4ed8" : "#4b5563" }}>
															{hasExactTiming ? "Word-timed match" : "Cue-overlap fallback"}
														</span>
													</div>
													<div style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px" }}>
														{formatSeconds(segment.startSeconds)} - {formatSeconds(segment.endSeconds)} {Number.isFinite(Number(segment.durationSeconds)) ? `(${formatSeconds(segment.durationSeconds)})` : ""}
													</div>
												</div>
												<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
													<Button
														bsStyle="default"
														bsSize="small"
														onClick={(event) => {
															event.stopPropagation();
															this.handleSetActiveOutputIndex(index);
															this.seekMainVideo(segment.startSeconds);
														}}
													>
														Focus
													</Button>
													<Button
														bsStyle="default"
														bsSize="small"
														onClick={(event) => {
															event.stopPropagation();
															this.handleCopyTranscriptText(displayTranscriptText);
														}}
														disabled={!displayTranscriptText}
													>
														Copy
													</Button>
													{allowAutoCreateOutline &&
														<Button
															bsStyle="default"
															bsSize="small"
															onClick={(event) => {
																event.stopPropagation();
																this.handleApplyTranscriptSuggestion(index, displayTranscriptText);
															}}
															disabled={!displayTranscriptText}
														>
															Use for Label 1
														</Button>
													}
												</div>
											</div>
											<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.6, marginTop: "10px", whiteSpace: "pre-wrap" }}>
												{displayTranscriptText || "No transcript text could be matched to this chunk yet."}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					) : (
						<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
							Add chunk points and load transcript timing to see one transcript card per chunk here.
						</div>
					)
				) : transcriptViewMode === TRANSCRIPT_VIEW_STEP_CARDS ? (
					<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
						<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
							Create these from highlighted clean transcript text when you want author-defined step content instead of cue or sentence-driven groupings.
						</div>
						<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
							{allowAutoCreateOutline &&
								<Button bsStyle="default" bsSize="small" onClick={this.handleApplyAllTextStepCardsToLabels} disabled={!customTextStepCards.length}>
									Apply step cards to labels in order
								</Button>
							}
							{stepCardNotice &&
								<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
									{stepCardNotice}
								</div>
							}
						</div>
						{customTextStepCards.length > 0 ? (
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								{customTextStepCards.map((card, index) => {
									const stepCardColor = getCustomTextStepCardColor(card.colorIndex);
									return (
										<div
											key={card.id}
											style={{
												border: `2px solid ${stepCardColor.border}`,
												borderRadius: "8px",
												padding: "10px 12px",
												background: stepCardColor.background,
												boxShadow: index === resolvedActiveOutputIndex ? `0 0 0 2px ${stepCardColor.chipBackground}` : "none"
											}}
										>
											<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
												<div style={{ minWidth: 0 }}>
													<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px", color: stepCardColor.accent, display: "flex", alignItems: "center", gap: "8px" }}>
														<span style={{ width: "10px", height: "10px", borderRadius: "999px", background: stepCardColor.border, display: "inline-block", flex: "0 0 auto" }} />
														Step Card {index + 1}
													</div>
													<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5 }}>
														{card.text}
													</div>
												</div>
												<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
													<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTextStepCard(card.id)}>
														Copy
													</Button>
													{allowAutoCreateOutline &&
														<Button bsStyle="default" bsSize="small" onClick={() => this.handleApplyTextStepCardToActiveLabel(card.id)}>
															Use for active label
														</Button>
													}
													<Button bsStyle="default" bsSize="small" onClick={() => this.handleRemoveTextStepCard(card.id)} disabled={processing}>
														Remove
													</Button>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
								No step cards yet. Go to `Clean Text`, highlight the first chunk of transcript you want for step 1, and add it as a card. Repeat for the next chunks of text.
							</div>
						)}
					</div>
				) : transcriptViewMode === TRANSCRIPT_VIEW_SENTENCES ? (
					transcriptSentences.length > 0 ? (
						<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
							<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
								Sentences are easier to map to what the speaker is describing for a keyframe. Select one or more sentences to combine them into the active label or turn them into reusable sentence cards.
							</div>
							<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
								<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(selectedSentenceText)} disabled={!selectedSentenceText}>
									Copy selected sentences
								</Button>
								{allowAutoCreateOutline &&
									<Button bsStyle="default" bsSize="small" onClick={this.handleApplySelectedSentencesToActiveLabel} disabled={!selectedSentenceText}>
										Use selected for active label
									</Button>
								}
								<Button bsStyle="default" bsSize="small" onClick={this.handleAddSentenceGroup} disabled={!selectedSentenceIndexes.length}>
									Add sentence card from selection
								</Button>
								<Button bsStyle="default" bsSize="small" onClick={this.handleClearSentenceSelection} disabled={!selectedSentenceIndexes.length}>
									Clear sentence selection
								</Button>
								{selectedSentenceText &&
									<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
										Selected text: {selectedSentenceText}
									</div>
								}
								{sentenceGroupingNotice &&
									<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
										{sentenceGroupingNotice}
									</div>
								}
							</div>
							{customSentenceGroups.length > 0 &&
								<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
									<div style={{ fontWeight: 600, fontSize: "12px" }}>Sentence Cards</div>
									<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
										{customSentenceGroups.map((group, groupIndex) => {
											const groupColor = getCustomSentenceGroupColor(group.colorIndex);
											const groupText = this.getCustomSentenceGroupText(group, transcriptSentences);
											const groupRange = this.getCustomSentenceGroupRange(group, transcriptSentences);
											return (
												<div
													key={group.id}
													style={{
														border: `2px solid ${groupColor.border}`,
														borderRadius: "8px",
														padding: "10px 12px",
														background: groupColor.background
													}}
												>
													<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
														<div style={{ minWidth: 0 }}>
															<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px", color: groupColor.accent }}>
																Sentence Card {groupIndex + 1}
																{groupRange.startSeconds !== null && groupRange.endSeconds !== null ? `  •  ${formatSeconds(groupRange.startSeconds)} - ${formatSeconds(groupRange.endSeconds)}` : ""}
															</div>
															<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5 }}>
																{groupText}
															</div>
														</div>
														<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
															<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyCustomSentenceGroup(group.id)}>
																Copy
															</Button>
															{allowAutoCreateOutline &&
																<Button bsStyle="default" bsSize="small" onClick={() => this.handleApplyCustomSentenceGroupToActiveLabel(group.id)}>
																	Use for active label
																</Button>
															}
															<Button bsStyle="default" bsSize="small" onClick={() => this.handleRemoveSentenceGroup(group.id)} disabled={processing}>
																Remove
															</Button>
														</div>
													</div>
												</div>
											);
										})}
									</div>
								</div>
							}
							<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
								{transcriptSentences.map((sentence) => {
									const sentenceSelectionActive = selectedSentenceIndexes.includes(sentence.index);
									const sentenceFocus = isSentenceRelevantToSegment(sentence, activeSegment, generationMode, currentTime, currentScrubCue);
									const sentenceGroupAssignment = sentenceGroupAssignments[sentence.index] || null;
									const sentenceGroupColor = sentenceGroupAssignment ? sentenceGroupAssignment.color : null;
									const sentenceBackground = sentenceSelectionActive
										? "#ecfeff"
										: (sentenceFocus.matchesScrub
											? "#fffbeb"
											: (sentenceFocus.matchesSegment
												? "#eef6ff"
												: (sentenceGroupColor ? sentenceGroupColor.background : "#f9fafb")));
									const sentenceBorder = sentenceFocus.matchesScrub
										? "2px solid #f59e0b"
										: (sentenceFocus.matchesSegment
											? "2px solid #60a5fa"
											: (sentenceGroupColor ? `2px solid ${sentenceGroupColor.border}` : "1px solid #dbe5f0"));
									return (
										<div
											key={`sentence-${sentence.index}`}
											style={{
												border: sentenceBorder,
												borderRadius: "8px",
												padding: "10px 12px",
												background: sentenceBackground,
												cursor: sentenceGroupAssignment ? "default" : "pointer"
											}}
											onClick={sentenceGroupAssignment ? undefined : (() => this.handleToggleSentenceSelection(sentence.index))}
										>
											<div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "flex-start" }}>
												<div style={{ display: "flex", gap: "10px", alignItems: "flex-start", minWidth: 0 }}>
													<input
														type="checkbox"
														checked={sentenceSelectionActive}
														disabled={!!sentenceGroupAssignment}
														onChange={() => this.handleToggleSentenceSelection(sentence.index)}
														onClick={(event) => event.stopPropagation()}
														style={{ marginTop: "2px" }}
													/>
													<div style={{ minWidth: 0 }}>
														<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px" }}>
															Sentence {sentence.index + 1}
															{sentence.timed && sentence.startSeconds !== null && sentence.endSeconds !== null ? `  •  ${formatSeconds(sentence.startSeconds)} - ${formatSeconds(sentence.endSeconds)}` : ""}
														</div>
														{sentenceGroupAssignment &&
															<div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "2px 8px", borderRadius: "999px", background: sentenceGroupColor.chipBackground, color: sentenceGroupColor.accent, fontSize: "11px", fontWeight: 600, marginBottom: "6px" }}>
																<span style={{ width: "8px", height: "8px", borderRadius: "999px", background: sentenceGroupColor.border, display: "inline-block" }} />
																Sentence Card {sentenceGroupAssignment.groupOrder}
															</div>
														}
														<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5 }}>
															{sentence.text}
														</div>
													</div>
												</div>
												<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", justifyContent: "flex-end" }}>
													<Button
														bsStyle="default"
														bsSize="small"
														onClick={(event) => {
															event.stopPropagation();
															this.handleCopyTranscriptText(sentence.text);
														}}
													>
														Copy
													</Button>
													{allowAutoCreateOutline &&
														<Button
															bsStyle="default"
															bsSize="small"
															onClick={(event) => {
																event.stopPropagation();
																this.handleApplyTranscriptSuggestion(resolvedActiveOutputIndex, sentence.text);
															}}
														>
															Use for active label
														</Button>
													}
												</div>
											</div>
										</div>
									);
								})}
							</div>
						</div>
					) : (
						<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
							No sentence groups are available yet. Generate transcript or VTT first.
						</div>
					)
				) : transcriptVtt ? (
					<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
						{transcriptCues.map((cue) => {
							const cueMatchesScrub = !!(currentScrubCue && cue.index === currentScrubCue.index);
							const cueMatchesActiveSegment = isCueRelevantToSegment(cue, activeSegment, generationMode, transcriptCues);
							return (
								<div
									key={`cue-${cue.index}`}
									style={{
										border: cueMatchesScrub ? "2px solid #f59e0b" : (cueMatchesActiveSegment ? "2px solid #60a5fa" : "1px solid #dbe5f0"),
										borderRadius: "8px",
										padding: "8px 10px",
										background: cueMatchesScrub ? "#fffbeb" : (cueMatchesActiveSegment ? "#eef6ff" : "#f9fafb")
									}}
								>
									<div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center" }}>
										<div style={{ fontWeight: 600, fontSize: "12px" }}>{formatSeconds(cue.startSeconds)} - {formatSeconds(cue.endSeconds)}</div>
										<div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
											<Button bsStyle="default" bsSize="small" onClick={() => this.handleCopyTranscriptText(cue.text)}>
												Copy
											</Button>
											{allowAutoCreateOutline && activeSegment &&
												<Button bsStyle="default" bsSize="small" onClick={() => this.handleApplyTranscriptSuggestion(resolvedActiveOutputIndex, cue.text)}>
													Use for active label
												</Button>
											}
										</div>
									</div>
									<div style={{ color: "#374151", fontSize: "12px", lineHeight: 1.5, marginTop: "6px" }}>
										{cue.text}
									</div>
								</div>
							);
						})}
					</div>
				) : (
					<div style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.5 }}>
						No timed transcript loaded yet. Generate VTT to highlight which subtitle cues belong to each chunk or nearest keyframe.
					</div>
				)}

				{transcriptError &&
					<div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "10px 12px", lineHeight: 1.5 }}>
						{transcriptError}
					</div>
				}
			</div>
		);
		const transcriptWorkspacePanel = buildTranscriptWorkspacePanel(false);
		const stackedTranscriptWorkspacePanel = buildTranscriptWorkspacePanel(true);

		return (
			<Modal
				open={open}
				onClose={busy ? undefined : this.handleRequestClose}
				classNames={{ overlay: "video-chunk-modal-overlay", modal: "video-chunk-modal-fullscreen" }}
				styles={{
					overlay: {
						padding: 0,
						margin: 0,
						display: "block",
						overflow: "hidden"
					},
					modal: {
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						width: "100%",
						height: "100%",
						maxWidth: "none",
						maxHeight: "none",
						margin: 0,
						padding: 0,
						borderRadius: 0,
						boxSizing: "border-box"
					}
				}}
				closeButton
			>
				<div style={{ width: "100%", maxWidth: "none", height: "100%", maxHeight: "100%", overflow: "hidden", display: "flex", flexDirection: "column", gap: "10px", minHeight: 0, padding: "10px 12px 8px", boxSizing: "border-box" }}>
					<div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: "12px" }}>
						<div />
						<h3 style={{ marginTop: 0, marginBottom: 0, textAlign: "center" }}>{title}</h3>
						<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", flexWrap: "wrap", paddingRight: "48px" }}>
							<Button
								bsStyle={resolvedLayoutMode === CHUNK_MODAL_LAYOUT_DEFAULT ? "primary" : "default"}
								bsSize="small"
								onClick={() => this.handleChunkModalLayoutChange(CHUNK_MODAL_LAYOUT_DEFAULT)}
								disabled={processing}
							>
								Default Layout
							</Button>
								<Button
									bsStyle={resolvedLayoutMode === CHUNK_MODAL_LAYOUT_VIDEO_FOCUS ? "primary" : "default"}
									bsSize="small"
									onClick={() => this.handleChunkModalLayoutChange(CHUNK_MODAL_LAYOUT_VIDEO_FOCUS)}
									disabled={processing}
								>
									Video Focus
								</Button>
								<Button
									bsStyle={resolvedLayoutMode === CHUNK_MODAL_LAYOUT_STEPS_FOCUS ? "primary" : "default"}
								bsSize="small"
								onClick={() => this.handleChunkModalLayoutChange(CHUNK_MODAL_LAYOUT_STEPS_FOCUS)}
								disabled={processing}
							>
								Steps Focus
							</Button>
							</div>
						</div>

						{topControlsPanel}

						{isVideoFocusLayout ? (
							<div style={{ display: "grid", gridTemplateColumns: (typeof window !== "undefined" && window.innerWidth < 1500) ? "minmax(0, 1fr)" : "minmax(0, 1.4fr) minmax(0, 0.6fr)", gap: "18px", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
								<div style={{ minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
									{videoFocusEditorPanel}
								</div>
								<div style={{ minHeight: 0, overflowY: "auto", paddingRight: "4px", display: "flex", flexDirection: "column", gap: "12px" }}>
									{stackedTranscriptWorkspacePanel}
									{outputSegmentsPanel}
								</div>
							</div>
						) : isStepsFocusLayout ? (
							<>
								<div style={{ border: "1px solid #dbe5f0", borderRadius: "10px", padding: "12px 14px", background: "#f8fbff", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
									<div>
										<div style={{ fontWeight: 600, marginBottom: "4px" }}>Steps Focus layout is active</div>
										<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
											Steps Focus hides the video column so you can work directly in the transcript workspace and outputs. Expand the top controls any time you need create or outline settings.
										</div>
									</div>
									<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
										Video controls are hidden in this layout.
									</div>
								</div>
								<div style={{ display: "grid", gridTemplateColumns: (typeof window !== "undefined" && window.innerWidth < 1280) ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)", gap: "20px", alignItems: "stretch", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
									<div style={{ minHeight: 0, overflowY: "auto", paddingRight: "6px" }}>
										{transcriptWorkspacePanel}
									</div>
									<div style={{ minHeight: 0, overflowY: "auto", paddingRight: "6px" }}>
										{outputSegmentsPanel}
									</div>
								</div>
							</>
						) : (
							<div style={{ display: "grid", gridTemplateColumns: (typeof window !== "undefined" && window.innerWidth < 1400) ? "minmax(0, 1fr)" : "minmax(0, 1.4fr) minmax(0, 1.05fr) minmax(0, 1.05fr)", gap: "18px", alignItems: "stretch", flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
								<div style={{ minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
									{defaultVideoPanel}
								</div>
								<div style={{ minHeight: 0, overflowY: "auto", paddingRight: "6px" }}>
									{transcriptWorkspacePanel}
								</div>
								<div style={{ minHeight: 0, overflowY: "auto", paddingRight: "6px" }}>
									{outputSegmentsPanel}
								</div>
							</div>
						)}

					{error &&
						<div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "10px 12px", lineHeight: 1.5 }}>
							{error}
						</div>
					}
					{processing && processingStatusMessage &&
						<div style={{ color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", padding: "10px 12px", lineHeight: 1.5 }}>
							<div style={{ fontWeight: 600, marginBottom: "4px" }}>
								{isKeyframeMode ? "Creating keyframes" : "Creating chunks"}
							</div>
							<div>{processingStatusMessage}</div>
						</div>
					}

					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
						<div style={{ color: "#6b7280", fontSize: "12px", lineHeight: 1.5 }}>
							{isKeyframeMode
								? "Keyframe capture creates new Drive image artifacts only. Auto-create outline inserts new sibling steps after the selected source item and leaves the source item untouched."
								: "Chunking creates new Drive video artifacts only. Auto-create outline inserts new sibling steps after the selected source item and leaves the source item untouched."}
						</div>
						<div style={{ display: "flex", gap: "10px" }}>
							<Button bsStyle="default" onClick={this.handleSaveDraft} disabled={busy}>
								{savingDraft ? "Saving..." : "Save"}
							</Button>
							<Button bsStyle="danger" onClick={this.confirmResetAuthoringState} disabled={busy}>Clear</Button>
							<Button bsStyle="default" onClick={this.handleRequestClose} disabled={busy}>Cancel</Button>
							<Button bsStyle="primary" onClick={this.handleCreateChunks} disabled={!canCreateChunks}>
								{createButtonLabel}
							</Button>
						</div>
					</div>
				</div>
			</Modal>
		);
	}
}
